import { stripRetiredRegistrationFields } from './utils/registrationSubmissionFields';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calculator, CheckCircle, RefreshCw } from 'lucide-react';
import { loadGoogleMapsScript } from './utils/googleMapsLoader';

const EssentialIcon = () => (
  <svg className="w-12 h-12 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
      clipRule="evenodd"
    />
  </svg>
);

const PremiumIcon = () => (
  <svg className="w-12 h-12 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2l1.179-4.456A1 1 0 0112 2z"
      clipRule="evenodd"
    />
  </svg>
);

const OnlineStoreIcon = () => (
  <svg className="w-12 h-12 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
  </svg>
);

// Plan codes sent to Make.com and Zoho CRM (Website_Plan): one word per plan.
// 'essential' = Essential (£39), 'business' = Business (£69), 'premium' = Premium (£89).
// 'online-store' is a retired plan code kept only for existing customers.
const PLAN_DISPLAY_NAMES = {
  essential: 'Essential',
  business: 'Business',
  premium: 'Premium'
};

const INDUSTRY_ADDONS = [
  {
    value: 'onlinestore',
    label: 'Online Store',
    priceText: '£299 one-off',
    amount: 299,
    billingType: 'one_off',
    key: 'addonOnlineStore',
    description: 'Sell products online with a cart, checkout, stock management and payments.',
    allowedPlans: ['premium'],
    upgradePlan: 'premium'
  },
  {
    value: 'apartments',
    label: 'Apartment Listings & Bookings',
    priceText: '£249 one-off',
    amount: 249,
    billingType: 'one_off',
    key: 'addonApartmentListings',
    description: 'List your apartments and let guests check availability and book direct.',
    allowedPlans: ['premium'],
    upgradePlan: 'premium'
  },
  {
    value: 'estateagent',
    label: 'Estate Agent Listings',
    priceText: 'Price on request',
    amount: 0,
    billingType: 'quote',
    key: 'addonEstateAgentListings',
    description: 'List properties with photos and take enquiries. We confirm the price with you before starting.',
    allowedPlans: ['premium'],
    upgradePlan: 'premium'
  },
  {
    value: 'restaurant',
    label: 'Restaurant Menu & Table Bookings',
    priceText: '£169 one-off',
    amount: 169,
    billingType: 'one_off',
    key: 'addonRestaurantMenu',
    description: 'Update your menus yourself and let customers book a table online.',
    allowedPlans: ['essential', 'business', 'premium'],
    upgradePlan: null
  }
];

// Local SEO plans, matching ubiewebsites.co.uk/local-search-engine-optimisation/.
// Billed monthly on top of the website plan: the first month is paid upfront and the
// rest goes on the monthly repeating invoice. Customers pick one plan at most.
const LOCAL_SEO_PLANS = [
  {
    value: 'local-seo-local',
    plan: 'local',
    name: 'Starter',
    label: 'Local SEO - Starter',
    priceText: '£149/month',
    amount: 149,
    billingType: 'monthly',
    minimumTermMonths: 6,
    key: 'addonLocalSeoLocal',
    description: 'Local citations, Google Business Profile optimisation, ranking tracking, 1 hour of on-page work a month and a quarterly report.'
  },
  {
    value: 'local-seo-growth',
    plan: 'growth',
    name: 'Growth',
    label: 'Local SEO - Growth',
    priceText: '£249/month',
    amount: 249,
    billingType: 'monthly',
    minimumTermMonths: 6,
    key: 'addonLocalSeoGrowth',
    description: 'Everything in Starter, plus a monthly report, a guest post on a DR 20+ site and a blog post by a human writer.'
  },
  {
    value: 'local-seo-pro',
    plan: 'pro',
    name: 'Pro',
    label: 'Local SEO - Pro',
    priceText: '£399/month',
    amount: 399,
    billingType: 'monthly',
    minimumTermMonths: 6,
    key: 'addonLocalSeoPro',
    description: 'Everything in Growth, plus 1.5 hours of on-page work a month, a guest post on a DR 40+ site and advanced site speed work.'
  }
];

const LocalSeoIcon = () => (
  <svg className="w-12 h-12 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
  </svg>
);

// Google Ads click IDs, so a paid registration can be matched back to the ad click.
// The website's Conversion Linker stores _gcl_aw on .ubiewebsites.co.uk, which this
// subdomain can read; a gclid/gbraid/wbraid in the URL takes priority.
const readCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
};

const readAdClickIds = () => {
  if (typeof window === 'undefined') return { gclid: '', gbraid: '', wbraid: '' };
  const params = new URLSearchParams(window.location.search);
  const fromGclCookie = (cookieName) => {
    const parts = readCookie(cookieName).split('.');
    return parts.length >= 3 ? parts.slice(2).join('.') : '';
  };
  return {
    gclid: params.get('gclid') || fromGclCookie('_gcl_aw') || readCookie('gclid') || readCookie('ubie_gclid') || '',
    gbraid: params.get('gbraid') || fromGclCookie('_gcl_gb') || readCookie('ubie_gbraid') || '',
    wbraid: params.get('wbraid') || readCookie('ubie_wbraid') || ''
  };
};

const AD_CLICK_IDS = readAdClickIds();

const getIndustryAddon = (value) => INDUSTRY_ADDONS.find((addon) => addon.value === value);

const isAddonAllowedOnPlan = (addon, plan) => !addon || !plan || !addon.allowedPlans || addon.allowedPlans.includes(plan);

const getLocalSeoPlan = (value) => LOCAL_SEO_PLANS.find((seoPlan) => seoPlan.value === value);

// Industry add-ons and Local SEO plans priced the same way, for totals and invoices.
const getPricedAddon = (value) => getIndustryAddon(value) || getLocalSeoPlan(value);

// Add this helper function for email validation
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const normalizeEmailInput = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, '');
const NAME_INPUT_MAX_LENGTH = 50;
// Single spaces are allowed so two-part names like "Van Der Berg" or "Mary Jane" stay intact.
const NAME_ALLOWED_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ'’-]+(?: [A-Za-zÀ-ÖØ-öø-ÿ'’-]+)*$/;
const normalizeNameInput = (value) => String(value ?? '')
  .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ'’\s-]/g, '')
  .replace(/\s+/g, ' ')
  .replace(/^ /, '')
  .slice(0, NAME_INPUT_MAX_LENGTH);

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDobDateBoundaries = () => {
  const today = new Date();
  const maxDate = new Date(today);
  maxDate.setFullYear(maxDate.getFullYear() - 18);

  if (maxDate.getMonth() > today.getMonth() || (maxDate.getMonth() === today.getMonth() && maxDate.getDate() > today.getDate())) {
    maxDate.setDate(0);
  }

  return {
    minDob: '1900-01-01',
    maxDob: formatDateInputValue(maxDate)
  };
};

const sanitizeDobInputValue = (value, minDob, maxDob) => {
  const raw = String(value || '').replace(/[^\d-]/g, '').slice(0, 10);
  if (!raw) return '';

  const fullDatePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!fullDatePattern.test(raw)) {
    return raw;
  }

  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  if (raw < minDob) return minDob;
  if (raw > maxDob) return maxDob;
  return raw;
};

const normalizeDateInputForTyping = (value) => String(value || '').replace(/[^\d-]/g, '').slice(0, 10);

const PROMO_CONFIG = {
  code: 'APRIL30',
  discountPercent: 30,
  expiresAtIso: '2026-05-15T23:50:00+01:00',
  timezoneLabel: 'Europe/London',
  displayExpiryDate: '15/05/2026'
};

const getPromoStatus = () => {
  const expiresAt = new Date(PROMO_CONFIG.expiresAtIso);
  const now = new Date();
  const isExpired = now.getTime() > expiresAt.getTime();

  return {
    ...PROMO_CONFIG,
    expiresAt,
    isExpired
  };
};

const UK_MOBILE_NUMBER_REGEX = /^07\d{9}$/;
const UK_LANDLINE_OR_MOBILE_REGEX = /^(?:\+44|0)(?:1\d{9}|2\d{9}|3\d{9}|7\d{9}|8\d{9})$/;

const normalizeUkMobileInput = (input, options = {}) => {
  const { enforceLocal07 = false } = options;

  if (input == null) {
    return '';
  }

  let normalized = String(input)
    .trim()
    .replace(/[\s()-]/g, '')
    .replace(/[^\d+]/g, '');

  if (normalized.startsWith('00')) {
    normalized = `+${normalized.slice(2)}`;
  }

  if (normalized.indexOf('+', 1) !== -1) {
    const [firstChar, rest] = [normalized[0], normalized.slice(1)];
    normalized = `${firstChar}${rest.replace(/\+/g, '')}`;
  }

  if (normalized.startsWith('+')) {
    const digits = normalized.slice(1).replace(/\D/g, '');
    normalized = digits ? `+${digits}` : '';
  } else {
    normalized = normalized.replace(/\D/g, '');
  }

  if (normalized.startsWith('+440')) {
    normalized = `+44${normalized.slice(4)}`;
  }

  if (normalized.startsWith('+44') && normalized.length > 3 && normalized[3] === '0') {
    normalized = `+44${normalized.slice(4)}`;
  }

  if (!normalized.startsWith('+') && normalized.startsWith('44')) {
    normalized = `+44${normalized.slice(2)}`;
  }

  if (enforceLocal07) {
    // The field shows a fixed "07" prefix, so digits typed without a 0, +44 or 44 prefix are the
    // remaining 9 digits (e.g. "700900123" for 07700 900123), not a number starting with 7.
    const typedRemainingDigits = !normalized.startsWith('+') && !normalized.startsWith('0') && normalized.length <= 9;
    let digits = normalized.startsWith('+') ? normalized.slice(1).replace(/\D/g, '') : normalized.replace(/\D/g, '');

    if (typedRemainingDigits) {
      return digits ? `07${digits}` : '';
    }

    // Keep a leading "0" while it is being typed, so a full 07 number can be entered too.
    if (normalized === '0') {
      return '0';
    }

    if (digits.startsWith('44')) {
      digits = digits.slice(2);
    }

    if (digits.startsWith('0')) {
      digits = digits.slice(1);
    }

    if (!digits) {
      return '';
    }

    const localRest = (digits.startsWith('7') ? digits.slice(1) : digits).slice(0, 9);
    return `07${localRest}`;
  }

  return normalized;
};


const getUkPhoneValidationError = (value) => {
  const normalized = normalizeUkMobileInput(value);

  if (!normalized) {
    return 'Please enter a valid UK landline or mobile number';
  }

  if (!UK_LANDLINE_OR_MOBILE_REGEX.test(normalized)) {
    return 'Please enter a valid UK landline or mobile number';
  }

  return null;
};

const getUkMobileValidationError = (value) => {
  const normalized = normalizeUkMobileInput(value, { enforceLocal07: true });

  if (!normalized) {
    return 'Enter your UK mobile number starting with 07.';
  }

  if (!UK_MOBILE_NUMBER_REGEX.test(normalized)) {
    return 'Use a UK mobile format starting with 07.';
  }

  return null;
};


const getExistingDomainValidationError = (value) => {
  if (!value || !String(value).trim()) {
    return null;
  }

  const domainValue = String(value).trim().toLowerCase();

  if (domainValue.startsWith('http://') || domainValue.startsWith('https://')) {
    return 'Remove http:// or https:// (example: mydomainhere.com).';
  }

  if (domainValue.startsWith('www.')) {
    return 'Remove www. (example: mydomainhere.com).';
  }

  if (domainValue.includes('/') || domainValue.includes('?') || domainValue.includes('#')) {
    return 'Enter only the web address (example: mydomainhere.com).';
  }

  if (!domainValue.includes('.')) {
    return 'Include an extension (example: mydomainhere.com).';
  }

  const domainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
  if (!domainRegex.test(domainValue)) {
    return 'Enter a valid web address (example: mydomainhere.com).';
  }

  const extensionRegex = /\.[a-z]{2,}$/;
  if (!extensionRegex.test(domainValue)) {
    return 'Use a valid extension (example: mydomainhere.com).';
  }

  return null;
};

const generateSubmissionId = () => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `sub_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

// Local browser persistence is intentionally disabled for GDPR/privacy.
const clearRecoveryData = () => {};

const MobileStickyNavigation = ({
  show,
  canGoBack,
  backButtonLabel,
  continueButtonLabel,
  onBack,
  onContinue,
  isNextDisabled,
  busy
}) => {
  if (!show) return null;

  return (
    <div className="registration-mobile-nav fixed bottom-0 inset-x-0 z-50 border-t border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 p-2.5 lg:hidden shadow-[0_-6px_20px_rgba(0,0,0,0.08)]">
      <div className="mx-auto flex max-w-5xl gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={!canGoBack}
          className={`flex-1 rounded-[3px] px-3 py-2.5 text-sm font-semibold leading-tight transition ${
            canGoBack
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              : 'bg-gray-50 text-gray-400 cursor-not-allowed'
          }`}
        >
          {backButtonLabel}
        </button>
        <button
          type="button"
          onClick={onContinue}
          aria-busy={busy}
          disabled={isNextDisabled}
          className={`flex-1 rounded-[3px] px-3 py-2.5 text-sm font-semibold leading-tight transition ${
            isNextDisabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-500 text-white hover:bg-yellow-600'
          }`}
        >
          {continueButtonLabel}
          {busy && <RefreshCw className="inline-block w-4 h-4 ml-2 animate-spin" aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
};

const UI_CLASS_TOKENS = {
  disclosureToggle: 'inline-flex items-center gap-2 rounded-[3px] border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600 transition-colors hover:border-yellow-400 hover:text-yellow-600',
  buttonPrimary: 'rounded-[3px] bg-yellow-500 text-white hover:bg-yellow-600',
  buttonSecondary: 'rounded-[3px] border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
};

const DisclosureToggleButton = ({ isOpen, closedLabel, openLabel, onClick, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={UI_CLASS_TOKENS.disclosureToggle}
  >
    <span>{isOpen ? openLabel : closedLabel}{typeof count === 'number' ? ` (${count})` : ''}</span>
    <svg
      className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </button>
);

const DomainResultsHeader = ({
  checkedCount,
  availableCount,
  unavailableCount,
  selectedCount,
  showDomainTips,
  onToggleTips
}) => (
  <div className="rounded-[3px] border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="font-medium text-gray-600">
        {checkedCount > 0
          ? `${selectedCount} selected • ${availableCount} available • ${unavailableCount} unavailable`
          : 'Search and check availability'}
      </div>
      <DisclosureToggleButton
        isOpen={showDomainTips}
        closedLabel="Tips"
        openLabel="Hide tips"
        onClick={onToggleTips}
      />
    </div>
  </div>
);

const DOMAIN_EXTENSION_CONFIG = {
  '.co.uk': {
    price: 15,
    freeEligible: true,
    label: 'Most popular for UK businesses'
  },
  '.uk': {
    price: 15,
    freeEligible: true,
    label: 'Short, modern alternative for UK brands'
  },
  '.com': {
    price: 20,
    freeEligible: true,
    label: 'Internationally recognised classic web address'
  },
  '.online': {
    price: 10,
    freeEligible: true,
    label: 'Great for digital-first businesses'
  },
  '.biz': {
    price: 10,
    freeEligible: true,
    label: 'Ideal for service-led companies'
  },
  '.london': {
    price: 20,
    freeEligible: false,
    label: 'Perfect if you trade in and around London'
  },
  '.design': {
    price: 20,
    freeEligible: false,
    label: 'Tailored for creative studios and makers'
  }
};

const DOMAIN_EXTENSION_ORDER = ['.co.uk', '.com', '.uk', '.online', '.biz', '.london', '.design'];
const DOMAIN_PRIMARY_VISIBLE_EXTENSIONS = ['.co.uk', '.com', '.uk'];
const AUTO_DOMAIN_CHECK_IDLE_MS = 900;

const AVAILABLE_DOMAIN_EXTENSIONS = DOMAIN_EXTENSION_ORDER.filter(
  (extension) => DOMAIN_EXTENSION_CONFIG[extension]
);

const GBP_CURRENCY_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

const MAX_DOMAIN_RETRIES = 5;
const MAX_PARALLEL_DOMAIN_CHECKS = 3;
const DOMAIN_FORMAT_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;


const toDomainSelection = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const normalizedValue = value.toLowerCase();
    const extension = AVAILABLE_DOMAIN_EXTENSIONS.find(ext => normalizedValue.endsWith(ext));
    if (!extension) return null;
    return { fullDomain: normalizedValue, extension };
  }

  if (typeof value === 'object') {
    const rawFullDomain = value.fullDomain || value.domain || value.value;
    if (!rawFullDomain) return null;
    const normalizedFullDomain = rawFullDomain.toLowerCase();
    const extension = value.extension || AVAILABLE_DOMAIN_EXTENSIONS.find(ext => normalizedFullDomain.endsWith(ext));
    if (!extension) return null;
    return { fullDomain: normalizedFullDomain, extension };
  }

  return null;
};

const normalizeSelectedDomainEntries = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    const seen = new Set();
    return value
      .map(toDomainSelection)
      .filter((item) => {
        if (!item) return false;
        if (seen.has(item.fullDomain)) {
          return false;
        }
        seen.add(item.fullDomain);
        return true;
      });
  }

  const single = toDomainSelection(value);
  return single ? [single] : [];
};

const extractSelectedDomains = (selectedValue, fallbackValue) => {
  if (Array.isArray(selectedValue)) {
    return normalizeSelectedDomainEntries(selectedValue);
  }

  if (selectedValue && typeof selectedValue === 'object' && !Array.isArray(selectedValue)) {
    return normalizeSelectedDomainEntries([selectedValue]);
  }

  return normalizeSelectedDomainEntries(fallbackValue);
};

const ensureValidFreeDomainSelection = (selectedDomains, proposedFreeDomain) => {
  if (!selectedDomains.length) {
    return '';
  }

  if (proposedFreeDomain) {
    const match = selectedDomains.find(domain => domain.fullDomain === proposedFreeDomain);
    if (match && DOMAIN_EXTENSION_CONFIG[match.extension]?.freeEligible) {
      return proposedFreeDomain;
    }
  }

  const firstEligible = selectedDomains.find(domain => DOMAIN_EXTENSION_CONFIG[domain.extension]?.freeEligible);
  return firstEligible ? firstEligible.fullDomain : '';
};

const calculateDomainAnnualCost = (selectedDomains, freeDomain) =>
  selectedDomains.reduce((total, domain) => {
    const config = DOMAIN_EXTENSION_CONFIG[domain.extension];
    if (!config) return total;
    if (config.freeEligible && freeDomain && domain.fullDomain === freeDomain) {
      return total;
    }
    return total + (config.price || 0);
  }, 0);

const normalizeDomainName = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  return value.toLowerCase().trim();
};

const buildAllConfiguredDomains = ({ domainOption, existingDomainFull, selectedDomains }) => {
  const domains = [];
  const seen = new Set();

  const normalizedExistingDomain = normalizeDomainName(existingDomainFull);
  if (domainOption === 'existing-domain' && normalizedExistingDomain) {
    seen.add(normalizedExistingDomain);
    domains.push({
      fullDomain: normalizedExistingDomain,
      source: 'existing'
    });
  }

  selectedDomains.forEach((domain) => {
    const normalizedDomain = normalizeDomainName(domain?.fullDomain);
    if (!normalizedDomain || seen.has(normalizedDomain)) {
      return;
    }

    seen.add(normalizedDomain);
    domains.push({
      fullDomain: normalizedDomain,
      source: 'new'
    });
  });

  return domains;
};

// SearchableSelect Component
const SearchableSelect = ({ field, fieldValue, error, handleInputChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(field.options);
  const [activeIndex, setActiveIndex] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) document.getElementById(`${field.id}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, field.id]);
  
  useEffect(() => {
    const filtered = field.options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
    setActiveIndex(0);
  }, [searchTerm, field.options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = field.options.find(option => option.value === fieldValue);

  const handleSelect = (option) => {
    handleInputChange(field.id, option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          type="text"
          id={field.id}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={`${field.id}-options`}
          aria-activedescendant={isOpen && filteredOptions[activeIndex] ? `${field.id}-option-${activeIndex}` : undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${field.id}-error` : undefined}
          autoComplete="off"
          onBlur={() => setIsOpen(false)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') { event.preventDefault(); setIsOpen(false); }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault(); setIsOpen(true);
              setActiveIndex(index => Math.max(0, Math.min(filteredOptions.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
            }
            if (event.key === 'Enter' && isOpen && filteredOptions[activeIndex]) { event.preventDefault(); handleSelect(filteredOptions[activeIndex]); }
          }}
          value={isOpen ? searchTerm : (selectedOption?.label || '')}
          onChange={handleSearchChange}
          onClick={handleInputClick}
          placeholder={field.placeholder || "Search or select an option..."}
          className={`w-full px-4 py-4 border focus:ring-yellow-400 focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 focus:shadow-lg focus:shadow-yellow-500/20 transition-all duration-300 cursor-pointer ${
            error ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
          } ${fieldValue ? 'bg-gray-50/50' : 'bg-white'}`}
          style={{ borderRadius: '3px' }}
          required={field.required}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {isOpen && (
        <div id={`${field.id}-options`} role="listbox" aria-label={field.label} className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-[3px] shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                id={`${field.id}-option-${index}`}
                role="option"
                aria-selected={fieldValue === option.value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option)}
                className={`px-4 py-3 cursor-pointer hover:bg-yellow-50 transition-colors ${activeIndex === index ? 'bg-yellow-50' : ''} ${
                  fieldValue === option.value ? 'bg-yellow-100 text-yellow-900' : 'text-gray-900'
                }`}
              >
                {option.label}
              </div>
            ))
          ) : (
            <>
              {field.options.some((option) => option.value === 'other') && (
                <div
                  role="option"
                  aria-selected={fieldValue === 'other'}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(field.options.find((option) => option.value === 'other'))}
                  className={`px-4 py-3 cursor-pointer hover:bg-yellow-50 transition-colors ${
                    fieldValue === 'other' ? 'bg-yellow-100 text-yellow-900' : 'text-gray-900'
                  }`}
                >
                  Other (please specify)
                </div>
              )}
              <div className="px-4 py-3 text-gray-500 text-sm space-y-1">
                <p>No matches found for "{searchTerm}".</p>
                {field.options.some((option) => option.value === 'other') && (
                  <p className="text-gray-600">Can’t find it? Choose “Other (please specify)”.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
      
      {error && (
        <p id={`${field.id}-error`} role="status" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};



const WebsitePackageBuilder = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [calculations, setCalculations] = useState({
    monthlyPrice: 0,
    setupFee: 0,
    yearlyTotal: 0,
    domainAnnualCost: 0,
    websitePlanMonthlyFee: 0,
    websitePlanMonthlyFeeBeforeDiscount: 0,
    websitePlanSetupFee: 0,
    discountCode: '',
    discountPercent: 0,
    websitePlanDiscountAmount: 0,
    discountApplied: false,
    oneOffAddonsTotal: 0,
    emailStorageUpgradeTotalMonthly: 0,
    emailStorageUpgradeQuantity: 0,
    domainItems: [],
    domainFieldBreakdown: {},
    emailStorageUpgradeItems: [],
    selectedAddOnItems: [],
    addOnFieldBreakdown: {}
  });
  const [hasCompletedSignature, setHasCompletedSignature] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [reviewEditing, setReviewEditing] = useState(false);
  const [visitedStepIds, setVisitedStepIds] = useState([]);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const continueInFlightRef = useRef(false);
  const [domainAvailability, setDomainAvailability] = useState({});
  const [activeDomainChecks, setActiveDomainChecks] = useState({});
  const [domainCountdowns, setDomainCountdowns] = useState({});
  const [domainCheckAttempts, setDomainCheckAttempts] = useState({});
  const [isCheckAllRunning, setIsCheckAllRunning] = useState(false);
  const [checkAllCurrentExtension, setCheckAllCurrentExtension] = useState(null);
  const [hasTriggeredInitialDomainCheck, setHasTriggeredInitialDomainCheck] = useState(false);
  const [showDomainTips, setShowDomainTips] = useState(false);
  const [showSelectedDomains, setShowSelectedDomains] = useState(false);
  const [showAllDomainExtensions, setShowAllDomainExtensions] = useState(false);
  const [debouncedDomainSearch, setDebouncedDomainSearch] = useState('');
  const [idleDomainSearch, setIdleDomainSearch] = useState('');
  const hasAppliedPlanDeeplinkRef = useRef(false);
  const countdownTimersRef = useRef({});
  const countdownResolversRef = useRef({});
  const checkAllCancellationRef = useRef(false);

  useEffect(() => {
    const timers = countdownTimersRef.current;
    return () => {
      Object.values(timers || {}).forEach((timer) => {
        if (timer) {
          clearInterval(timer);
        }
      });
      Object.keys(countdownResolversRef.current || {}).forEach((key) => {
        const resolver = countdownResolversRef.current[key];
        if (resolver) {
          resolver.cancelled = true;
          resolver.resolve({ cancelled: true });
        }
      });
      countdownResolversRef.current = {};
    };
    }, []);

  // eslint-disable-next-line no-unused-vars
  const runCountdown = useCallback((key, duration = 0) => {
    if (!key) {
      return Promise.resolve({ cancelled: false });
    }

    if (duration <= 0) {
      setDomainCountdowns((prev) => {
        const { [key]: _ignored, ...rest } = prev;
        return rest;
      });
      return Promise.resolve({ cancelled: false });
    }

    return new Promise((resolve) => {
      if (countdownTimersRef.current[key]) {
        clearInterval(countdownTimersRef.current[key]);
      }

      const existingResolver = countdownResolversRef.current[key];
      if (existingResolver) {
        existingResolver.cancelled = true;
        existingResolver.resolve({ cancelled: true });
        delete countdownResolversRef.current[key];
      }

      let remaining = duration;
      setDomainCountdowns((prev) => ({ ...prev, [key]: remaining }));

      countdownResolversRef.current[key] = {
        resolve,
        cancelled: false,
      };

      countdownTimersRef.current[key] = setInterval(() => {
        remaining -= 1;

        if (remaining <= 0) {
          clearInterval(countdownTimersRef.current[key]);
          countdownTimersRef.current[key] = null;
          setDomainCountdowns((prev) => {
            const { [key]: _removed, ...rest } = prev;
            return rest;
          });

          const resolver = countdownResolversRef.current[key];
          if (resolver) {
            resolver.resolve({ cancelled: resolver.cancelled });
            delete countdownResolversRef.current[key];
          } else {
            resolve({ cancelled: false });
          }
        } else {
          setDomainCountdowns((prev) => ({ ...prev, [key]: remaining }));
        }
      }, 1000);
    });
  }, []);

  const cancelCountdown = useCallback((key) => {
    if (!key) {
      return;
    }

    if (countdownTimersRef.current[key]) {
      clearInterval(countdownTimersRef.current[key]);
      countdownTimersRef.current[key] = null;
    }

    setDomainCountdowns((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, key)) {
        return prev;
      }
      const { [key]: _ignored, ...rest } = prev;
      return rest;
    });

    const resolver = countdownResolversRef.current[key];
    if (resolver) {
      resolver.cancelled = true;
      resolver.resolve({ cancelled: true });
      delete countdownResolversRef.current[key];
    }
  }, []);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  // Signature state
  const [signatureStep, setSignatureStep] = useState('review'); // 'review', 'creating', 'signing', 'completed', 'error'
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '', show: false });
  const [signatureData, setSignatureData] = useState({
    contractId: null,
    signingUrl: null,
    error: null,
    isCompleted: false,
    latestStatus: null,
    statusDetails: null,
    pendingSigners: [],
    lastCheckedAt: null
  });
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState(false);
  const [isCookiesPolicyModalOpen, setIsCookiesPolicyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [manualCheckCooldown, setManualCheckCooldown] = useState(0); // Rate limiting for manual checks
  const [hasFinalizedRegistration, setHasFinalizedRegistration] = useState(false);
  const [isTransitioningToSignature, setIsTransitioningToSignature] = useState(false);
  const [isAutoSubmittingAfterSignature, setIsAutoSubmittingAfterSignature] = useState(false);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const [showPricingDetails, setShowPricingDetails] = useState(false);
  const [showDiscountCodeInput, setShowDiscountCodeInput] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountStatusMessage, setDiscountStatusMessage] = useState({ type: '', text: '' });
  const [expandedSummaryCards, setExpandedSummaryCards] = useState({});
  const [summaryValidationAttempted, setSummaryValidationAttempted] = useState(false);
  const featureRefs = useRef([]);
  const shouldAutoScrollRef = useRef(true);
  const signatureCheckIntervalRef = useRef(null);
  const messageTriggeredCheckTimeoutRef = useRef(null);
  const lastManualCheckTimeRef = useRef(null);
  const cooldownIntervalRef = useRef(null);
  const pollingAttemptsRef = useRef(0); // Track number of polling attempts
  const pollingStartTimeRef = useRef(null); // Track when polling started
  const isFinalizingRegistrationRef = useRef(false);
  const autoFinalizeTriggeredRef = useRef(false);
  const hasAutoStartedSignatureRef = useRef(false);
  const preSubmissionInFlightRef = useRef(false);
  const currentYear = new Date().getFullYear();

  // Local browser persistence intentionally disabled for GDPR/privacy.

  const selectedDomains = useMemo(
    () => extractSelectedDomains(formData.selectedDomains, formData.newDomainName_selected),
    [formData.selectedDomains, formData.newDomainName_selected]
  );

  const isFreeDomainEntitled = useMemo(() => {
    if (formData.domainOption === 'free-domain') {
      return true;
    }

    if (formData.domainOption === 'existing-domain' && formData.domainTransferOption === 'keep-domain') {
      return true;
    }

    return false;
  }, [formData.domainOption, formData.domainTransferOption]);

  const freeDomainSelection = useMemo(
    () => (
      isFreeDomainEntitled
        ? ensureValidFreeDomainSelection(
            selectedDomains,
            formData.freeDomainSelection || formData.newDomainName_selected
          )
        : ''
    ),
    [selectedDomains, formData.freeDomainSelection, formData.newDomainName_selected, isFreeDomainEntitled]
  );

  const allConfiguredDomains = useMemo(
    () => buildAllConfiguredDomains({
      domainOption: formData.domainOption,
      existingDomainFull: formData.existingDomainFull,
      selectedDomains
    }),
    [formData.domainOption, formData.existingDomainFull, selectedDomains]
  );

  const primaryDomainSelection = useMemo(() => {
    const normalizedPrimary = normalizeDomainName(formData.primaryDomain);
    if (normalizedPrimary && allConfiguredDomains.some((domain) => domain.fullDomain === normalizedPrimary)) {
      return normalizedPrimary;
    }

    return allConfiguredDomains[0]?.fullDomain || '';
  }, [formData.primaryDomain, allConfiguredDomains]);

  const domainAnnualCost = useMemo(
    () => calculateDomainAnnualCost(selectedDomains, freeDomainSelection),
    [selectedDomains, freeDomainSelection]
  );

  useEffect(() => {
    setFormData((prev) => {
      const normalizedPrimary = normalizeDomainName(prev.primaryDomain);
      const hasSelectedPrimary = normalizedPrimary && allConfiguredDomains.some((domain) => domain.fullDomain === normalizedPrimary);

      if (hasSelectedPrimary) {
        return prev;
      }

      const fallbackPrimary = allConfiguredDomains[0]?.fullDomain || '';
      if (!fallbackPrimary && !normalizedPrimary) {
        return prev;
      }

      if (normalizedPrimary === fallbackPrimary) {
        return prev;
      }

      return {
        ...prev,
        primaryDomain: fallbackPrimary
      };
    });
  }, [allConfiguredDomains]);

  // Refs for Google Places Autocomplete (when implemented)
  const invoiceAddressRef = useRef(null);
  const businessAddressRef = useRef(null);

  const parseGoogleAddress = useCallback((place, type) => {
    if (!place.address_components) return;

    let streetNumber = '';
    let streetName = '';
    let city = '';
    let county = '';
    let postcode = '';

    place.address_components.forEach(component => {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          streetNumber = component.long_name;
          break;
        case 'route':
          streetName = component.long_name;
          break;
        case 'locality':
        case 'postal_town':
        case 'sublocality_level_1':
          if (!city) city = component.long_name;
          break;
        case 'administrative_area_level_2':
          county = component.long_name;
          break;
        case 'administrative_area_level_1':
          if (!county) county = component.long_name;
          break;
        case 'postal_code':
          postcode = component.long_name;
          break;
        default:
          break;
      }
    });

    const fullAddress = `${streetNumber} ${streetName}`.trim();

    if (type === 'invoice') {
      setFormData(prev => ({
        ...prev,
        invoiceAddressLine1: fullAddress,
        invoiceCity: city,
        invoiceCounty: county,
        invoicePostcode: postcode
      }));
    } else if (type === 'business') {
      setFormData(prev => ({
        ...prev,
        businessAddressLine1: fullAddress,
        businessCity: city,
        businessCounty: county,
        businessPostcode: postcode
      }));
    }
  }, []);

  const initializeAutocomplete = useCallback(() => {
    console.log('Initializing autocomplete...'); // Debug log

    // Clean up any existing autocomplete instances
    if (window.invoiceAutocomplete) {
      window.google.maps.event.clearInstanceListeners(window.invoiceAutocomplete);
    }
    if (window.businessAutocomplete) {
      window.google.maps.event.clearInstanceListeners(window.businessAutocomplete);
    }

    // Invoice address autocomplete
    if (invoiceAddressRef.current && invoiceAddressRef.current.offsetParent !== null) {
      console.log('Setting up invoice address autocomplete'); // Debug log
      window.invoiceAutocomplete = new window.google.maps.places.Autocomplete(
        invoiceAddressRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'gb' }, // UK addresses only
          fields: ['address_components', 'formatted_address', 'place_id']
        }
      );

      window.invoiceAutocomplete.addListener('place_changed', () => {
        const place = window.invoiceAutocomplete.getPlace();
        parseGoogleAddress(place, 'invoice');
      });
    }

    // Business address autocomplete (check if element exists and is visible)
    if (businessAddressRef.current && businessAddressRef.current.offsetParent !== null) {
      console.log('Setting up business address autocomplete'); // Debug log
      window.businessAutocomplete = new window.google.maps.places.Autocomplete(
        businessAddressRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'gb' }, // UK addresses only
          fields: ['address_components', 'formatted_address', 'place_id']
        }
      );

      window.businessAutocomplete.addListener('place_changed', () => {
        const place = window.businessAutocomplete.getPlace();
        parseGoogleAddress(place, 'business');
      });
    }
  }, [parseGoogleAddress]);


  useEffect(() => {
    const searchValue = String(formData.newDomainName || '').trim().toLowerCase();
    const timeoutId = setTimeout(() => {
      setDebouncedDomainSearch(searchValue);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [formData.newDomainName]);

  useEffect(() => {
    const searchValue = String(formData.newDomainName || '').trim().toLowerCase();

    if (!searchValue || searchValue.length < 3) {
      setIdleDomainSearch('');
      return;
    }

    const timeoutId = setTimeout(() => {
      setIdleDomainSearch(searchValue);
    }, AUTO_DOMAIN_CHECK_IDLE_MS);

    return () => clearTimeout(timeoutId);
  }, [formData.newDomainName]);

  useEffect(() => {
    if (hasAppliedPlanDeeplinkRef.current || typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search || '');
    const rawPlan = String(params.get('plan') || '').trim().toLowerCase();

    const planMap = {
      essential: 'essential',
      starter: 'essential',
      business: 'business',
      premium: 'premium',
      'online-store': 'premium',
      onlinestore: 'premium',
      store: 'premium'
    };
    const preselectOnlineStore = ['online-store', 'onlinestore', 'store'].includes(rawPlan);

    const selectedPlan = planMap[rawPlan];
    if (!selectedPlan) {
      hasAppliedPlanDeeplinkRef.current = true;
      return;
    }

    hasAppliedPlanDeeplinkRef.current = true;
    setFormData((prev) => ({
      ...prev,
      websitePlan: selectedPlan,
      ...(preselectOnlineStore
        ? { features: Array.from(new Set([...(Array.isArray(prev.features) ? prev.features : []), 'onlinestore'])) }
        : {})
    }));
    setCurrentStep(1);
    setValidationErrors((prev) => {
      if (!prev || !Object.keys(prev).length) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors.websitePlan;
      return nextErrors;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search || '');
    const deepLinkedCode = String(params.get('discount') || params.get('promo') || '').trim().toUpperCase();
    if (!deepLinkedCode) {
      return;
    }

    setShowDiscountCodeInput(true);
    setDiscountCodeInput(deepLinkedCode);
    setFormData((prev) => ({
      ...prev,
      discountCode: deepLinkedCode
    }));
  }, []);

  // Auto-poll signature status when user is signing (checks datastore, not eSignatures API)
  // DISABLED: Datastore polling removed - Make.com API doesn't support GET/READ operations
  // Signature completion is detected via eSignatures.io iframe postMessage events (see handleSignerMessage)
  useEffect(() => {
    if (signatureStep !== 'signing' || !formData.submissionId) {
      return;
    }

    console.log('Signature modal open - waiting for eSignatures.io iframe completion event...');

    // No polling needed - we rely on postMessage from iframe
    return () => {
      console.log('Signature step changed or unmounting');
    };
  }, [signatureStep, formData.submissionId]);

  useEffect(() => {
    let isActive = true;

    const loadGoogleMaps = async () => {
      try {
        await loadGoogleMapsScript();
        if (isActive) {
          setIsGoogleMapsReady(true);
        }
      } catch (error) {
        console.error('Failed to load Google Maps script:', error);
      }
    };

    loadGoogleMaps();

    return () => {
      isActive = false;
    };
  }, []);

useEffect(() => {
  const updateViewport = () => {
    setIsMobileViewport(window.innerWidth < 1024);
  };

  updateViewport();
  window.addEventListener('resize', updateViewport);
  return () => window.removeEventListener('resize', updateViewport);
}, []);

// Check button visibility for mobile indicator (IntersectionObserver for better performance)
useEffect(() => {
  const navButtons = document.querySelector('.navigation-buttons');
  const hasStepData = currentStep === 0 ? formData.websitePlan : true;

  if (!hasStepData || !isMobileViewport || !navButtons) {
    setShowScrollIndicator(false);
    return undefined;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      setShowScrollIndicator(!entry.isIntersecting);
    },
    { threshold: 0.1 }
  );

  observer.observe(navButtons);

  return () => {
    observer.disconnect();
  };
}, [formData.websitePlan, currentStep, isMobileViewport]);

useEffect(() => {
  const viewport = window.visualViewport;
  const update = () => {
    const editing = document.activeElement?.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select');
    setKeyboardOpen(Boolean(editing && (!viewport || window.innerHeight - viewport.height > 120)));
  };
  viewport?.addEventListener('resize', update);
  document.addEventListener('focusin', update);
  document.addEventListener('focusout', update);
  return () => {
    viewport?.removeEventListener('resize', update);
    document.removeEventListener('focusin', update);
    document.removeEventListener('focusout', update);
  };
}, []);

  const allFormSteps = useMemo(() => ([
    {
      id: 'website-plan',
      title: 'Choose a website plan',
      subtitle: 'Pick the plan that fits your business.',
      fields: [
        {
          id: 'websitePlan',
          type: 'plan-cards',
          label: '',
          required: true,
          options: [
            {
              value: 'essential',
              label: 'Essential Plan',
              pages: '5 Pages',
              emails: '1 Email Address',
              monthlyPrice: 39,
              setupFee: 0,
              icon: <EssentialIcon />
            },
            {
              value: 'business',
              label: 'Business Plan',
              pages: '10 Pages + 5 Location Pages',
              emails: '3 Email Addresses',
              monthlyPrice: 69,
              setupFee: 0,
              popular: true,
              icon: <PremiumIcon />
            },
            {
              value: 'premium',
              label: 'Premium Plan',
              pages: '20 Pages + 15 Location Pages',
              emails: '5 Email Addresses',
              monthlyPrice: 89,
              setupFee: 0,
              icon: <PremiumIcon />
            }
          ]
        }
      ]
    },
    {
      id: 'web-address',
      title: 'Your web address',
      subtitle: 'Choose your web address option.',
      fields: [
        {
          id: 'domainOption',
          type: 'radio',
          label: '',
          required: true,
          options: [
            {
              value: 'free-domain',
              label: 'Get a free web address with Ubie Websites',
              description: 'One standard web address is included. Additional web addresses are billed annually.'
            },
            { value: 'existing-domain', label: 'I already have a web address', description: 'Use my existing web address' }
          ],
          helpText: ''
        }
      ]
    },
    {
      id: 'domain-details',
      title: 'Choose web address',
      subtitle: 'Search for a new web address or use your current one.',
      fields: [
        {
          id: 'newDomainName',
          type: 'domain-checker',
          label: 'Search for your web address',
          required: true,
          placeholder: 'Enter your desired web address',
          conditionalDisplay: (data) => data.domainOption === 'free-domain',
          revealedContentId: 'domainOption-free-domain'
        },
        {
          id: 'existingDomainFull',
          type: 'text',
          label: 'Your existing web address',
          required: true,
          placeholder: 'e.g. mywebsite.co.uk',
          helpText: 'Enter format: mywebaddress.com',
          conditionalDisplay: (data) => data.domainOption === 'existing-domain',
          revealedContentId: 'domainOption-existing-domain'
        },
        {
          id: 'domainTransferOption',
          type: 'radio',
          label: '',
          required: true,
          options: [
            {
              value: 'transfer-domain',
              label: 'Move my web address to Ubie Websites',
              description: 'We\'ll guide you through the transfer process.'
            },
            {
              value: 'keep-domain',
              label: 'Keep my web address where it is',
              description: 'After your registration, we will contact you to organise the next setup steps for your web address.'
            }
          ],
          conditionalDisplay: (data) => data.domainOption === 'existing-domain',
          revealedContentId: 'domainTransferOption',
          helpText: 'We\'ll guide you after signup.'
        },
        {
          id: 'existingDomainAdditionalChoice',
          type: 'radio',
          label: 'Do you also want to check availability for extra web addresses?',
          required: true,
          options: [
            {
              value: 'add',
              label: 'Yes, show me the web address search',
              description: 'Check for extra web addresses and apply your free credit if eligible.'
            },
            {
              value: 'none',
              label: 'No, just use my existing web address'
            }
          ],
          conditionalDisplay: (data) => data.domainOption === 'existing-domain' && !!data.domainTransferOption,
          revealedContentId: 'existing-domain-additional',
          helpText: 'Optional.'
        }
      ]
    },
    {
      id: 'additional-domain-search',
      title: 'Add extra web addresses',
      subtitle: 'Search and add an extra web address to continue.',
      fields: [
        {
          id: 'newDomainName',
          type: 'domain-checker',
          label: 'Search for your web address',
          required: true,
          placeholder: 'Enter your desired web address',
          conditionalDisplay: (data) =>
            data.domainOption === 'existing-domain' &&
            !!data.domainTransferOption &&
            data.existingDomainAdditionalChoice === 'add',
          revealedContentId: 'existing-domain-additional',
        }
      ]
    },
    {
      id: 'primary-domain-selection',
      title: 'Choose your primary web address',
      subtitle: 'Choose your main web address.',
      fields: [
        {
          id: 'primaryDomain',
          type: 'primary-domain-selector',
          label: 'Primary web address',
          required: true
        }
      ]
    },
    {
      id: 'about-business',
      title: 'Tell us about your business',
      subtitle: 'Tell us about your business.',
      fields: [
        {
          id: 'businessName',
          type: 'text',
          label: 'Business Name',
          required: true,
          placeholder: 'Enter your business name'
        },
        // Replace the businessCategory options array in your App.js with this updated version:

{
  id: 'businessCategory',
  type: 'select',
  label: 'What type of business do you run?',
  required: true,
  options: [
    { value: 'accountant', label: 'Accountant' },
    { value: 'accounting-firm', label: 'Accounting Firm' },
    { value: 'advertising-agency', label: 'Advertising Agency' },
    { value: 'architect', label: 'Architect' },
    { value: 'art-gallery', label: 'Art Gallery' },
    { value: 'attorney', label: 'Solicitor' }, // Changed from Attorney
    { value: 'auto-repair-shop', label: 'Car Repair Shop' }, // Changed from Auto Repair Shop
    { value: 'bakery', label: 'Bakery' },
    { value: 'bank', label: 'Bank' },
    { value: 'bar', label: 'Bar' },
    { value: 'barber-shop', label: 'Barber Shop' },
    { value: 'beauty-salon', label: 'Beauty Salon' },
    { value: 'book-store', label: 'Bookshop' }, // Changed from Book Store
    { value: 'bowling-alley', label: 'Bowling Alley' },
    { value: 'building-society', label: 'Building Society' },
    { value: 'business-management-consultant', label: 'Business Management Consultant' },
    { value: 'cafe', label: 'Café' }, // Added accent
    { value: 'car-dealer', label: 'Car Dealer' },
    { value: 'car-rental-agency', label: 'Car Hire' }, // Changed from Car Rental Agency
    { value: 'carpenter', label: 'Carpenter' },
    { value: 'caterer', label: 'Caterer' },
    { value: 'chartered-accountant', label: 'Chartered Accountant' },
    { value: 'chartered-surveyor', label: 'Chartered Surveyor' },
    { value: 'childminder', label: 'Childminder' },
    { value: 'chiropractor', label: 'Chiropractor' },
    { value: 'church', label: 'Church' },
    { value: 'cleaning-service', label: 'Cleaning Service' },
    { value: 'clothing-store', label: 'Clothes Shop' }, // Changed from Clothing Store
    { value: 'coffee-shop', label: 'Coffee Shop' },
    { value: 'commercial-real-estate-agency', label: 'Commercial Property Agent' }, // Changed from Commercial Real Estate Agency
    { value: 'computer-repair-service', label: 'Computer Repair Service' },
    { value: 'construction-company', label: 'Construction Company' },
    { value: 'convenience-store', label: 'Convenience Store' },
    { value: 'conveyancer', label: 'Conveyancer' },
    { value: 'dentist', label: 'Dentist' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'driving-school', label: 'Driving School' },
    { value: 'electrician', label: 'Electrician' },
    { value: 'electronics-store', label: 'Electronics Shop' }, // Changed from Electronics Store
    { value: 'estate-agent', label: 'Estate Agent' },
    { value: 'fitness-center', label: 'Fitness Centre' }, // Changed from Fitness Center (UK spelling)
    { value: 'florist', label: 'Florist' },
    { value: 'funeral-home', label: 'Funeral Director' }, // Changed from Funeral Home
    { value: 'furniture-store', label: 'Furniture Shop' }, // Changed from Furniture Store
    { value: 'gas-station', label: 'Petrol Station' }, // Changed from Gas Station
    { value: 'general-contractor', label: 'General Contractor' },
    { value: 'grocery-store', label: 'Grocery Shop' }, // Changed from Grocery Store
    { value: 'gym', label: 'Gym' },
    { value: 'hair-salon', label: 'Hair Salon' },
    { value: 'hardware-store', label: 'Hardware Shop' }, // Changed from Hardware Store
    { value: 'home-improvement-store', label: 'DIY Shop' }, // Changed from Home Improvement Store
    { value: 'hospital', label: 'Hospital' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'insurance-agency', label: 'Insurance Broker' }, // Changed from Insurance Agency
    { value: 'jewelry-store', label: 'Jewellery Shop' }, // Changed from Jewelry Store (UK spelling)
    { value: 'lawyer', label: 'Lawyer' },
    { value: 'library', label: 'Library' },
    { value: 'locksmith', label: 'Locksmith' },
    { value: 'meal-delivery', label: 'Meal Delivery' },
    { value: 'medical-clinic', label: 'Medical Practice' }, // Changed from Medical Clinic
    { value: 'moving-company', label: 'Removal Company' }, // Changed from Moving Company
    { value: 'nail-salon', label: 'Nail Salon' },
    { value: 'night-club', label: 'Nightclub' }, // Changed from Night Club (one word)
    { value: 'optometrist', label: 'Optician' }, // Changed from Optometrist
    { value: 'painter', label: 'Painter' },
    { value: 'park', label: 'Park' },
    { value: 'pet-store', label: 'Pet Shop' }, // Changed from Pet Store
    { value: 'pharmacy', label: 'Pharmacy' },
    { value: 'photographer', label: 'Photographer' },
    { value: 'physiotherapist', label: 'Physiotherapist' },
    { value: 'plumber', label: 'Plumber' },
    { value: 'post-office', label: 'Post Office' },
    { value: 'primary-school', label: 'Primary School' },
    { value: 'pub', label: 'Pub' },
    { value: 'real-estate-agent', label: 'Estate Agent' }, // Changed from Real Estate Agent
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'roofing-contractor', label: 'Roofing Contractor' },
    { value: 'school', label: 'School' },
    { value: 'secondary-school', label: 'Secondary School' },
    { value: 'shoe-store', label: 'Shoe Shop' }, // Changed from Shoe Store
    { value: 'shopping-mall', label: 'Shopping Centre' }, // Changed from Shopping Mall
    { value: 'solicitor', label: 'Solicitor' },
    { value: 'spa', label: 'Spa' },
    { value: 'storage-facility', label: 'Self Storage' }, // Changed from Storage Facility
    { value: 'supermarket', label: 'Supermarket' },
    { value: 'swimming-pool', label: 'Swimming Pool' },
    { value: 'taxi-service', label: 'Taxi Service' },
    { value: 'tourist-attraction', label: 'Tourist Attraction' },
    { value: 'travel-agency', label: 'Travel Agent' }, // Changed from Travel Agency
    { value: 'university', label: 'University' },
    { value: 'used-car-dealer', label: 'Used Car Dealer' },
    { value: 'veterinarian', label: 'Veterinary Surgery' }, // Changed from Veterinarian
    { value: 'wedding-venue', label: 'Wedding Venue' },
    { value: 'other', label: 'Other (please specify)' }
  ]
},
        {
          id: 'businessCategoryOther',
          type: 'text',
          label: 'Please specify your business category',
          required: true,
          placeholder: 'Enter your business category',
          conditionalDisplay: (data) => data.businessCategory === 'other',
          revealedContentId: 'businessCategory-other'
        },
        {
          id: 'businessType',
          type: 'select',
          label: 'How is your business set up?',
          required: true,
          options: [
            { value: 'sole-trader', label: 'Sole Trader (Self Assessment)' },
            { value: 'limited-company', label: 'Limited Company (Ltd)' },
            { value: 'partnership', label: 'Partnership' },
            { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
            { value: 'charity', label: 'Charity' }
          ]
        },
        {
          id: 'charityNumber',
          type: 'text',
          label: 'Charity Number',
          required: false,
          placeholder: 'Enter your registered charity number',
          helpText: 'Why we ask: to verify your registered charity details for your agreement.',
          conditionalDisplay: (data) => data.businessType === 'charity',
          revealedContentId: 'businessType-charity'
        }
      ]
    },
    {
      id: 'contact-information',
      title: 'How can we reach you?',
      subtitle: 'Add your contact details.',
      fields: [
        {
          id: 'firstName',
          type: 'text',
          label: 'First Name',
          required: true,
          placeholder: 'Enter your first name',
          maxLength: NAME_INPUT_MAX_LENGTH,
          width: 'half'
        },
        {
          id: 'lastName',
          type: 'text',
          label: 'Last Name',
          required: true,
          placeholder: 'Enter your last name',
          maxLength: NAME_INPUT_MAX_LENGTH,
          width: 'half'
        },
        {
          id: 'email',
          type: 'email',
          label: 'Email Address',
          required: true,
          placeholder: 'your.email@example.com',
          helpText: 'Use an email you can access now.',
          maxLength: 120
        },
        {
          id: 'phone',
          type: 'tel',
          label: 'Contact Mobile Number',
          placeholder: 'Enter the remaining 9 digits',
          required: true
        },
        {
          id: 'separateBusinessPhone',
          type: 'checkbox-single',
          label: 'Use a different phone number on my website',
          helpText: 'Tick only if your website phone number is different.'
        }
      ]
    },
    {
      id: 'account-verification',
      title: 'Quick verification',
      subtitle: 'Confirm your details.',
      fields: [
        {
          id: 'confirmEmail',
          type: 'email',
          label: 'Confirm Your Email Address',
          required: true,
          placeholder: 'Enter your email address again',
          maxLength: 120
        },
        {
          id: 'dateOfBirth',
          type: 'date',
          label: 'Date of Birth',
          required: true,
          helpText: 'Why we ask: contract identity check (18+).',
        }
      ]
    },
    {
      id: 'newsletter-signup',
      title: 'Stay in the loop',
      subtitle: 'Choose which updates you want.',
      fields: [
        {
          id: 'newsletterSubscriptions',
          type: 'newsletter-checkbox',
          label: '',
          options: [
            {
              value: 'essential-updates',
              label: 'Important service updates and announcements',
              description: 'Important updates about your website and our services.',
              recommended: true
            },
            {
              value: 'tips-offers',
              label: 'Website advice and exclusive offers',
              description: 'Tips, SEO advice, and exclusive offers.'
            }
          ]
        }
      ]
    },
    {
      id: 'address-website-display',
      title: 'Your address details',
      subtitle: 'Add your billing details.',
      fields: [
        {
          id: 'invoiceAddressLine1',
          type: 'text',
          label: 'Your Address',
          required: true,
          placeholder: 'Start typing your address...',
        },
        {
          id: 'invoiceAddressLine2',
          type: 'text',
          label: 'Address Line 2',
          placeholder: 'Flat, building name, etc. (optional)'
        },
        {
          id: 'invoiceCity',
          type: 'text',
          label: 'Town/City',
          required: true,
          placeholder: 'Enter your town or city',
          width: 'two-thirds'
        },
        {
          id: 'invoiceCounty',
          type: 'text',
          label: 'County',
          required: true,
          placeholder: 'Enter your county',
          width: 'one-third'
        },
        {
          id: 'invoicePostcode',
          type: 'text',
          label: 'Postcode',
          required: true,
          placeholder: 'e.g. SW1A 1AA'
        },
        {
          id: 'separateBusinessAddress',
          type: 'checkbox-single',
          label: 'Use a different business address on my website',
          helpText: 'Tick only if your website address is different.'
        }
      ]
    },
    {
      id: 'enhance-website',
      title: 'Complete your website',
      subtitle: 'Choose extras (optional).',
      fields: [
        {
          id: 'features',
          type: 'checkbox',
          label: '',
          options: [
            { 
              value: 'logo-design', 
              label: 'Logo Design', 
              monthlyPrice: 0, 
              setupPrice: 65, 
              description: 'A professional logo for your business.',
              popular: true,
              icon: (
                <svg className="w-12 h-12 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              )
            },
            { 
              value: 'social-setup', 
              label: 'Social Media Setup + Branding', 
              monthlyPrice: 0, 
              setupPrice: 0, 
              description: 'We design social cover photos, optimise profile icon logos, and complete social profile metadata. You need a logo from us or a suitable existing one.',
              icon: (
                <svg className="w-12 h-12 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              )
            },
            {
              value: 'industry-addons',
              label: 'Online Store & Industry Add-ons',
              monthlyPrice: 0,
              setupPrice: 0,
              description: 'Extra features for online shops, holiday lets, estate agents and restaurants.',
              icon: <OnlineStoreIcon />
            },
            {
              value: 'local-seo',
              label: 'Local SEO',
              monthlyPrice: 0,
              setupPrice: 0,
              description: 'Help more local customers find you on Google.',
              icon: <LocalSeoIcon />
            }
          ]
        }
      ]
    },
    {
      id: 'review-submit',
      title: 'Review your website plan',
      subtitle: 'Check your details before submitting your registration.',
      isSummary: true
    }

  ]), []);

  const formSteps = useMemo(() => {
    const shouldIncludeStep = (step) => {
      // A single unique domain is already selected as primary automatically.
      if (step.id === 'primary-domain-selection') {
        return allConfiguredDomains.length > 1;
      }

      if (step.isSummary || step.isSignature) {
        return true;
      }

      if (!step.fields || step.fields.length === 0) {
        return true;
      }

      return step.fields.some(field => !field.conditionalDisplay || field.conditionalDisplay(formData));
    };

    return allFormSteps.filter(shouldIncludeStep);
  }, [allFormSteps, formData, allConfiguredDomains]);

  const addressStepIndex = useMemo(
    () => formSteps.findIndex(step => step.id === 'address-website-display'),
    [formSteps]
  );

  // 1. FIRST useEffect - Main Google Maps initialization
  useEffect(() => {
    if (!isGoogleMapsReady || addressStepIndex === -1 || currentStep !== addressStepIndex) {
      return undefined;
    }

    const checkAndInitialize = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setTimeout(() => {
          initializeAutocomplete();
        }, 100);
      }
    };

    if (window.google) {
      checkAndInitialize();
      return undefined;
    }

    const interval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        checkAndInitialize();
        clearInterval(interval);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [currentStep, addressStepIndex, initializeAutocomplete, isGoogleMapsReady]);

  // 2. SECOND useEffect - Business Address Specific
  useEffect(() => {
    if (!isGoogleMapsReady) {
      return;
    }

    // Initialize business address autocomplete when the field becomes visible
    if (formData.separateBusinessAddress &&
        formData.separateBusinessAddress.includes('separate') &&
        window.google &&
        window.google.maps &&
        window.google.maps.places &&
        businessAddressRef.current &&
        addressStepIndex !== -1 &&
        currentStep === addressStepIndex) {

      console.log('Business address field is now visible, initializing autocomplete...'); // Debug log

      // Small delay to ensure the field is fully rendered
      setTimeout(() => {
        if (businessAddressRef.current && businessAddressRef.current.offsetParent !== null) {
          // Clean up existing instance
          if (window.businessAutocomplete) {
            window.google.maps.event.clearInstanceListeners(window.businessAutocomplete);
          }

          // Create new autocomplete instance
          window.businessAutocomplete = new window.google.maps.places.Autocomplete(
            businessAddressRef.current,
            {
              types: ['address'],
              componentRestrictions: { country: 'gb' },
              fields: ['address_components', 'formatted_address', 'place_id']
            }
          );

          window.businessAutocomplete.addListener('place_changed', () => {
            const place = window.businessAutocomplete.getPlace();
            parseGoogleAddress(place, 'business');
          });

          console.log('Business address autocomplete initialized successfully'); // Debug log
        }
      }, 200);
    }
  }, [formData.separateBusinessAddress, currentStep, addressStepIndex, parseGoogleAddress, isGoogleMapsReady]);

  // 3. THIRD useEffect - Cleanup
  useEffect(() => {
    // Cleanup function when component unmounts
    return () => {
      if (window.invoiceAutocomplete) {
        window.google.maps.event.clearInstanceListeners(window.invoiceAutocomplete);
      }
      if (window.businessAutocomplete) {
        window.google.maps.event.clearInstanceListeners(window.businessAutocomplete);
      }
    };
  }, []);

  useEffect(() => {
    if (currentStep > formSteps.length) {
      setCurrentStep(Math.max(formSteps.length, 0));
    }
  }, [currentStep, formSteps.length]);

  const signatureStepIndex = useMemo(
    () => formSteps.findIndex(step => step.isSignature),
    [formSteps]
  );

  const findStepIndex = useCallback(
    (stepId) => formSteps.findIndex(step => step.id === stepId),
    [formSteps]
  );

  useEffect(() => {
    const id = formSteps[currentStep]?.id;
    if (id) setVisitedStepIds(prev => prev.includes(id) ? prev : [...prev, id]);
  }, [currentStep, formSteps]);

  const isBackNavigationLocked = signatureStepIndex !== -1 && currentStep >= signatureStepIndex;
  const canGoBack = currentStep > 0 && !isBackNavigationLocked && currentStep < formSteps.length && !isTransitioningToSignature;

  const handleStepChange = useCallback((newStep) => {
    if (preSubmissionInFlightRef.current || typeof newStep !== 'number' || newStep < 0 || newStep > formSteps.length) {
      return;
    }

    if (isBackNavigationLocked && newStep < signatureStepIndex) {
      return;
    }

    // FIX: Block navigation back to signature step if already completed
    if (hasCompletedSignature && formSteps[newStep]?.isSignature) {
      setStatusMessage({
        type: 'info',
        text: 'Your signature has already been completed. Please continue to the final confirmation step.',
        show: true
      });
      setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 3000);
      return;
    }

    setCurrentStep(newStep);

    setTimeout(() => {
      const stepTitle = document.getElementById('registration-step-title');
      stepTitle?.focus({ preventScroll: true });
      stepTitle?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }, 50);
  }, [isBackNavigationLocked, signatureStepIndex, hasCompletedSignature, formSteps]);

  const navigateToStep = useCallback((stepId) => {
    const targetIndex = findStepIndex(stepId);

    if (targetIndex === -1) {
      return;
    }

    if (formSteps[currentStep]?.isSummary) setReviewEditing(true);
    handleStepChange(targetIndex);
  }, [findStepIndex, handleStepChange, formSteps, currentStep]);


  const navigateToFirstAvailableStep = useCallback((stepIds = []) => {
    if (!Array.isArray(stepIds) || stepIds.length === 0) {
      return;
    }

    const targetStepId = stepIds.find((stepId) => findStepIndex(stepId) !== -1);
    if (!targetStepId) {
      return;
    }

    navigateToStep(targetStepId);
  }, [findStepIndex, navigateToStep]);

  const createInvoiceLineItems = useCallback((domains = [], freeDomainSelection = '') => {
    const items = [];
    const planOption = formSteps?.[0]?.fields?.[0]?.options?.find(opt => opt.value === formData.websitePlan);
    const planName = planOption?.label || (formData.websitePlan ? `${formData.websitePlan} Plan` : 'Website Plan');

    if (planName && calculations.websitePlanMonthlyFee > 0) {
      items.push({
        type: 'plan_monthly_fee_upfront',
        description: calculations.discountApplied
          ? `${planName} - First Monthly Fee (Upfront) (${calculations.discountCode} applied)`
          : `${planName} - First Monthly Fee (Upfront)`,
        quantity: 1,
        unitAmount: Number(calculations.websitePlanMonthlyFee.toFixed(2))
      });
    }

    if (planName && calculations.websitePlanSetupFee > 0) {
      items.push({
        type: 'plan_setup_fee',
        description: `${planName} - Setup Fee`,
        quantity: 1,
        unitAmount: Number(calculations.websitePlanSetupFee.toFixed(2))
      });
    }

    const features = Array.isArray(formData.features) ? formData.features : [];

    features.forEach(featureValue => {
      switch (featureValue) {
        case 'logo-design':
          items.push({
            type: 'addon',
            description: 'Business Logo Design',
            quantity: 1,
            unitAmount: 65
          });
          break;
        case 'logo-design-bespoke':
          items.push({
            type: 'addon',
            description: 'Bespoke Logo Design',
            quantity: 1,
            unitAmount: 499
          });
          break;
        case 'business-card':
          items.push({
            type: 'addon',
            description: 'Business Card Design (Single Sided)',
            quantity: 1,
            unitAmount: 85
          });
          break;
        case 'business-card-print':
          items.push({
            type: 'addon',
            description: 'Business Card Design + Print 500 (Single Sided)',
            quantity: 1,
            unitAmount: 110
          });
          break;
        case 'business-card-double':
          items.push({
            type: 'addon',
            description: 'Business Card Design (Double Sided)',
            quantity: 1,
            unitAmount: 125
          });
          break;
        case 'business-card-double-print':
          items.push({
            type: 'addon',
            description: 'Business Card Design + Print 500 (Double Sided)',
            quantity: 1,
            unitAmount: 160
          });
          break;
        case 'social-setup-package-1':
          items.push({
            type: 'addon',
            description: 'Social Media Setup + Branding - Package 1 (Facebook, LinkedIn, X/Twitter)',
            quantity: 1,
            unitAmount: 165
          });
          break;
        case 'social-setup-package-2':
          items.push({
            type: 'addon',
            description: 'Social Media Setup + Branding - Package 2 (Facebook, LinkedIn, X/Twitter, Instagram, YouTube)',
            quantity: 1,
            unitAmount: 195
          });
          break;
        default: {
          const industryAddon = getPricedAddon(featureValue);
          if (!industryAddon || !isAddonAllowedOnPlan(industryAddon, formData.websitePlan)) {
            break;
          }
          if (industryAddon.billingType === 'monthly') {
            items.push({
              type: 'addon_monthly_fee_upfront',
              description: `${industryAddon.label} Add-on - First Monthly Fee (Upfront)`,
              quantity: 1,
              unitAmount: industryAddon.amount
            });
          } else if (industryAddon.billingType === 'quote') {
            items.push({
              type: 'addon_quote',
              description: `${industryAddon.label} (price to be confirmed)`,
              quantity: 1,
              unitAmount: 0
            });
          } else {
            items.push({
              type: 'addon',
              description: industryAddon.label,
              quantity: 1,
              unitAmount: industryAddon.amount
            });
          }
          break;
        }
      }
    });

    const validFreeDomainSelection = ensureValidFreeDomainSelection(domains, freeDomainSelection);

    domains.forEach(domain => {
      const domainConfig = DOMAIN_EXTENSION_CONFIG[domain.extension] || {};
      const isFree = domainConfig.freeEligible && validFreeDomainSelection === domain.fullDomain;
      items.push({
        type: 'domain',
        description: `Domain: ${domain.fullDomain}${isFree ? ' (free credit applied)' : ''}`,
        quantity: 1,
        unitAmount: isFree ? 0 : (domainConfig.price || 0)
      });
    });

    if (formData.existingDomainFull) {
      items.push({
        type: 'domain_existing',
        description: `Existing web address: ${formData.existingDomainFull}`,
        quantity: 1,
        unitAmount: 0
      });
    }

    return items;
  }, [formSteps, formData, calculations]);

  const createXeroLineItems = useCallback((invoiceItems = []) => {
    return invoiceItems.map(item => {
      const quantity = Number.isFinite(Number(item?.quantity)) && Number(item?.quantity) > 0
        ? Number(item.quantity)
        : 1;

      const unitAmountNumber = Number(item?.unitAmount ?? 0);
      const unitAmount = Number.isFinite(unitAmountNumber) ? Number(unitAmountNumber.toFixed(2)) : 0;

      return {
        Description: item?.description || '',
        Quantity: quantity,
        UnitAmount: unitAmount,
        AccountCode: '200'
      };
    });
  }, []);

  const createRecurringMonthlyLineItems = useCallback(() => {
    const items = [];
    const planOption = formSteps?.[0]?.fields?.[0]?.options?.find(opt => opt.value === formData.websitePlan);
    const planName = planOption?.label || (formData.websitePlan ? `${formData.websitePlan} Plan` : 'Website Plan');

    if (planName && calculations.websitePlanMonthlyFee > 0) {
      items.push({
        type: 'plan_monthly_fee',
        description: calculations.discountApplied
          ? `${planName} - Monthly Fee (${calculations.discountCode} applied)`
          : `${planName} - Monthly Fee`,
        quantity: 1,
        unitAmount: Number(calculations.websitePlanMonthlyFee.toFixed(2))
      });
    }

    const monthlyUpgradeItems = Array.isArray(calculations.emailStorageUpgradeItems)
      ? calculations.emailStorageUpgradeItems
      : [];

    monthlyUpgradeItems.forEach((item) => {
      const amount = Number(item?.amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      items.push({
        type: item?.id || 'email_storage_upgrade',
        description: item?.title || 'Email storage upgrade',
        quantity: Number.isFinite(Number(item?.quantity)) && Number(item.quantity) > 0 ? Number(item.quantity) : 1,
        unitAmount: Number(amount.toFixed(2))
      });
    });

    const monthlyAddOnItems = Array.isArray(calculations.monthlyAddOnItems)
      ? calculations.monthlyAddOnItems
      : [];

    monthlyAddOnItems.forEach((item) => {
      const amount = Number(item?.amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      items.push({
        type: 'addon_monthly_fee',
        description: `${item.title} Add-on - Monthly Fee`,
        quantity: 1,
        unitAmount: Number(amount.toFixed(2))
      });
    });

    return items;
  }, [
    formSteps,
    formData.websitePlan,
    calculations.websitePlanMonthlyFee,
    calculations.discountApplied,
    calculations.discountCode,
    calculations.emailStorageUpgradeItems,
    calculations.monthlyAddOnItems
  ]);

  const createRecurringAnnualLineItems = useCallback(() => {
    const domains = extractSelectedDomains(formData.selectedDomains, formData.newDomainName_selected);
    const validFreeDomainSelection = ensureValidFreeDomainSelection(domains, freeDomainSelection);

    return domains
      .map((domain) => {
        const domainConfig = DOMAIN_EXTENSION_CONFIG[domain.extension] || {};

        // The free-credit domain renews free while the plan is active, so it gets no annual invoice line
        if (domainConfig.freeEligible && validFreeDomainSelection === domain.fullDomain) {
          return null;
        }

        const amount = Number(domainConfig.price || 0);

        if (!Number.isFinite(amount) || amount <= 0) {
          return null;
        }

        return {
          type: 'domain_annual_renewal',
          description: `Annual renewal - ${domain.fullDomain}`,
          quantity: 1,
          unitAmount: Number(amount.toFixed(2))
        };
      })
      .filter(Boolean);
  }, [formData.selectedDomains, formData.newDomainName_selected, freeDomainSelection]);

  const buildSubmissionPayload = useCallback((submissionId) => {
    const effectiveSubmissionId = submissionId || formData.submissionId || generateSubmissionId();
    const domains = extractSelectedDomains(formData.selectedDomains, formData.newDomainName_selected);
    const resolvedFreeDomainSelection = ensureValidFreeDomainSelection(domains, formData.freeDomainSelection);
    const domainFields = {};

    domains.forEach((domain, index) => {
      const num = index + 1;
      if (num <= 5) {
        domainFields[`domain${num}`] = domain.fullDomain;
        domainFields[`domain${num}Extension`] = domain.extension;
      }
    });

    domainFields.totalDomains = domains.length;
    domainFields.freeDomainSelection = resolvedFreeDomainSelection;
    domainFields.primaryDomain = primaryDomainSelection;
    domainFields.clientmanagedomain = (formData.domainOption === 'existing-domain' && formData.domainTransferOption === 'keep-domain') ? 'true' : 'false';

    const isPurchasingDomainWithUsText = domains.length > 0 ? 'true' : 'false';
    domainFields.isPurchasingDomainWithUs = isPurchasingDomainWithUsText;

    // Mailbox setup is deferred; keep the existing Make.com keys without requesting provisioning.
    const ubieWebmailSetupText = 'false';
    const ubieWebmailFields = {};
    const requestedEmails = [];

    for (let i = 1; i <= 6; i += 1) {
      const emailData = requestedEmails[i - 1] || {};
      const username = String(emailData.username || '').trim();
      const domain = String(emailData.domain || '').trim().toLowerCase();
      const fullAddress = username && domain ? `${username}@${domain}` : username;

      const requestedStorageMbRaw = emailData.storageMb ?? emailData.storageMB ?? emailData.storageOptionMb;
      const requestedStorageMb = String(requestedStorageMbRaw ?? '').replace(/\D/g, '');
      const storageMb = requestedStorageMb || (emailData.upgradeStorage ? '10000' : '2000');

      ubieWebmailFields[`ubiewebmail${i}`] = fullAddress;
      ubieWebmailFields[`ubiewebmail${i}storage`] = storageMb;
      ubieWebmailFields[`ubiewebmail${i}StorageUpgrade`] = emailData.upgradeStorage ? 'true' : 'false';
    }

    const pricingFields = {
      ...AD_CLICK_IDS,
      ubiewebmailsetup: ubieWebmailSetupText,
      ...ubieWebmailFields,
      monthlyPrice: calculations.monthlyPrice ?? 0,
      monthlyFee: calculations.monthlyPrice ?? 0,
      setupFee: calculations.setupFee ?? 0,
      setupFeeTotal: calculations.setupFee ?? 0,
      yearlyTotal: calculations.yearlyTotal ?? 0,
      domainAnnualCost: calculations.domainAnnualCost ?? 0,
      websitePlanMonthlyFee: calculations.websitePlanMonthlyFee ?? 0,
      websitePlanMonthlyFeeBeforeDiscount: calculations.websitePlanMonthlyFeeBeforeDiscount ?? calculations.websitePlanMonthlyFee ?? 0,
      discountCode: calculations.discountCode ?? '',
      discountPercent: calculations.discountPercent ?? 0,
      websitePlanDiscountAmount: calculations.websitePlanDiscountAmount ?? 0,
      discountApplied: calculations.discountApplied ? 'true' : 'false',
      websitePlanSetupFee: calculations.websitePlanSetupFee ?? 0,
      oneOffAddonsTotal: calculations.oneOffAddonsTotal ?? 0,
      emailStorageUpgradeTotalMonthly: calculations.emailStorageUpgradeTotalMonthly ?? 0,
      emailStorageUpgradeQuantity: calculations.emailStorageUpgradeQuantity ?? 0,
      emailStorageUpgradeItems: calculations.emailStorageUpgradeItems ?? [],
      selectedAddOnItems: calculations.selectedAddOnItems ?? [],
      // One line for CRM text fields, e.g. "Online Store (£299); Business Logo Design (£65)".
      addOnsSummary: (calculations.selectedAddOnItems ?? [])
        .map((item) => `${item.title} (£${item.amount}${item.billingType === 'monthly' ? '/month' : ''})`)
        .join('; '),
      monthlyAddOnItems: calculations.monthlyAddOnItems ?? [],
      monthlyAddOnsTotal: calculations.monthlyAddOnsTotal ?? 0,
      websitePlanDisplayName: PLAN_DISPLAY_NAMES[formData.websitePlan] || '',
      domainItems: calculations.domainItems ?? [],
      isPurchasingDomainWithUs: isPurchasingDomainWithUsText,
      domainFieldBreakdown: calculations.domainFieldBreakdown ?? {},
      addOnFieldBreakdown: calculations.addOnFieldBreakdown ?? {},
      ...(calculations.domainFieldBreakdown ?? {}),
      ...(calculations.addOnFieldBreakdown ?? {})
    };

    const invoiceLineItems = createInvoiceLineItems(domains, resolvedFreeDomainSelection);
    const xeroLineItems = createXeroLineItems(invoiceLineItems);
    const recurringMonthlyLineItems = createRecurringMonthlyLineItems();
    const xeroRecurringMonthlyLineItems = createXeroLineItems(recurringMonthlyLineItems);
    const recurringAnnualLineItems = createRecurringAnnualLineItems();
    const xeroRecurringAnnualLineItems = createXeroLineItems(recurringAnnualLineItems);

    return {
      ...stripRetiredRegistrationFields(formData),
      // Retain legacy field names, but collect pages and mailboxes in later onboarding.
      pageChoice: 'choose-later',
      customPages: [],
      pageContentUpgrades: {},
      emailProvider: '',
      currentEmailProvider: '',
      currentEmailProviderOther: '',
      currentEmailAddresses: [],
      newEmailAddresses: [],
      ...domainFields,
      ...pricingFields,
      websitePlanLabel: formSteps?.[0]?.fields?.[0]?.options?.find(option => option.value === formData.websitePlan)?.label || '',
      invoiceLineItems,
      xeroLineItems,
      xeroLineItemsJson: JSON.stringify(xeroLineItems),
      recurringMonthlyLineItems,
      xeroRecurringMonthlyLineItems,
      xeroRecurringMonthlyLineItemsJson: JSON.stringify(xeroRecurringMonthlyLineItems),
      recurringAnnualLineItems,
      xeroRecurringAnnualLineItems,
      xeroRecurringAnnualLineItemsJson: JSON.stringify(xeroRecurringAnnualLineItems),
      submissionId: effectiveSubmissionId,
      email: formData.email,
      firstName: (formData.firstName || '').trim(),
      lastName: (formData.lastName || '').trim(),
      businessName: formData.businessName || ''
    };
  }, [formData, formSteps, calculations, createInvoiceLineItems, createXeroLineItems, createRecurringMonthlyLineItems, createRecurringAnnualLineItems, primaryDomainSelection]);

  const saveSubmissionSnapshot = useCallback(async (submissionId) => {
    if (preSubmissionInFlightRef.current) {
      return { success: false, skipped: true, reason: 'already_submitting' };
    }

    preSubmissionInFlightRef.current = true;

    try {
      const submissionPayload = buildSubmissionPayload(submissionId);
      const response = await fetch('/.netlify/functions/save-submission', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionPayload)
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        const errorMessage = result.error || result.message || 'Unable to submit your registration details.';
        throw new Error(errorMessage);
      }

      return {
        success: true,
        submissionId: submissionPayload.submissionId,
        result
      };
    } catch (error) {
      console.warn('Initial Make.com submission failed:', error);
      setStatusMessage({
        type: 'error',
        text: 'We could not send your registration automatically right now. Please try again in a moment.',
        show: true
      });
      setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 12000);

      return {
        success: false,
        error: error.message
      };
    } finally {
      preSubmissionInFlightRef.current = false;
    }
  }, [buildSubmissionPayload]);

  const finalizeRegistration = useCallback(async () => {
    if (hasFinalizedRegistration) {
      return true;
    }

    if (isFinalizingRegistrationRef.current) {
      return false;
    }

    const effectiveSubmissionId = formData.submissionId;
    const effectiveEmail = formData.email;

    if (!effectiveSubmissionId || !effectiveEmail) {
      console.warn('Cannot finalise registration: submission ID or email missing.');
      return false;
    }

    isFinalizingRegistrationRef.current = true;
    const contractState = 'Completed';

    try {
      const submissionPayload = buildSubmissionPayload(effectiveSubmissionId);

      const response = await fetch('/.netlify/functions/finalize-registration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...submissionPayload,
          email: effectiveEmail,
          contractId: signatureData.contractId || null,
          contractState,
          signatureCompleted: true
        })
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        const errorMessage = result.error || result.message || 'Unable to finalise registration.';
        throw new Error(errorMessage);
      }

      setHasFinalizedRegistration(true);

      setStatusMessage({
        type: 'success',
        text: 'Registration submitted! We’ll confirm approval and arrange payment next.',
        show: true
      });
      setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 8000);
      clearRecoveryData();
      return true;
    } catch (error) {
      console.error('Failed to finalise registration:', error);
      setStatusMessage({
        type: 'error',
        text: 'We could not complete registration finalisation automatically. Our onboarding team has been alerted.',
        show: true
      });
      setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 12000);
      return false;
    } finally {
      isFinalizingRegistrationRef.current = false;
    }
    }, [formData, signatureData.contractId, hasFinalizedRegistration, buildSubmissionPayload]);

  const extrasStepIndex = formSteps.findIndex(step => step.id === 'enhance-website');

  useEffect(() => {
    if (!Array.isArray(formData.features) || !formData.websitePlan) return;
    const allowedFeatures = formData.features.filter((feature) => isAddonAllowedOnPlan(getIndustryAddon(feature), formData.websitePlan));
    if (allowedFeatures.length !== formData.features.length) {
      setFormData((prev) => ({ ...prev, features: allowedFeatures }));
    }
  }, [formData.websitePlan, formData.features]);

  useEffect(() => {
    if (extrasStepIndex !== -1 && currentStep !== extrasStepIndex && activeFeatureIndex !== 0) {
      setActiveFeatureIndex(0);
    }
  }, [currentStep, extrasStepIndex, activeFeatureIndex, formSteps]);

  useEffect(() => {
    if (extrasStepIndex === -1) return;
    if (currentStep !== extrasStepIndex) return;

    if (!shouldAutoScrollRef.current) {
      shouldAutoScrollRef.current = true;
      return;
    }

    const activeCard = featureRefs.current[activeFeatureIndex];
    if (activeCard) {
      activeCard.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    }

}, [activeFeatureIndex, currentStep, extrasStepIndex]);

  useEffect(() => {
    if (!Array.isArray(formData.selectedDomains) && selectedDomains.length > 0) {
      setFormData(prev => {
        const ensuredFree = isFreeDomainEntitled
          ? ensureValidFreeDomainSelection(
              selectedDomains,
              prev.freeDomainSelection || prev.newDomainName_selected
            )
          : '';

        return {
          ...prev,
          selectedDomains,
          freeDomainSelection: ensuredFree || '',
          newDomainName_selected: ensuredFree || ''
        };
      });
    }
  }, [selectedDomains, formData.selectedDomains, formData.freeDomainSelection, formData.newDomainName_selected, isFreeDomainEntitled]);

  useEffect(() => {
    if (!isFreeDomainEntitled && (formData.freeDomainSelection || formData.newDomainName_selected)) {
      setFormData(prev => ({
        ...prev,
        freeDomainSelection: '',
        newDomainName_selected: ''
      }));
    }
  }, [isFreeDomainEntitled, formData.freeDomainSelection, formData.newDomainName_selected]);

    

  useEffect(() => {
    let monthlyPrice = 0;
    let setupFee = 0;

    const websitePlan = formSteps[0].fields[0].options.find(opt => opt.value === formData.websitePlan);
    if (websitePlan) {
      monthlyPrice = websitePlan.monthlyPrice;
      setupFee = websitePlan.setupFee;
    }

    let monthlyAddons = 0;
    let setupAddons = 0;
    const selectedAddOnItems = [];
    const emailStorageUpgradeItems = [];

    const featurePricingMap = {
      'logo-design': { title: 'Business Logo Design', amount: 65, key: 'addonLogoDesign' },
      'logo-design-bespoke': { title: 'Bespoke Logo Design', amount: 499, key: 'addonLogoDesignBespoke' },
      'business-card': { title: 'Business Card Design (Single Sided)', amount: 85, key: 'addonBusinessCardSingle' },
      'business-card-print': { title: 'Business Card Design + Print 500 (Single Sided)', amount: 110, key: 'addonBusinessCardSinglePrint' },
      'business-card-double': { title: 'Business Card Design (Double Sided)', amount: 125, key: 'addonBusinessCardDouble' },
      'business-card-double-print': { title: 'Business Card Design + Print 500 (Double Sided)', amount: 160, key: 'addonBusinessCardDoublePrint' },
      'social-setup-package-1': { title: 'Social Media Setup + Branding - Package 1 (Facebook, LinkedIn, X/Twitter)', amount: 165, key: 'addonSocialSetupPackage1' },
      'social-setup-package-2': { title: 'Social Media Setup + Branding - Package 2 (Facebook, LinkedIn, X/Twitter, Instagram, YouTube)', amount: 195, key: 'addonSocialSetupPackage2' }
    };

    const addOnFieldBreakdown = Object.values(featurePricingMap).reduce((acc, meta) => {
      acc[`${meta.key}Selected`] = false;
      acc[`${meta.key}Title`] = meta.title;
      acc[`${meta.key}Cost`] = 0;
      return acc;
    }, {});
    addOnFieldBreakdown.addonContentWritingSelected = false;
    addOnFieldBreakdown.addonContentWritingTitle = 'Content writing upgrades';
    addOnFieldBreakdown.addonContentWritingCost = 0;
    addOnFieldBreakdown.addonContentWritingCount = 0;

    const monthlyAddOnItems = [];
    let monthlyAddOnsTotal = 0;
    INDUSTRY_ADDONS.forEach((addon) => {
      addOnFieldBreakdown[`${addon.key}Selected`] = false;
      addOnFieldBreakdown[`${addon.key}Title`] = addon.label;
      addOnFieldBreakdown[`${addon.key}Cost`] = 0;
      addOnFieldBreakdown[`${addon.key}BillingType`] = addon.billingType;
    });
    // Separate one-off amount for the Online Store, so Make.com never has to infer it from the monthly total.
    addOnFieldBreakdown.addonOnlineStoreOneOffAmount = 0;
    LOCAL_SEO_PLANS.forEach((seoPlan) => {
      addOnFieldBreakdown[`${seoPlan.key}Selected`] = false;
      addOnFieldBreakdown[`${seoPlan.key}Title`] = seoPlan.label;
      addOnFieldBreakdown[`${seoPlan.key}Cost`] = 0;
      addOnFieldBreakdown[`${seoPlan.key}BillingType`] = seoPlan.billingType;
    });
    // One set of Local SEO fields for CRM/Make.com routers, whichever plan is chosen.
    addOnFieldBreakdown.localSeoSelected = false;
    addOnFieldBreakdown.localSeoPlan = '';
    addOnFieldBreakdown.localSeoPlanName = '';
    addOnFieldBreakdown.localSeoMonthlyFee = 0;
    addOnFieldBreakdown.localSeoMinimumTermMonths = 0;

    if (formData.features && Array.isArray(formData.features)) {
      formData.features.forEach(featureValue => {
        const featureMeta = featurePricingMap[featureValue];
        if (featureMeta) {
          setupAddons += featureMeta.amount;
          selectedAddOnItems.push({
            id: featureValue,
            title: featureMeta.title,
            amount: Number(featureMeta.amount.toFixed(2)),
            billingType: 'one_off'
          });
          addOnFieldBreakdown[`${featureMeta.key}Selected`] = true;
          addOnFieldBreakdown[`${featureMeta.key}Cost`] = Number(featureMeta.amount.toFixed(2));
          return;
        }

        const industryAddon = getPricedAddon(featureValue);
        if (!industryAddon || !isAddonAllowedOnPlan(industryAddon, formData.websitePlan)) {
          return;
        }

        if (industryAddon.billingType === 'monthly') {
          monthlyAddons += industryAddon.amount;
          monthlyAddOnsTotal += industryAddon.amount;
          monthlyAddOnItems.push({
            id: industryAddon.value,
            title: industryAddon.label,
            amount: Number(industryAddon.amount.toFixed(2)),
            billingType: 'monthly'
          });
        } else {
          setupAddons += industryAddon.amount;
        }

        selectedAddOnItems.push({
          id: industryAddon.value,
          title: industryAddon.label,
          amount: Number(industryAddon.amount.toFixed(2)),
          billingType: industryAddon.billingType
        });
        addOnFieldBreakdown[`${industryAddon.key}Selected`] = true;
        addOnFieldBreakdown[`${industryAddon.key}Cost`] = Number(industryAddon.amount.toFixed(2));
        if (industryAddon.value === 'onlinestore') {
          addOnFieldBreakdown.addonOnlineStoreOneOffAmount = Number(industryAddon.amount.toFixed(2));
        }
        if (getLocalSeoPlan(featureValue)) {
          addOnFieldBreakdown.localSeoSelected = true;
          addOnFieldBreakdown.localSeoPlan = industryAddon.plan;
          addOnFieldBreakdown.localSeoPlanName = industryAddon.label;
          addOnFieldBreakdown.localSeoMonthlyFee = Number(industryAddon.amount.toFixed(2));
          addOnFieldBreakdown.localSeoMinimumTermMonths = industryAddon.minimumTermMonths;
        }
      });
    }

    const selectedDomains = extractSelectedDomains(formData.selectedDomains, formData.newDomainName_selected);
    const validFreeDomainSelection = ensureValidFreeDomainSelection(selectedDomains, formData.freeDomainSelection);
    const domainItems = selectedDomains.map((domain) => {
      const domainConfig = DOMAIN_EXTENSION_CONFIG[domain.extension] || {};
      const isFree = domainConfig.freeEligible && validFreeDomainSelection === domain.fullDomain;
      const normalizedDomainTitle = String(domain.fullDomain || '').toLowerCase().trim();
      const safeDomainTitle = DOMAIN_FORMAT_REGEX.test(normalizedDomainTitle) ? normalizedDomainTitle : '';

      return {
        domain: domain.fullDomain,
        extension: domain.extension,
        title: safeDomainTitle,
        amount: Number((isFree ? 0 : (domainConfig.price || 0)).toFixed(2)),
        quantity: 1,
        isFree
      };
    });

    const domainFieldBreakdown = {
      totalDomainItemQuantity: domainItems.length
    };

    for (let i = 1; i <= 5; i += 1) {
      domainFieldBreakdown[`domain${i}Title`] = '';
      domainFieldBreakdown[`domain${i}Cost`] = 0;
      domainFieldBreakdown[`domain${i}Quantity`] = 0;
    }

    domainItems.slice(0, 5).forEach((item, index) => {
      const domainIndex = index + 1;
      domainFieldBreakdown[`domain${domainIndex}Title`] = item.title;
      domainFieldBreakdown[`domain${domainIndex}Cost`] = Number((item.amount || 0).toFixed(2));
      domainFieldBreakdown[`domain${domainIndex}Quantity`] = 1;
    });

    const enteredDiscountCode = String(formData.discountCode || '').trim().toUpperCase();
    const promo = getPromoStatus();
    const isDiscountCodeValid = enteredDiscountCode === promo.code && !promo.isExpired;
    const websitePlanDiscountAmount = isDiscountCodeValid
      ? Number(((monthlyPrice * promo.discountPercent) / 100).toFixed(2))
      : 0;
    const discountedWebsitePlanMonthlyFee = Number((monthlyPrice - websitePlanDiscountAmount).toFixed(2));

    const finalMonthlyPrice = discountedWebsitePlanMonthlyFee + monthlyAddons;
    const finalSetupFee = setupFee + setupAddons;
    const finalYearlyTotal = finalMonthlyPrice * 12 + finalSetupFee + domainAnnualCost;

    setCalculations({
      monthlyPrice: finalMonthlyPrice,
      setupFee: finalSetupFee,
      yearlyTotal: finalYearlyTotal,
      domainAnnualCost,
      websitePlanMonthlyFee: discountedWebsitePlanMonthlyFee,
      websitePlanMonthlyFeeBeforeDiscount: Number(monthlyPrice.toFixed(2)),
      websitePlanSetupFee: Number(setupFee.toFixed(2)),
      discountCode: isDiscountCodeValid ? promo.code : '',
      discountPercent: isDiscountCodeValid ? promo.discountPercent : 0,
      websitePlanDiscountAmount,
      discountApplied: isDiscountCodeValid,
      oneOffAddonsTotal: Number(setupAddons.toFixed(2)),
      emailStorageUpgradeTotalMonthly: Number((monthlyAddons - monthlyAddOnsTotal).toFixed(2)),
      monthlyAddOnsTotal: Number(monthlyAddOnsTotal.toFixed(2)),
      monthlyAddOnItems,
      emailStorageUpgradeQuantity: emailStorageUpgradeItems.length,
      domainItems,
      domainFieldBreakdown,
      emailStorageUpgradeItems,
      selectedAddOnItems,
      addOnFieldBreakdown
    });
  }, [formData, formSteps, domainAnnualCost]);

  // Secure domain availability checker using Netlify function
  const checkDomainAvailability = useCallback(
    async (domainName, extension) => {
      if (!domainName) {
        return false;
      }

      const fullDomain = `${domainName}${extension}`;

      try {
        const response = await fetch('/.netlify/functions/check-domain', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            domain: fullDomain
          })
        });

        if (!response.ok) {
          throw new Error(`Domain check failed: ${response.status}`);
        }

        const data = await response.json();
        const available = data.available === true;

        setDomainAvailability(prev => ({
          ...prev,
          [fullDomain]: available
        }));

        setDomainCheckAttempts(prev => {
          if (prev[fullDomain]) {
            const { [fullDomain]: _removed, ...rest } = prev;
            return rest;
          }
          return prev;
        });

        return available;
      } catch (error) {
        console.error('Domain check error:', error);

        setDomainAvailability(prev => ({
          ...prev,
          [fullDomain]: 'error'
        }));

        setDomainCheckAttempts(prev => ({
          ...prev,
          [fullDomain]: Math.min(MAX_DOMAIN_RETRIES, (prev[fullDomain] || 0) + 1)
        }));

        return 'error';
      }
    },
    []
  );

  const performDomainAvailabilityCheck = useCallback(
    async (domainName, extension) => {
      const fullDomain = `${domainName}${extension}`;
      setActiveDomainChecks((prev) => ({ ...prev, [fullDomain]: true }));

      try {
        await checkDomainAvailability(domainName, extension);
      } finally {
        setActiveDomainChecks((prev) => {
          const { [fullDomain]: _removed, ...rest } = prev;
          return rest;
        });
      }
    },
    [checkDomainAvailability]
  );

  const checkAllDomainExtensions = useCallback(
    async (domainName, extensions) => {
      if (!domainName || domainName.length < 3 || isCheckAllRunning) {
        return;
      }

      const orderedExtensions = [...extensions]
        .filter((extension) => AVAILABLE_DOMAIN_EXTENSIONS.includes(extension))
        .sort((a, b) => DOMAIN_EXTENSION_ORDER.indexOf(a) - DOMAIN_EXTENSION_ORDER.indexOf(b));

      const pendingExtensions = orderedExtensions.filter((extension) => {
        const fullDomain = `${domainName}${extension}`;
        const previousAttempts = domainCheckAttempts[fullDomain] || 0;
        const hasMaxedRetries =
          domainAvailability[fullDomain] === 'error' && previousAttempts >= MAX_DOMAIN_RETRIES;
        return !hasMaxedRetries;
      });

      if (!pendingExtensions.length) {
        return;
      }

      setIsCheckAllRunning(true);
      checkAllCancellationRef.current = false;

      if (!hasTriggeredInitialDomainCheck) {
        setHasTriggeredInitialDomainCheck(true);
      }

      try {
        for (let i = 0; i < pendingExtensions.length; i += MAX_PARALLEL_DOMAIN_CHECKS) {
          if (checkAllCancellationRef.current) {
            break;
          }

          const batch = pendingExtensions.slice(i, i + MAX_PARALLEL_DOMAIN_CHECKS);
          setCheckAllCurrentExtension(batch[0] || null);

          await Promise.allSettled(
            batch.map((extension) => performDomainAvailabilityCheck(domainName, extension))
          );
        }
      } finally {
        setIsCheckAllRunning(false);
        setCheckAllCurrentExtension(null);
        setDomainCountdowns((prev) => {
          const { __all__, ...rest } = prev;
          return rest;
        });
        cancelCountdown('__all__');
        checkAllCancellationRef.current = false;
      }
    },
    [
      cancelCountdown,
      domainAvailability,
      domainCheckAttempts,
      hasTriggeredInitialDomainCheck,
      isCheckAllRunning,
      performDomainAvailabilityCheck,
    ]
  );

  const handleIndividualDomainCheck = useCallback(
    async (domainName, extension) => {
      if (!domainName || domainName.length < 3 || isCheckAllRunning) {
        return;
      }

      const fullDomain = `${domainName}${extension}`;

      const previousAttempts = domainCheckAttempts[fullDomain] || 0;
      const hasMaxedRetries =
        domainAvailability[fullDomain] === 'error' && previousAttempts >= MAX_DOMAIN_RETRIES;

      if (hasMaxedRetries || activeDomainChecks[fullDomain]) {
        return;
      }

      if (!hasTriggeredInitialDomainCheck) {
        setHasTriggeredInitialDomainCheck(true);
      }

      await performDomainAvailabilityCheck(domainName, extension);
    },
    [
      activeDomainChecks,
      domainAvailability,
      domainCheckAttempts,
      hasTriggeredInitialDomainCheck,
      isCheckAllRunning,
      performDomainAvailabilityCheck,
    ]
  );

  const runAutoCheckForExtensions = useCallback(
    async (domainName, extensions = []) => {
      const normalizedDomainName = String(domainName || '').trim().toLowerCase();
      if (!normalizedDomainName || normalizedDomainName.length < 3) {
        return;
      }

      const orderedExtensions = Array.from(new Set(extensions || []))
        .filter((extension) => AVAILABLE_DOMAIN_EXTENSIONS.includes(extension))
        .sort((a, b) => DOMAIN_EXTENSION_ORDER.indexOf(a) - DOMAIN_EXTENSION_ORDER.indexOf(b));

      const pendingExtensions = orderedExtensions.filter((extension) => {
        const fullDomain = `${normalizedDomainName}${extension}`;
        const hasExistingResult = Object.prototype.hasOwnProperty.call(domainAvailability, fullDomain);
        return !hasExistingResult && !activeDomainChecks[fullDomain];
      });

      if (!pendingExtensions.length) {
        return;
      }

      const initialBatch = pendingExtensions.slice(0, MAX_PARALLEL_DOMAIN_CHECKS);
      await Promise.allSettled(
        initialBatch.map((extension) => handleIndividualDomainCheck(normalizedDomainName, extension))
      );
    },
    [activeDomainChecks, domainAvailability, handleIndividualDomainCheck]
  );

  useEffect(() => {
    if (!idleDomainSearch || idleDomainSearch.length < 3 || showAllDomainExtensions) {
      return;
    }

    runAutoCheckForExtensions(idleDomainSearch, DOMAIN_PRIMARY_VISIBLE_EXTENSIONS);
  }, [idleDomainSearch, runAutoCheckForExtensions, showAllDomainExtensions]);

  const handleCancelCheckAllCountdown = useCallback(() => {
    checkAllCancellationRef.current = true;
    cancelCountdown('__all__');
  }, [cancelCountdown]);

  const handleCancelIndividualCountdown = useCallback(
    (fullDomain) => {
      if (!fullDomain) {
        return;
      }
      cancelCountdown(fullDomain);
    },
    [cancelCountdown]
  );

const createSignatureDocument = async () => {
  if (signatureStep === 'creating') {
    console.log('Already creating signature document, ignoring duplicate click');
    return;
  }
  setSignatureStep('creating');
  setHasFinalizedRegistration(false);
  isFinalizingRegistrationRef.current = false;
  autoFinalizeTriggeredRef.current = false;

  const submissionId = formData.submissionId || generateSubmissionId();

  try {
    // Update formData with submissionId for downstream steps
    setFormData(prev => ({ ...prev, submissionId }));

    // Create the signature document
    const response = await fetch('/.netlify/functions/create-esignature', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customerName: `${formData.firstName} ${formData.lastName}`,
        customerEmail: formData.email,
        businessName: formData.businessName,
        websitePlan: formData.websitePlan,
        monthlyPrice: calculations.monthlyPrice,
        setupFee: calculations.setupFee,
        submissionId,
        documentTitle: `Website Plan Agreement - ${formData.businessName || formData.firstName + ' ' + formData.lastName}`,
        submissionData: formData
      })
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to create signature document');
    }

    setSignatureData({
      contractId: result.contractId,
      signingUrl: result.signingUrl,
      error: null,
      isCompleted: false,
      latestStatus: null,
      statusDetails: null,
      pendingSigners: [],
      lastCheckedAt: null
    });
    setHasCompletedSignature(false);

    setFormData(prev => ({
      ...prev,
      submissionId
    }));

    // Now open the modal
    setIsSignatureModalOpen(true);
    setSignatureStep('signing');

  } catch (error) {
    console.error('Failed to create signature document:', error);
    setSignatureData({
      contractId: null,
      signingUrl: null,
      error: error.message,
      isCompleted: false,
      latestStatus: null,
      statusDetails: null,
      pendingSigners: [],
      lastCheckedAt: null
    });
    setHasCompletedSignature(false);
    setIsSignatureModalOpen(false);
    setSignatureStep('error');
  }
};

const checkSignatureStatus = useCallback(
  async (options = {}) => {
    const { silentOnPending = false, isAutoCheck = false, showPendingHint = false } = options;

    // Rate limiting for manual checks (20 seconds cooldown)
    if (!isAutoCheck) {
      const now = Date.now();
      const timeSinceLastCheck = lastManualCheckTimeRef.current ? now - lastManualCheckTimeRef.current : Infinity;

      if (timeSinceLastCheck < 20000) {
        const remainingSeconds = Math.ceil((20000 - timeSinceLastCheck) / 1000);
        setStatusMessage({
          type: 'info',
          text: `Please wait ${remainingSeconds} seconds, then try again.`,
          show: true
        });
        setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 3000);
        return;
      }

      // Record manual check time and start cooldown
      lastManualCheckTimeRef.current = now;
      setManualCheckCooldown(20);

      // Start countdown
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
      cooldownIntervalRef.current = setInterval(() => {
        setManualCheckCooldown(prev => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    if (!signatureData.contractId) {
      if (!isAutoCheck) {
        setStatusMessage({
          type: 'error',
          text: 'We could not find your agreement. Please reopen the signing window and try again.',
          show: true
        });
        setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 5000);
      }
      return;
    }

    try {
      console.log('Checking signature status for contract:', signatureData.contractId);

      // Check eSignatures.io API directly with adaptive polling
      const response = await fetch('/.netlify/functions/check-esignature-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractId: signatureData.contractId
        })
      });

      const result = await response.json();
      console.log('Signature status check result:', result);

      if (!response.ok || !result.success) {
        let errorText = `We could not confirm your signature just yet: ${result.error || 'please try again in a moment'}`;

        setSignatureData(prev => ({
          ...prev,
          error: errorText,
          lastCheckedAt: new Date().toISOString()
        }));

        setStatusMessage({
          type: 'error',
          text: errorText,
          show: true
        });
        setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 8000);
        return;
      }

      const isCompletedResponse = result?.isCompleted === true || result?.signatureCompleted === true;
      const normalizedStatus = result?.signatureStatus || result?.signatureStatusNormalized || null;
      const statusDetails = result && typeof result.statusDetails === 'object' ? result.statusDetails : null;
      const pendingSigners = Array.isArray(result?.pendingSigners) ? result.pendingSigners : [];
      const lastCheckedAt = new Date().toISOString();

      if (isCompletedResponse) {
        setSignatureData(prev => ({
          ...prev,
          isCompleted: true,
          error: null,
          latestStatus: normalizedStatus || prev.latestStatus || null,
          statusDetails: statusDetails || prev.statusDetails || null,
          pendingSigners: [],
          lastCheckedAt
        }));
        setSignatureStep('completed');
        setIsSignatureModalOpen(false);
        setHasCompletedSignature(true);
        setStatusMessage({
          type: 'success',
          text: normalizedStatus
            ? `Your agreement has been signed successfully (${normalizedStatus}).`
            : 'Your agreement has been signed successfully.',
          show: true
        });
        if (signatureCheckIntervalRef.current) {
          clearTimeout(signatureCheckIntervalRef.current);
          signatureCheckIntervalRef.current = null;
        }
        pollingAttemptsRef.current = 0; // Reset polling on completion
        setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 5000);
      } else {
        setSignatureData(prev => ({
          ...prev,
          isCompleted: false,
          error: null,
          latestStatus: normalizedStatus || prev.latestStatus || null,
          statusDetails: statusDetails || prev.statusDetails || null,
          pendingSigners,
          lastCheckedAt
        }));

        const infoText = pendingSigners.length > 0
          ? 'We have not received your signature yet. Please finish signing the agreement, then try again.'
          : 'We have not received your signature yet. Please complete the agreement, then try again.';

        if (!silentOnPending) {
          setStatusMessage({
            type: 'info',
            text: infoText,
            show: true
          });
          setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 5000);
        } else if (showPendingHint) {
          setStatusMessage({
            type: 'info',
            text: 'We have not seen your signature come through just yet. If you have just signed, please wait a few seconds and try again.',
            show: true
          });
          setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 4500);
        }
      }
    } catch (error) {
      console.error('Failed to check signature status:', error);
      setSignatureData(prev => ({
        ...prev,
        error: error.message,
        lastCheckedAt: new Date().toISOString()
      }));
      setStatusMessage({
        type: 'error',
        text: 'We could not check your signature right now. Please try again in a moment or contact us if the issue continues.',
        show: true
      });
      setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 5000);
    }
  },
  [signatureData.contractId]
);

  const closeSignatureModal = useCallback(() => {
    setIsSignatureModalOpen(false);

    if (signatureStep === 'signing') {
      checkSignatureStatus({ silentOnPending: true, isAutoCheck: true, showPendingHint: true });
    }
  }, [signatureStep, checkSignatureStatus]);

  const openSignatureModal = useCallback(() => {
    if (signatureData.signingUrl) {
      setIsSignatureModalOpen(true);
      console.log('Signature modal opened - waiting for postMessage completion event');
    }
  }, [signatureData.signingUrl]);

  // Adaptive polling for eSignatures.io API with exponential backoff
  // Schedule: 30s wait, then 40s×4, 1m×5, 5m×10, 10m×10, 30m×10, 60m×10
  useEffect(() => {
    if (signatureCheckIntervalRef.current) {
      clearTimeout(signatureCheckIntervalRef.current);
      signatureCheckIntervalRef.current = null;
    }

    if (signatureStep === 'signing' && signatureData.contractId) {
      pollingAttemptsRef.current = 0;
      pollingStartTimeRef.current = Date.now();

      const scheduleNextPoll = () => {
        const attemptNumber = pollingAttemptsRef.current;

        // Calculate delay based on attempt number
        let delay;
        let phase;

        if (attemptNumber === 0) {
          // First poll: wait 30 seconds
          delay = 30000;
          phase = 'initial wait';
        } else if (attemptNumber <= 4) {
          // Attempts 1-4: every 40 seconds
          delay = 40000;
          phase = `fast polling (${attemptNumber}/4)`;
        } else if (attemptNumber <= 9) {
          // Attempts 5-9: every 1 minute
          delay = 60000;
          phase = `medium polling (${attemptNumber - 4}/5)`;
        } else if (attemptNumber <= 19) {
          // Attempts 10-19: every 5 minutes
          delay = 300000;
          phase = `slow polling (${attemptNumber - 9}/10)`;
        } else if (attemptNumber <= 29) {
          // Attempts 20-29: every 10 minutes
          delay = 600000;
          phase = `slower polling (${attemptNumber - 19}/10)`;
        } else if (attemptNumber <= 39) {
          // Attempts 30-39: every 30 minutes
          delay = 1800000;
          phase = `very slow polling (${attemptNumber - 29}/10)`;
        } else if (attemptNumber <= 49) {
          // Attempts 40-49: every 60 minutes
          delay = 3600000;
          phase = `hourly polling (${attemptNumber - 39}/10)`;
        } else {
          // Stop polling after 49 attempts
          console.log('Signature polling stopped after maximum attempts');
          return;
        }

        console.log(`Scheduling signature check in ${delay/1000}s - ${phase}`);

        signatureCheckIntervalRef.current = setTimeout(async () => {
          pollingAttemptsRef.current++;
          await checkSignatureStatus({ silentOnPending: true, isAutoCheck: true });

          // Schedule next poll
          scheduleNextPoll();
        }, delay);
      };

      // Start the polling schedule
      scheduleNextPoll();
    }

    return () => {
      if (signatureCheckIntervalRef.current) {
        clearTimeout(signatureCheckIntervalRef.current);
        signatureCheckIntervalRef.current = null;
      }
      pollingAttemptsRef.current = 0;
      pollingStartTimeRef.current = null;
    };
  }, [signatureStep, signatureData.contractId, checkSignatureStatus]);

  useEffect(() => {
    if (signatureStep !== 'signing' || !signatureData.contractId) {
      return undefined;
    }

    const allowedHostSuffixes = ['esignatures.io', 'esignatures.com'];
    const contractIdLower = String(signatureData.contractId).toLowerCase();

    const handleSignerMessage = (event) => {
      if (signatureStep !== 'signing' || !signatureData.contractId) {
        return;
      }

      if (event?.origin && event.origin !== 'null') {
        let originHost = '';
        try {
          originHost = new URL(event.origin).host;
        } catch (error) {
          originHost = '';
        }

        if (
          !originHost ||
          !allowedHostSuffixes.some((suffix) => originHost.endsWith(suffix))
        ) {
          return;
        }
      }

      const rawData = event?.data;
      let serializedData = '';

      if (typeof rawData === 'string') {
        serializedData = rawData;
      } else if (rawData != null) {
        try {
          serializedData = JSON.stringify(rawData);
        } catch (error) {
          serializedData = String(rawData);
        }
      }

      if (!serializedData) {
        return;
      }

      const lowerCased = serializedData.toLowerCase();
      const mentionsCompletion = lowerCased.includes('complete') || lowerCased.includes('signed') || lowerCased.includes('finish');
      const mentionsContract = lowerCased.includes(contractIdLower);

      if (!mentionsCompletion && !mentionsContract) {
        return;
      }

      console.log('Signature completion message received from iframe – verifying status via API');

      if (messageTriggeredCheckTimeoutRef.current) {
        clearTimeout(messageTriggeredCheckTimeoutRef.current);
      }

      messageTriggeredCheckTimeoutRef.current = setTimeout(() => {
        checkSignatureStatus({ silentOnPending: true, isAutoCheck: true })
          .finally(() => {
            messageTriggeredCheckTimeoutRef.current = null;
          });
      }, 500);
    };

    window.addEventListener('message', handleSignerMessage);

    return () => {
      window.removeEventListener('message', handleSignerMessage);
    };
  }, [signatureStep, signatureData.contractId, checkSignatureStatus]);

  useEffect(() => {
    return () => {
      if (signatureCheckIntervalRef.current) {
        clearInterval(signatureCheckIntervalRef.current);
        signatureCheckIntervalRef.current = null;
      }
      if (messageTriggeredCheckTimeoutRef.current) {
        clearTimeout(messageTriggeredCheckTimeoutRef.current);
        messageTriggeredCheckTimeoutRef.current = null;
      }
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    };
  }, []);

  // DISABLED: Window focus/visibility polling removed - not needed with postMessage detection
  // useEffect(() => {
  //   if (signatureStep !== 'signing' || !signatureData.contractId) {
  //     return undefined;
  //   }
  //
  //   if (typeof window === 'undefined' || typeof document === 'undefined') {
  //     return undefined;
  //   }
  //
  //   console.log('Signature completion will be detected via iframe postMessage events');
  //
  //   return undefined;
  // }, [signatureStep, signatureData.contractId]);

  useEffect(() => {
    if (signatureStep !== 'signing' && messageTriggeredCheckTimeoutRef.current) {
      clearTimeout(messageTriggeredCheckTimeoutRef.current);
      messageTriggeredCheckTimeoutRef.current = null;
    }
  }, [signatureStep]);

  useEffect(() => {
    if (signatureStep === 'completed') {
      setIsSignatureModalOpen(false);
    }
  }, [signatureStep]);

  useEffect(() => {
    if (signatureStep !== 'completed') {
      autoFinalizeTriggeredRef.current = false;
      setIsAutoSubmittingAfterSignature(false);
      return;
    }

    if (hasFinalizedRegistration || autoFinalizeTriggeredRef.current) {
      return;
    }

    if (!formSteps[currentStep]?.isSignature) {
      return;
    }

    autoFinalizeTriggeredRef.current = true;

    const autoFinalizeAfterSignature = async () => {
      setIsAutoSubmittingAfterSignature(true);
      const finalized = await finalizeRegistration();

      if (!finalized) {
        autoFinalizeTriggeredRef.current = false;
        setIsAutoSubmittingAfterSignature(false);
        return;
      }

      handleStepChange(currentStep + 1);
    };

    autoFinalizeAfterSignature();
  }, [
    signatureStep,
    hasFinalizedRegistration,
    formSteps,
    currentStep,
    finalizeRegistration,
    handleStepChange
  ]);


  const validateField = (field, value) => {
    if (field.type === 'domain-checker') {
      const wantsDomainSearch =
        formData.domainOption === 'free-domain' ||
        (formData.domainOption === 'existing-domain' &&
          formData.domainTransferOption &&
          formData.existingDomainAdditionalChoice === 'add');

      if (!wantsDomainSearch) {
        return null;
      }

      if (!value || value.trim().length < 3) {
        return 'Enter at least 3 characters (for example: yourbrand) to search web addresses.';
      }

      const selectedDomainEntries = extractSelectedDomains(
        formData.selectedDomains,
        formData.newDomainName_selected
      );

      if (selectedDomainEntries.length === 0) {
        return 'Add at least one available web address to continue.';
      }

      return null;
    }

    if (field.required && field.type === 'checkbox-single') {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return 'Tick this option only if it applies to you.';
      }
    } else if (field.required && field.type === 'radio') {
      if (!value || value.trim() === '') {
        if (field.id === 'domainOption') {
          return 'Please choose how you\'d like to set up your web address';
        }

        if (field.id === 'domainTransferOption') {
          if (formData.domainOption !== 'existing-domain') {
            return null;
          }
          return 'Choose whether to transfer your existing web address or keep it with your current provider.';
        }

        if (field.id === 'existingDomainAdditionalChoice') {
          if (
            formData.domainOption !== 'existing-domain' ||
            !formData.domainTransferOption
          ) {
            return null;
          }

          return 'Choose whether you also want to search for extra web addresses.';
        }

        return 'Please choose one option to continue.';
      }
  } else if (field.required && (!value || value.trim() === '')) {
    if (field.id === 'businessName') {
      return 'Enter your business or trading name.';
    } else if (field.id === 'firstName') {
      return 'Enter your first name.';
    } else if (field.id === 'lastName') {
      return 'Enter your surname.';
    } else if (field.id === 'email') {
      return 'Enter your email address.';
    } else if (field.id === 'confirmEmail') {
      return 'Re-enter your email address to confirm it.';
    } else if (field.id === 'phone') {
      return 'Enter your UK mobile number starting with 07.';
    }
    return `Enter ${field.label.toLowerCase()} to continue.`;
  }
  
  if ((field.id === 'firstName' || field.id === 'lastName') && value && String(value).trim()) {
    const nameValue = String(value).trim();

    if (nameValue.length > NAME_INPUT_MAX_LENGTH) {
      return `Keep this within ${NAME_INPUT_MAX_LENGTH} characters.`;
    }

    if (!NAME_ALLOWED_REGEX.test(nameValue)) {
      return field.id === 'firstName'
        ? 'Use letters only, plus apostrophes or hyphens (no numbers/symbols).'
        : 'Use letters only, plus apostrophes or hyphens (no numbers/symbols).';
    }
  }

  // Email format validation for single email fields - MUST come before other validations
  if ((field.type === 'email' || field.id === 'email' || field.id === 'confirmEmail') && value && value.trim()) {
    if (!isValidEmail(value)) {
      if (field.id === 'confirmEmail') {
        return 'Enter a valid email address to match your main email.';
      }
      return 'Enter a valid email address (for example: name@example.co.uk).';
    }
  }

  if (field.id === 'phone' && value) {
    const normalizedPhone = normalizeUkMobileInput(value, { enforceLocal07: true });

    if (!UK_MOBILE_NUMBER_REGEX.test(normalizedPhone)) {
      return 'Use a UK mobile format starting with 07.';
    }
  }

  if (field.id === 'businessPhone' && value) {
    const businessPhoneError = getUkPhoneValidationError(value);
    if (businessPhoneError) {
      return businessPhoneError;
    }
  }

    if (formData.separateBusinessAddress && formData.separateBusinessAddress.includes('separate')) {
     if (['businessAddressLine1', 'businessCity', 'businessPostcode'].includes(field.id) && (!value || value.trim() === '')) {
        if (field.id === 'businessAddressLine1') {
          return 'Enter your business street address.';
        } else if (field.id === 'businessCity') {
          return 'Enter your business town or city.';
        } else if (field.id === 'businessPostcode') {
          return 'Enter your business postcode.';
        }
        return `Enter ${field.label.toLowerCase()} to continue.`;
      }
    }
    
    if (formData.separateBusinessPhone && formData.separateBusinessPhone.includes('separate')) {
      if (field.id === 'businessPhone' && (!value || value.trim() === '')) {
        return 'Please enter the phone number you\'d like displayed on your website';
      }
    }

    
    if (field.id === 'confirmEmail' && normalizeEmailInput(value) !== normalizeEmailInput(formData.email)) {
      return 'The email addresses don\'t match. Please check and try again';
    }
    
    if (field.id === 'invoicePostcode' && value) {
      const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i;
      if (!postcodeRegex.test(value.trim())) {
        return 'Enter a valid UK postcode (for example: SW1A 1AA).';
      }
    }

    if (field.id === 'businessPostcode' && value) {
      const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i;
      if (!postcodeRegex.test(value.trim())) {
        return 'Enter a valid UK business postcode (for example: SW1A 1AA).';
      }
    }
    
    if (field.id === 'dateOfBirth' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 18) {
        return 'You must be at least 18 years old to continue.';
      }
    }

    if (field.id === 'charityNumber' && formData.businessType === 'charity') {
      if (!value || !String(value).trim()) {
        return 'Enter your charity number to continue.';
      }
    }
    
    if (field.id === 'existingDomainFull') {
      if (!value || !value.trim()) {
        return 'Enter your web address (for example: mydomainhere.com).';
      }

      const existingDomainError = getExistingDomainValidationError(value);
      if (existingDomainError) {
        return existingDomainError;
      }
    }
    
    return null;
  };

  const handleInputChange = (fieldId, value) => {
    const processedValue =
      fieldId === 'phone'
        ? normalizeUkMobileInput(value, { enforceLocal07: true })
        : fieldId === 'businessPhone'
        ? normalizeUkMobileInput(value)
        : fieldId === 'email' || fieldId === 'confirmEmail'
        ? normalizeEmailInput(value)
        : fieldId === 'firstName' || fieldId === 'lastName'
        ? normalizeNameInput(value)
        : value;

    setFormData(prev => {
      const updated = {
        ...prev,
        [fieldId]: processedValue
      };

      if (fieldId === 'domainOption') {
        if (processedValue === 'free-domain') {
          updated.domainTransferOption = '';
          updated.existingDomainAdditionalChoice = '';
          updated.existingDomainFull = '';
          updated.existingDomainName = '';
          updated.existingDomainExtension = '';
        } else if (processedValue === 'existing-domain') {
          updated.newDomainName = '';
          updated.selectedDomains = [];
          updated.freeDomainSelection = '';
          updated.newDomainName_selected = '';
        }
      }

      if (fieldId === 'domainTransferOption') {
        if (processedValue === 'transfer-domain') {
          updated.selectedDomains = [];
          updated.freeDomainSelection = '';
          updated.newDomainName_selected = '';
        }

        updated.existingDomainAdditionalChoice = '';
      }

      if (fieldId === 'existingDomainAdditionalChoice' && processedValue !== 'add') {
        updated.newDomainName = '';
        updated.selectedDomains = [];
        updated.freeDomainSelection = '';
        updated.newDomainName_selected = '';
      }

      // Auto-parse existing domain when full domain is entered
      if (fieldId === 'existingDomainFull' && processedValue) {
        const domain = processedValue.toLowerCase().trim();
        const extensions = ['.co.uk', '.org.uk', '.me.uk', '.ltd.uk', '.plc.uk', '.com', '.uk', '.org', '.net', '.eu', '.info', '.biz', '.name', '.mobi', '.tv', '.cc', '.ws', '.io', '.co', '.london', '.online', '.design'];

        let foundExtension = '';
        let domainName = '';

        for (const ext of extensions) {
          if (domain.endsWith(ext)) {
            foundExtension = ext;
            domainName = domain.slice(0, -ext.length);
            break;
          }
        }

        if (foundExtension && domainName) {
          updated.existingDomainName = domainName;
          updated.existingDomainExtension = foundExtension;
        }
      }

      if (fieldId === 'newDomainName') {
        updated.newDomainName_selected = '';
      }

      if (fieldId === 'businessType') {
        updated.companyNumber = '';
        updated.charityNumber = '';
      }

      return updated;
    });

    if (
      fieldId === 'domainOption' ||
      fieldId === 'domainTransferOption' ||
      fieldId === 'newDomainName' ||
      (fieldId === 'existingDomainAdditionalChoice' && processedValue !== 'add')
    ) {
      setDomainAvailability({});
      setActiveDomainChecks({});
      setDomainCountdowns({});
      setDomainCheckAttempts({});
    }

  // Auto-scroll for radio buttons that reveal content
  if (fieldId === 'phone') {
    setValidationErrors(prev => {
      const phoneError = getUkMobileValidationError(processedValue);
      const currentError = prev.phone;
      if (!currentError) return prev;

      if (phoneError) {
        if (currentError === phoneError) {
          return prev;
        }

        return {
          ...prev,
          phone: phoneError
        };
      }

      if (!currentError) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors.phone;
      return nextErrors;
    });
  } else if (fieldId === 'existingDomainFull') {
    setValidationErrors(prev => {
      const domainError = getExistingDomainValidationError(processedValue);
      const currentError = prev.existingDomainFull;
      if (!currentError) return prev;

      if (domainError) {
        if (currentError === domainError) {
          return prev;
        }

        return {
          ...prev,
          existingDomainFull: domainError
        };
      }

      if (!currentError) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors.existingDomainFull;
      return nextErrors;
    });
  } else if (validationErrors[fieldId]) {
    const field = allFormSteps.flatMap(step => step.fields || []).find(item => item.id === fieldId)
      || { id: fieldId, label: fieldId === 'businessPhone' ? 'Business Contact Number' : fieldId };
    const nextError = validateField(field, processedValue);
    setValidationErrors(prev => {
      if (nextError) return { ...prev, [fieldId]: nextError };
      if (!prev[fieldId]) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors[fieldId];
      return nextErrors;
    });
  }
};

  const handleFieldBlur = (field, value) => {
    const error = validateField(field, value);
    setValidationErrors(prev => { const next = { ...prev }; if(error) next[field.id] = error; else delete next[field.id]; return next; });
  };

  const handlePhoneBlur = useCallback((value) => {
    setValidationErrors(prev => {
      const phoneError = getUkMobileValidationError(value);
      const currentError = prev.phone;

      if (phoneError) {
        if (currentError === phoneError) {
          return prev;
        }

        return {
          ...prev,
          phone: phoneError
        };
      }

      if (!currentError) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors.phone;
      return nextErrors;
    });
  }, []);

  const toggleDomainSelection = useCallback((fullDomain, extension) => {
    if (!fullDomain || !extension) {
      return;
    }

    const normalizedDomain = fullDomain.toLowerCase();
    const normalizedExtension = extension.toLowerCase();

    if (!DOMAIN_EXTENSION_CONFIG[normalizedExtension]) {
      return;
    }

    setFormData(prev => {
      const previousSelected = extractSelectedDomains(prev.selectedDomains, prev.newDomainName_selected);
      const existingIndex = previousSelected.findIndex(domain => domain.fullDomain === normalizedDomain);

      let updatedSelected;

      if (existingIndex >= 0) {
        // Removing a domain - always allowed
        updatedSelected = [
          ...previousSelected.slice(0, existingIndex),
          ...previousSelected.slice(existingIndex + 1)
        ];
      } else {
        // Adding a new domain - check 5 domain limit
        const MAX_DOMAINS = 5;
        if (previousSelected.length >= MAX_DOMAINS) {
          setStatusMessage({
            type: 'error',
            text: `You can select a maximum of ${MAX_DOMAINS} domains. Please remove a domain before adding another.`,
            show: true
          });
          setTimeout(() => setStatusMessage(prev => ({ ...prev, show: false })), 5000);
          return prev; // Don't add the domain
        }

        updatedSelected = [
          ...previousSelected,
          { fullDomain: normalizedDomain, extension: normalizedExtension }
        ];
      }

      const isEntitled =
        prev.domainOption === 'free-domain' ||
        (prev.domainOption === 'existing-domain' && prev.domainTransferOption === 'keep-domain');

      const updatedFreeDomain = isEntitled
        ? ensureValidFreeDomainSelection(
            updatedSelected,
            prev.freeDomainSelection || prev.newDomainName_selected
          )
        : '';

      return {
        ...prev,
        selectedDomains: updatedSelected,
        freeDomainSelection: updatedFreeDomain || '',
        newDomainName_selected: updatedFreeDomain || ''
      };
    });
  }, []);

  const setPrimaryDomainSelection = useCallback((fullDomain) => {
    if (!fullDomain) return;

    const normalizedDomain = fullDomain.toLowerCase();

    setFormData(prev => {
      const previousSelected = extractSelectedDomains(prev.selectedDomains, prev.newDomainName_selected);
      const match = previousSelected.find(domain => domain.fullDomain === normalizedDomain);

      if (!match || !DOMAIN_EXTENSION_CONFIG[match.extension]?.freeEligible) {
        return prev;
      }

      const isEntitled =
        prev.domainOption === 'free-domain' ||
        (prev.domainOption === 'existing-domain' && prev.domainTransferOption === 'keep-domain');

      if (!isEntitled) {
        return prev;
      }

      if (prev.freeDomainSelection === normalizedDomain) {
        return prev;
      }

      return {
        ...prev,
        selectedDomains: previousSelected,
        freeDomainSelection: normalizedDomain,
        newDomainName_selected: normalizedDomain
      };
    });
  }, []);

 const handleCheckboxChange = (fieldId, value, checked) => {
  setFormData(prev => {
    const currentValues = prev[fieldId] || [];
    if (checked) {
      return { ...prev, [fieldId]: [...currentValues, value] };
    } else {
      return { ...prev, [fieldId]: currentValues.filter(v => v !== value) };
    }
  });
  

};

const scrollToFirstInvalidField = (errors) => {
    const firstErrorFieldId = Object.keys(errors || {})[0];
    if (!firstErrorFieldId) return;

    const fieldAnchorMap = {
      newDomainName: '[data-revealed-content="domainOption-free-domain"], [data-revealed-content="existing-domain-additional"]',
      primaryDomain: '[data-revealed-content="existing-domain-additional"], [data-revealed-content="domainOption-free-domain"]',
      businessPhone: '[data-revealed-content="separateBusinessPhone"]',
      businessAddressLine1: '[data-revealed-content="separateBusinessAddress"]',
      businessCity: '[data-revealed-content="separateBusinessAddress"]',
      businessPostcode: '[data-revealed-content="separateBusinessAddress"]',
    };

    const selector = fieldAnchorMap[firstErrorFieldId];
    const el =
      document.getElementById(firstErrorFieldId) ||
      document.querySelector(`[name="${firstErrorFieldId}"]`) ||
      (selector ? document.querySelector(selector) : null) ||
      document.querySelector(`[data-revealed-content="${firstErrorFieldId}"]`);
    if (!el || typeof el.scrollIntoView !== 'function') return;

    el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
    if (typeof el.focus === 'function') {
      setTimeout(() => el.focus({ preventScroll: true }), 120);
    }
  };

const isStepValid = (stepIndex, options = {}) => {
    const { scrollToError = false } = options;
    const step = formSteps[stepIndex];
    if (!step || step.isSummary) return true;

    const stepId = step.id;

    const errors = {};
    let hasErrors = false;
    
    step.fields.forEach(field => {
      if (field.conditionalDisplay && !field.conditionalDisplay(formData)) {
        return;
      }
      
      const value = formData[field.id];
      const error = validateField(field, value);
      if (error) {
        errors[field.id] = error;
        hasErrors = true;
      }
    });
    
    // Special validation for domain selection step
    if (stepId === 'domain-details' || stepId === 'additional-domain-search') {
      const shouldValidateDomainSearch =
        (stepId === 'domain-details' && formData.domainOption === 'free-domain') ||
        (stepId === 'additional-domain-search' &&
          formData.domainOption === 'existing-domain' &&
          !!formData.domainTransferOption &&
          formData.existingDomainAdditionalChoice === 'add');

      if (shouldValidateDomainSearch) {
        if (!selectedDomains.length) {
          const domainName = formData.newDomainName;
          const availableExtensions = AVAILABLE_DOMAIN_EXTENSIONS;

          if (domainName) {
            const hasAvailableDomains = availableExtensions.some(ext => {
              const fullDomain = domainName + ext;
              return domainAvailability[fullDomain] === true;
            });

            const hasCheckedDomains = availableExtensions.some(ext => {
              const fullDomain = domainName + ext;
              return Object.prototype.hasOwnProperty.call(domainAvailability, fullDomain);
            });

            if (hasAvailableDomains) {
              errors['newDomainName'] = 'One or more options are available. Add one using “Add web address”.';
            } else if (hasCheckedDomains) {
              errors['newDomainName'] = 'Those checked options are unavailable. Try another name or extension.';
            } else {
              errors['newDomainName'] = 'Check availability first, then add an available option.';
            }
          } else {
            errors['newDomainName'] = 'Enter a web address, check availability, then add one option.';
          }
          hasErrors = true;
        }
      }
    }
    
    if (stepId === 'primary-domain-selection') {
      if (!allConfiguredDomains.length) {
        errors.primaryDomain = 'Add at least one web address before selecting a primary address.';
        hasErrors = true;
      } else if (!primaryDomainSelection) {
        errors.primaryDomain = 'Select which web address should be your primary one.';
        hasErrors = true;
      }
    }

    if (stepId === 'address-website-display') {
      if (formData.separateBusinessAddress && formData.separateBusinessAddress.includes('separate')) {
        const businessAddressFields = [
          { id: 'businessAddressLine1', label: 'Business Address - Street Address' },
          { id: 'businessCity', label: 'Business Town/City' },
          { id: 'businessPostcode', label: 'Business Postcode' }
        ];
        
        businessAddressFields.forEach(field => {
          const value = formData[field.id];
          const error = validateField(field, value);
          if (error) {
            errors[field.id] = error;
            hasErrors = true;
          }
        });
      }
    }
    
    if (stepId === 'contact-information') {
      if (formData.separateBusinessPhone && formData.separateBusinessPhone.includes('separate')) {
        const field = { id: 'businessPhone', label: 'Business Contact Number' };
        const value = formData[field.id];
        const error = validateField(field, value);
        if (error) {
          errors[field.id] = error;
          hasErrors = true;
        }
      }
    }
    
    setValidationErrors(errors);

    if (hasErrors && scrollToError) {
      scrollToFirstInvalidField(errors);
    }

    return !hasErrors;
  };

  const renderField = (field) => {
    const fieldValue = formData[field.id];
    const error = validationErrors[field.id];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel': {
        const addressRef = field.id === 'invoiceAddressLine1' ? invoiceAddressRef :
                          field.id === 'businessAddressLine1' ? businessAddressRef : null;

        // Force lowercase for email fields
        const isEmailField = field.type === 'email';
        const isPrimaryPhoneField = field.type === 'tel' && field.id === 'phone';
        const inputPaddingClass = isPrimaryPhoneField ? 'pl-[3.75rem] pr-4' : 'px-4';

        return (
          <div>
            <div className="relative">
            {isPrimaryPhoneField && (
              <span className="pointer-events-none absolute z-10 left-3 top-1/2 -translate-y-1/2 inline-flex items-center rounded-[3px] border border-gray-300 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-700" aria-hidden="true">
                07
              </span>
            )}
            <input
              type={field.type}
              id={field.id}
              ref={addressRef}
              value={fieldValue || ''}
              onChange={(e) => {
                handleInputChange(field.id, e.target.value);
              }}
              onBlur={(e) => isPrimaryPhoneField ? handlePhoneBlur(e.target.value) : handleFieldBlur(field, e.target.value)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${field.id}-error` : undefined}
              placeholder={field.placeholder}
              className={`w-full ${inputPaddingClass} py-4 border focus:ring-yellow-400 focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 focus:shadow-lg focus:shadow-yellow-500/20 transition-all duration-300 ${
                error ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
              } ${fieldValue ? 'bg-gray-50/50' : 'bg-white'}`}
              style={{ borderRadius: '3px' }}
              required={field.required}
              inputMode={isPrimaryPhoneField ? 'tel' : undefined}
              autoComplete={isPrimaryPhoneField ? 'tel-national' : isEmailField ? 'email' : undefined}
              maxLength={isPrimaryPhoneField ? 11 : field.maxLength}
            />
            </div>
            {error && (
              <p id={`${field.id}-error`} role="status" className="mt-2 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );
      }

      case 'date': {
        const { minDob, maxDob } = getDobDateBoundaries();
        const isDobField = field.id === 'dateOfBirth';

        const openDatePicker = () => {
          const input = document.getElementById(field.id);
          if (!input) return;

          if (typeof input.showPicker === 'function') {
            input.showPicker();
          } else {
            input.focus();
          }
        };

        return (
          <div className="relative">
            <input
              type="date"
              id={field.id}
              value={fieldValue || ''}
              onChange={(e) => {
                if (!isDobField) {
                  handleInputChange(field.id, e.target.value);
                  return;
                }

                handleInputChange(field.id, normalizeDateInputForTyping(e.target.value));
              }}
              onBlur={(e) => {
                if (!isDobField) return;
                const nextValue = sanitizeDobInputValue(e.target.value, minDob, maxDob);
                handleInputChange(field.id, nextValue);
              }}
              min={isDobField ? minDob : undefined}
              max={isDobField ? maxDob : undefined}
              inputMode={isDobField ? 'numeric' : undefined}
              autoComplete={isDobField ? 'bday' : undefined}
              className={`w-full pl-4 pr-24 py-4 border focus:ring-yellow-400 focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 focus:shadow-lg focus:shadow-yellow-500/20 transition-all duration-300 ${
                error ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
              } ${fieldValue ? 'bg-gray-50/50' : 'bg-white'}`}
              required={field.required}
            />
            <div className="absolute inset-y-0 right-2 flex items-center gap-1">
              {!!fieldValue && (
                <button
                  type="button"
                  onClick={() => handleInputChange(field.id, '')}
                  className="rounded-[3px] border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:border-gray-400 hover:text-gray-800"
                  aria-label="Clear date"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={openDatePicker}
                className="rounded-[3px] border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:border-yellow-500 hover:text-yellow-700"
                aria-label="Open date picker"
              >
                Pick
              </button>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );
      }

      case 'select':
        // Use searchable select for business category, regular select for others
        if (field.id === 'businessCategory' || field.id === 'currentEmailProvider' || field.id === 'businessType') {
          return <SearchableSelect field={field} fieldValue={fieldValue} error={error} handleInputChange={handleInputChange} />;
        }
        
        return (
          <div className="relative">
            <select
              id={field.id}
              value={fieldValue || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`w-full px-4 py-4 border focus:ring-yellow-400 focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 focus:shadow-lg focus:shadow-yellow-500/20 transition-all duration-300 ${
                error ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
              } ${fieldValue ? 'bg-gray-50/50' : 'bg-white'}`}
              required={field.required}
            >
              <option value="">Choose an option...</option>
              {field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <p className="mt-2 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-6 sm:space-y-4">
            {field.options.map(option => (
              <label key={option.value} className="flex items-start rounded-[3px] p-6 border border-gray-200 hover:bg-gray-50 cursor-pointer transition-all bg-stone-100">
                <input
                  type="radio"
                  name={field.id}
                  value={option.value}
                  checked={fieldValue === option.value}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  className="mr-4 flex-shrink-0 mt-1 w-5 h-5"
                  style={{ accentColor: '#EAB308' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-lg sm:text-lg font-medium mb-3 sm:mb-2 font-cabin" style={{ color: '#090909' }}>{option.label}</div>
                  {option.description && (
                    <div className="text-base sm:text-sm font-mulish mb-2" style={{ color: '#090909' }}>{option.description}</div>
                  )}
                  {option.disclaimer && (
                    <div
                      className="mt-2 rounded-[3px] border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700"
                    >
                      <strong>Important:</strong> {option.disclaimer}
                    </div>
                  )}
                </div>
              </label>
            ))}
            {error && (
              <p className="mt-4 sm:mt-3 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );

      case 'plan-cards':
        return (
          <div role="radiogroup" aria-labelledby="registration-step-title" className="registration-plans grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10 lg:grid-cols-3">
            {field.options.map(option => (
              <div
                role="radio"
                tabIndex={0}
                aria-checked={fieldValue === option.value}
                aria-label={option.label}
                onKeyDown={(event) => {
                  if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); handleInputChange(field.id, option.value); }
                  if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) {
                    event.preventDefault();
                    const cards = [...event.currentTarget.parentElement.querySelectorAll('[role="radio"]')];
                    const next = (cards.indexOf(event.currentTarget) + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + cards.length) % cards.length;
                    cards[next].focus(); handleInputChange(field.id, field.options[next].value);
                  }
                }}
                key={option.value}
                style={{ 
                  borderRadius: '3px', 
                  backgroundColor: fieldValue === option.value ? undefined : '#F3F3F3' 
                }}
                className={`relative my-2 sm:my-0 cursor-pointer rounded-[3px] border-2 p-5 sm:p-6 transition-all duration-300 hover:shadow-lg ${
                  fieldValue === option.value
                    ? 'border-yellow-500 bg-yellow-50 shadow-lg shadow-yellow-500/20'
                    : 'border-gray-200 hover:border-yellow-300 hover:bg-gray-50 shadow-md'
                }`}
                onClick={() => handleInputChange(field.id, option.value)}
              >
                {fieldValue === option.value && <CheckCircle className="plan-selection-tick" aria-hidden="true" />}
                {option.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-[3px] bg-yellow-500 px-4 py-1 text-xs font-bold text-white shadow-md">
                    POPULAR
                  </div>
                )}
                
                <div className="flex items-start gap-4 sm:block sm:text-center">
                  <div className="mb-0 mt-1 flex justify-center sm:mb-4 sm:mt-0">
                    {option.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 font-cabin text-left sm:text-center" style={{ color: '#090909' }}>{option.label}</h2>

                    <div className="space-y-1 mb-4 sm:mb-4 text-left sm:text-center">
                      <div className="text-sm font-mulish" style={{ color: '#090909' }}>{option.pages}</div>
                      <div className="text-sm font-mulish" style={{ color: '#090909' }}>{option.emails}</div>
                    </div>

                    <div className="pt-3 sm:pt-4 border-t border-gray-200 text-left sm:text-center">
                      <div className="text-2xl sm:text-3xl font-bold mb-1 font-cabin" style={{ color: '#090909' }}>
                        £{option.monthlyPrice}
                        <span className="text-sm sm:text-base font-normal font-mulish" style={{ color: '#090909' }}> /month</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'domain-checker': {
        const domainName = fieldValue || '';
        const normalizedDomainName = String(domainName || '').trim().toLowerCase();
        const availableExtensions = AVAILABLE_DOMAIN_EXTENSIONS;
        const shouldDisplayResults = Boolean(
          normalizedDomainName &&
          normalizedDomainName.length >= 3 &&
          debouncedDomainSearch === normalizedDomainName
        );
        const isDomainSearchDebouncing =
          normalizedDomainName.length >= 3 && debouncedDomainSearch !== normalizedDomainName;
        const domainPrefixIssue = (() => {
          if (domainName.startsWith('http')) {
            return 'Please remove "http://" or "https://" from the start of your web address.';
          }
          if (domainName.startsWith('www')) {
            return 'Please remove "www." from the start of your web address.';
          }
          return '';
        })();
        const hasUnavailableDomains = availableExtensions.some((ext) => {
          const fullDomain = domainName + ext;
          return domainAvailability[fullDomain] === false;
        });
        const hasAvailableDomains = availableExtensions.some((ext) => {
          const fullDomain = domainName + ext;
          return domainAvailability[fullDomain] === true;
        });
        const checkedCount = availableExtensions.filter((ext) => Object.prototype.hasOwnProperty.call(domainAvailability, domainName + ext)).length;
        const availableCount = availableExtensions.filter((ext) => domainAvailability[domainName + ext] === true).length;
        const unavailableCount = availableExtensions.filter((ext) => domainAvailability[domainName + ext] === false).length;
        const activeDomainCheckKeys = Object.keys(activeDomainChecks);
        const countdownKeys = Object.keys(domainCountdowns);
        const canShowFreeDomainSwitch = isFreeDomainEntitled && selectedDomains.length > 1;
        const sortedExtensions = [...availableExtensions].sort((a, b) => {
          const rankFor = (ext) => {
            const fullDomain = domainName + ext;
            const isSelectedExt = selectedDomains.some((domain) => domain.fullDomain === fullDomain);
            const availability = domainAvailability[fullDomain];
            if (isSelectedExt) return 0;
            if (availability === true) return 1;
            if (availability === false) return 3;
            if (availability === 'error') return 4;
            return 2;
          };

          const rankDiff = rankFor(a) - rankFor(b);
          if (rankDiff !== 0) return rankDiff;

          const aConfig = DOMAIN_EXTENSION_CONFIG[a];
          const bConfig = DOMAIN_EXTENSION_CONFIG[b];
          if (Boolean(aConfig?.freeEligible) !== Boolean(bConfig?.freeEligible)) {
            return aConfig?.freeEligible ? -1 : 1;
          }

          const aPrice = aConfig?.price ?? Number.MAX_SAFE_INTEGER;
          const bPrice = bConfig?.price ?? Number.MAX_SAFE_INTEGER;
          if (aPrice !== bPrice) return aPrice - bPrice;

          return availableExtensions.indexOf(a) - availableExtensions.indexOf(b);
        });
        const quickList = DOMAIN_PRIMARY_VISIBLE_EXTENSIONS.filter((extension) =>
          sortedExtensions.includes(extension)
        );
        const selectedExtensions = sortedExtensions.filter((ext) => {
          const fullDomain = domainName + ext;
          return selectedDomains.some((domain) => domain.fullDomain === fullDomain);
        });
        const visibleExtensions = showAllDomainExtensions
          ? sortedExtensions
          : Array.from(new Set([...quickList, ...selectedExtensions]));
        const hiddenExtensionCount = Math.max(0, sortedExtensions.length - visibleExtensions.length);
        const marketGuidance = (() => {
          if (hasUnavailableDomains && !hasAvailableDomains) {
            return {
              tone: 'amber',
              title: 'No extensions are currently available',
              text: `Try a different web address or add words like location, service, or industry (e.g. '${domainName}services').`
            };
          }

          if (hasAvailableDomains && hasUnavailableDomains) {
            return {
              tone: 'yellow',
              title: 'Some options are available',
              text: `Pick an available extension below and add it to continue.`
            };
          }

          if (hasAvailableDomains) {
            return {
              tone: 'green',
              title: 'Great! Extensions are available',
              text: 'Choose one or more available options below.'
            };
          }

          return null;
        })();
        return (
          <div className="space-y-4 sm:space-y-4">
            <div className="space-y-2">
              <label htmlFor="domain-search-input" className="sr-only">
                {field.label}
              </label>
              <div className="flex flex-col gap-2.5 sm:flex-row">
                {(() => {
                  const checkAllCountdown = domainCountdowns['__all__'];
                  const isAnyManualCountdownActive = Object.keys(domainCountdowns).some(
                    (key) => key !== '__all__'
                  );
                  const isAnyManualCheckActive = Object.keys(activeDomainChecks).length > 0;
                  const disableCheckAllButton =
                    !shouldDisplayResults ||
                    isCheckAllRunning ||
                    Boolean(checkAllCountdown) ||
                    isAnyManualCountdownActive ||
                    isAnyManualCheckActive;

                  const checkAllButtonLabel = (() => {
                    if (checkAllCountdown) {
                      return checkAllCurrentExtension
                        ? `Checking ${checkAllCurrentExtension} in ${checkAllCountdown}s…`
                        : `Next check in ${checkAllCountdown}s…`;
                    }

                    if (isCheckAllRunning) {
                      return checkAllCurrentExtension
                        ? `Checking ${checkAllCurrentExtension}…`
                        : 'Checking…';
                    }

                    return 'Check all';
                  })();

                  return (
                    <>
                      <input
                        id="domain-search-input"
                        type="text"
                        value={domainName}
                        onChange={(e) => {
                          const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                          handleInputChange(field.id, value);
                          setShowAllDomainExtensions(false);
                          setDomainAvailability({});
                        }}
                        placeholder={field.placeholder}
                        className={`flex-1 w-full min-h-[56px] rounded-[3px] px-4 py-3 text-base border focus:ring-2 focus:ring-yellow-400 focus:border-yellow-500 transition-all ${
                          error ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      <div className="flex flex-col gap-2 sm:flex-none sm:self-stretch">
                        <button
                          type="button"
                          onClick={() => checkAllDomainExtensions(domainName, availableExtensions)}
                          disabled={disableCheckAllButton}
                          className={`inline-flex min-h-[56px] w-full sm:h-full items-center justify-center rounded-[3px] px-4 sm:px-6 text-sm font-semibold transition-colors ${
                            disableCheckAllButton
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-yellow-500 text-white hover:bg-yellow-600'
                          }`}
                        >
                          {checkAllButtonLabel}
                        </button>
                        {checkAllCountdown && (
                          <button
                            type="button"
                            onClick={handleCancelCheckAllCountdown}
                            className="inline-flex items-center justify-center rounded-[3px] border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100"
                          >
                            Cancel checks
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
              {domainPrefixIssue && (
                <p className="text-xs text-gray-500">{domainPrefixIssue}</p>
              )}
            </div>

            {shouldDisplayResults && (
              <div className="space-y-4">
                <DomainResultsHeader
                  checkedCount={checkedCount}
                  availableCount={availableCount}
                  unavailableCount={unavailableCount}
                  selectedCount={selectedDomains.length}
                  showDomainTips={showDomainTips}
                  onToggleTips={() => setShowDomainTips((prev) => !prev)}
                />
                {showDomainTips && (
                  <div className="rounded-[3px] border border-gray-200 bg-white p-4">
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <svg className="h-4 w-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm.75 11h-1.5v-1.5h1.5V13zm0-3h-1.5V6h1.5v4z" />
                        </svg>
                        <span>Web address tips</span>
                      </div>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
                        <li>Add only the web addresses you want to register now.</li>
                        <li>Your free credit applies automatically to one eligible address.</li>
                      </ul>
                    </div>
                  </div>
                )}

                {isDomainSearchDebouncing && (
                  <div className="text-sm text-gray-600">Checking extensions…</div>
                )}

                {selectedDomains.length > 0 && (
                  <div className="rounded-[3px] border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm font-medium text-yellow-900">
                    Selected web addresses: {selectedDomains.length}
                    {domainAnnualCost > 0 ? ` • ${GBP_CURRENCY_FORMATTER.format(domainAnnualCost)}/year additional` : ' • No additional annual fees'}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-gray-600">
                    {showAllDomainExtensions ? 'All extensions shown' : '.co.uk, .com, and .uk shown first'}
                  </span>
                  {showAllDomainExtensions && sortedExtensions.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setShowAllDomainExtensions(false)}
                      className="inline-flex items-center rounded-[3px] border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:border-yellow-400 hover:text-yellow-700"
                    >
                      Show fewer extensions
                    </button>
                  )}
                </div>

                <div className="overflow-hidden rounded-[3px] border border-gray-200 bg-white divide-y divide-gray-200">
                  {visibleExtensions.map((extension, index) => {
                    const fullDomain = domainName + extension;
                    const isChecked = Object.prototype.hasOwnProperty.call(domainAvailability, fullDomain);
                    const checkResult = domainAvailability[fullDomain];
                    const isAvailable = checkResult === true;
                    const isError = checkResult === 'error';
                    const isSelected = selectedDomains.some((domain) => domain.fullDomain === fullDomain);
                    const config = DOMAIN_EXTENSION_CONFIG[extension];
                    const isFreeEligible = Boolean(config?.freeEligible);
                    const isFreeDomain = isFreeEligible && freeDomainSelection === fullDomain;
                    const freeCreditAssignedTo = freeDomainSelection || '';
                    const hasUnusedFreeCredit = isFreeDomainEntitled && !freeCreditAssignedTo;
                    const isFreeCreditUsedElsewhere =
                      isFreeEligible &&
                      isFreeDomainEntitled &&
                      Boolean(freeCreditAssignedTo) &&
                      freeCreditAssignedTo !== fullDomain;
                    const wouldBeFreeIfSelected = isFreeEligible && (hasUnusedFreeCredit || isFreeDomain);
                    const attempts = domainCheckAttempts[fullDomain] || 0;
                    const countdownRemaining = domainCountdowns[fullDomain];
                    const checkAllCountdown = domainCountdowns['__all__'];
                    const isCheckingInProgress = Boolean(activeDomainChecks[fullDomain]);
                    const otherCountdownActive = countdownKeys.some((key) => key !== fullDomain && key !== '__all__');
                    const otherManualCountdownKey = countdownKeys.find(
                      (key) => key !== fullDomain && key !== '__all__'
                    );
                    const sharedManualCountdownRemaining = otherManualCountdownKey
                      ? domainCountdowns[otherManualCountdownKey]
                      : null;
                    const otherCheckActive = activeDomainCheckKeys.some((key) => key !== fullDomain);
                    const checkAllCountdownActive = countdownKeys.includes('__all__');
                    const isUpcomingCheckAllTarget =
                      Boolean(checkAllCountdown) &&
                      isCheckAllRunning &&
                      checkAllCurrentExtension === extension &&
                      !isCheckingInProgress;
                    const hasMaxedRetries = isError && attempts >= MAX_DOMAIN_RETRIES;
                    const manualCheckDisabled =
                      isCheckAllRunning ||
                      checkAllCountdownActive ||
                      Boolean(countdownRemaining) ||
                      isCheckingInProgress ||
                      otherCountdownActive ||
                      otherCheckActive ||
                      hasMaxedRetries;
                    const manualCheckLabel = (() => {
                      if (isUpcomingCheckAllTarget && checkAllCountdown) {
                        return `Checking in ${checkAllCountdown}s…`;
                      }
                      if (countdownRemaining) {
                        return `Checking in ${countdownRemaining}s…`;
                      }
                      if (sharedManualCountdownRemaining) {
                        return `Next check in ${sharedManualCountdownRemaining}s…`;
                      }
                      if (isCheckingInProgress) {
                        return 'Checking…';
                      }
                      if (hasMaxedRetries) {
                        return 'Maximum retries reached';
                      }
                      return 'Check';
                    })();
                    const priceLabel = (() => {
                      if (!config) {
                        return 'Pricing to be confirmed';
                      }

                      if (isFreeDomain) {
                        return 'Included with plan';
                      }

                      if (wouldBeFreeIfSelected && hasUnusedFreeCredit) {
                        return 'Included with plan (free credit eligible)';
                      }

                      if (config.price != null) {
                        return `${GBP_CURRENCY_FORMATTER.format(config.price)} per year`;
                      }

                      return 'Pricing to be confirmed';
                    })();
                    const canRetry = attempts < MAX_DOMAIN_RETRIES;
                    const retriesLeft = Math.max(0, MAX_DOMAIN_RETRIES - attempts);
                    const retryDisabled = manualCheckDisabled || !canRetry;

                    return (
                      <div
                        key={extension}
                        className={`registration-domain-row px-3 py-3.5 transition-colors sm:px-4 ${
                          isSelected ? 'bg-yellow-50' : 'bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex-1 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              {isSelected && (
                                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                  Selected
                                </span>
                              )}
                              {isFreeDomain && (
                                <span className="inline-flex items-center rounded-full bg-green-600 px-2 py-0.5 text-xs font-medium text-white">
                                  Free domain applied
                                </span>
                              )}
                              {isChecked && !isError && (
                                <span
                                  className={`inline-flex items-center gap-2 text-xs font-medium ${
                                    isAvailable ? 'text-green-600' : 'text-red-600'
                                  }`}
                                >
                                  <span
                                    className={`h-2.5 w-2.5 rounded-full ${
                                      isAvailable ? 'bg-green-500' : 'bg-red-500'
                                    }`}
                                  />
                                  {isAvailable ? 'Available' : 'Not available'}
                                </span>
                              )}
                            </div>
                            <div className="text-base font-semibold text-gray-900">{fullDomain}</div>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">{priceLabel}</span>
                            </div>
                            {isFreeCreditUsedElsewhere && (
                              <details className="text-xs text-yellow-800">
                                <summary className="cursor-pointer font-medium text-yellow-700">Details</summary>
                                <p className="mt-1">Free credit is currently used on {freeCreditAssignedTo}.</p>
                              </details>
                            )}
                          </div>

                          <div className="w-full sm:w-auto sm:min-w-[190px]">
                            <div className="space-y-2">
                              {countdownRemaining ? (
                                <div className="space-y-2">
                                  <button
                                    type="button"
                                    onClick={() => handleCancelIndividualCountdown(fullDomain)}
                                    className="w-full rounded-[3px] border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100"
                                  >
                                    Cancel check
                                  </button>
                                  <div className="rounded-[3px] border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">
                                    We'll try again in {countdownRemaining}s unless you cancel.
                                  </div>
                                </div>
                              ) : !isChecked ? (
                                <button
                                  type="button"
                                  onClick={() => handleIndividualDomainCheck(domainName, extension)}
                                  disabled={manualCheckDisabled}
                                  className={`w-full px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 ${UI_CLASS_TOKENS.buttonPrimary}`}
                                >
                                  {manualCheckLabel}
                                </button>
                              ) : isError ? (
                                <div className="space-y-3 rounded-[3px] border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                  <div>
                                    <div className="font-semibold">Service temporarily unavailable</div>
                                    <div className="text-xs">We hit a snag while checking this domain. Please try again after the countdown.</div>
                                  </div>
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <button
                                      type="button"
                                      onClick={() => handleIndividualDomainCheck(domainName, extension)}
                                      disabled={retryDisabled}
                                      className={`inline-flex w-full items-center justify-center rounded-[3px] border border-red-300 px-3 py-2 text-xs font-semibold transition-colors sm:w-auto ${
                                        retryDisabled
                                          ? 'cursor-not-allowed bg-red-100 text-red-400'
                                          : 'bg-white text-red-600 hover:bg-red-100'
                                      }`}
                                    >
                                      {canRetry ? `Try again (${retriesLeft} left)` : 'Retry unavailable'}
                                    </button>
                                    {!canRetry && (
                                      <span className="text-xs text-red-600">
                                        We've reached the retry limit. Contact support@ubiewebsites.co.uk for help.
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : isAvailable ? (
                                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => toggleDomainSelection(fullDomain, extension)}
                                    className={`w-full rounded-[3px] px-4 py-2 text-sm font-semibold transition-colors ${
                                      isSelected
                                        ? 'border border-red-300 bg-white text-red-700 hover:bg-red-50'
                                        : UI_CLASS_TOKENS.buttonPrimary
                                    }`}
                                  >
                                    {isSelected ? 'Remove web address' : 'Add web address'}
                                  </button>
                                  {isFreeEligible && !isFreeDomain && canShowFreeDomainSwitch && isSelected && (
                                    <button
                                      type="button"
                                      onClick={() => setPrimaryDomainSelection(fullDomain)}
                                      className={`w-full px-4 py-2 text-sm font-semibold transition-colors ${UI_CLASS_TOKENS.buttonSecondary}`}
                                    >
                                      Make this my free domain
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs font-medium text-amber-700">
                                  Extension taken — try another option or tweak your web address.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                      </div>
                    );
                  })}

                  {!showAllDomainExtensions && hiddenExtensionCount > 0 && (
                    <div className="bg-yellow-50 px-4 py-4 sm:px-5">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAllDomainExtensions(true);
                          runAutoCheckForExtensions(normalizedDomainName, sortedExtensions);
                        }}
                        className="inline-flex w-full items-center justify-center rounded-[3px] border border-yellow-500 bg-yellow-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-yellow-600"
                      >
                        Show {hiddenExtensionCount} more extensions and check availability
                      </button>
                    </div>
                  )}
                </div>
                {selectedDomains.length > 0 && (
                  <div className="space-y-2">
                    <DisclosureToggleButton
                      isOpen={showSelectedDomains}
                      closedLabel="Selected web addresses"
                      openLabel="Hide selected addresses"
                      count={selectedDomains.length}
                      onClick={() => setShowSelectedDomains((prev) => !prev)}
                    />
                    {showSelectedDomains && (
                      <div className="rounded-[3px] border border-yellow-200 bg-yellow-50 p-4">
                        <div className="text-yellow-800 font-medium mb-2">Selected domains</div>
                        <ul className="space-y-2 text-sm text-yellow-800">
                          {selectedDomains.map(domain => {
                            const config = DOMAIN_EXTENSION_CONFIG[domain.extension];
                            const isFreeDomain = config?.freeEligible && freeDomainSelection === domain.fullDomain;
                            const freeCreditAssignedTo = freeDomainSelection || '';
                            const hasUnusedFreeCredit = isFreeDomainEntitled && !freeCreditAssignedTo;
                            const isFreeCreditUsedElsewhere =
                              Boolean(freeCreditAssignedTo) &&
                              freeCreditAssignedTo !== domain.fullDomain &&
                              Boolean(config?.freeEligible) &&
                              isFreeDomainEntitled;
                            const priceDisplay = (() => {
                              if (!config) {
                                return 'Pricing to be confirmed';
                              }

                              if (isFreeDomain) {
                                return 'Included with plan';
                              }

                              if (config.freeEligible && hasUnusedFreeCredit) {
                                return 'Included with plan';
                              }

                              if (config.price != null) {
                                const basePrice = `${GBP_CURRENCY_FORMATTER.format(config.price)} per year`;
                                return isFreeCreditUsedElsewhere
                                  ? `${basePrice} (free credit used on ${freeCreditAssignedTo})`
                                  : basePrice;
                              }

                              return 'Pricing to be confirmed';
                            })();
                            const showFreeDomainButton = config?.freeEligible && !isFreeDomain && canShowFreeDomainSwitch;
                            return (
                              <li key={domain.fullDomain}>
                                <div className="flex flex-col gap-2 rounded-[3px] border border-yellow-200 bg-yellow-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="space-y-1">
                                    <span className="font-semibold text-yellow-900">{domain.fullDomain}</span>
                                    <div className="flex flex-wrap items-center gap-2 text-sm">
                                      <span className={isFreeDomain ? 'text-green-700 font-medium' : 'text-yellow-700'}>
                                        {priceDisplay}
                                      </span>
                                      {showFreeDomainButton && (
                                        <button
                                          type="button"
                                          onClick={() => setPrimaryDomainSelection(domain.fullDomain)}
                                          className="text-xs font-medium text-yellow-700 underline decoration-dotted underline-offset-4 hover:text-yellow-800"
                                        >
                                          Make this my free domain
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => toggleDomainSelection(domain.fullDomain, domain.extension)}
                                    className="inline-flex items-center justify-center rounded-[3px] border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition-colors hover:border-red-300 hover:bg-red-100"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                        <div className="mt-3 text-sm font-medium text-yellow-800">
                          {domainAnnualCost > 0
                            ? `Additional web addresses: ${GBP_CURRENCY_FORMATTER.format(domainAnnualCost)} per year`
                            : 'No additional web address fees'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {domainName && domainName.length < 3 && (
              <div className="text-sm text-gray-600">Web address must be at least 3 characters long</div>
            )}

            {marketGuidance && (
              <div className={`p-4 border ${marketGuidance.tone === 'green' ? 'bg-green-50 border-green-200' : marketGuidance.tone === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-yellow-50 border-yellow-200'}`} style={{ borderRadius: '3px' }}>
                <div>
                  <p className={`font-medium mb-1 ${marketGuidance.tone === 'green' ? 'text-green-800' : marketGuidance.tone === 'amber' ? 'text-amber-800' : 'text-yellow-800'}`}>{marketGuidance.title}</p>
                  <p className={`text-sm ${marketGuidance.tone === 'green' ? 'text-green-700' : marketGuidance.tone === 'amber' ? 'text-amber-700' : 'text-yellow-700'}`}>{marketGuidance.text}</p>
                </div>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );
      }

      case 'primary-domain-selector': {
        const availablePrimaryDomains = allConfiguredDomains;

        if (!availablePrimaryDomains.length) {
          return (
            <div className="rounded-[3px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Add at least one domain first, then come back to choose your primary web address.
            </div>
          );
        }

        return (
          <div className="space-y-4">
            <div className="rounded-[3px] border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm text-gray-700">
                Your primary web address is where your main website will load. Any additional domains will redirect to it.
              </p>
            </div>

            <div className="space-y-3">
              {availablePrimaryDomains.map((domain) => {
                const isSelected = primaryDomainSelection === domain.fullDomain;
                const sourceLabel = domain.source === 'existing' ? 'Existing web address' : 'New web address';

                return (
                  <label
                    key={domain.fullDomain}
                    className={`flex cursor-pointer items-start gap-3 rounded-[3px] border p-4 transition-all ${
                      isSelected
                        ? 'border-yellow-400 bg-yellow-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="primaryDomain"
                      value={domain.fullDomain}
                      checked={isSelected}
                      onChange={(e) => handleInputChange('primaryDomain', e.target.value)}
                      className="mt-1 h-4 w-4 text-yellow-500 focus:ring-yellow-400"
                    />
                    <span className="flex-1">
                      <span className="block font-semibold text-gray-900">{domain.fullDomain}</span>
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{sourceLabel}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        );
      }

      case 'url-list':
        const urls = fieldValue || [''];
        const canAddMoreUrls = urls.length < (field.maxUrls || 4);

        return (
          <div className="space-y-4">
            {urls.map((url, index) => (
              <div key={index} className="rounded-[3px] border border-gray-200 bg-white p-3 sm:p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-sm font-medium">{index + 1}</div>
                  {urls.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => {
                        const newUrls = urls.filter((_, i) => i !== index);
                        handleInputChange(field.id, newUrls.length ? newUrls : ['']);
                      }}
                      className="inline-flex items-center gap-1 rounded-[3px] border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 transition-all duration-300 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Remove website URL ${index + 1}`}
                    >
                      Remove
                    </button>
                  ) : (
                    <span className="text-xs font-medium text-gray-400">Optional</span>
                  )}
                </div>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => {
                    const newUrls = [...urls];
                    newUrls[index] = e.target.value;
                    while (newUrls.length > 1 && newUrls[newUrls.length - 1] === '') {
                      newUrls.pop();
                    }
                    handleInputChange(field.id, newUrls);
                  }}
                  placeholder={`Website URL ${index + 1} (optional)`}
                  className="w-full min-w-0 rounded-[3px] px-4 py-3 border border-gray-300 focus:ring-yellow-400 focus:border-yellow-500 transition-all"
                />
              </div>
            ))}
            {canAddMoreUrls && urls[urls.length - 1] !== '' && (
              <button
                type="button"
                onClick={() => {
                  const newUrls = [...urls, ''];
                  handleInputChange(field.id, newUrls);
                }}
                className="inline-flex items-center text-yellow-600 hover:text-yellow-700 text-sm font-medium transition-all px-1"
              >
                <span className="mr-2 w-5 h-5 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center text-base leading-none">+</span>
                Add another website
              </button>
            )}
            <div className="text-sm text-gray-500 px-1">{urls.filter(u => u.trim()).length}/{field.maxUrls || 4} websites added</div>
          </div>
        );

      case 'file':
        return (
          <div>
            <input
              type="file"
              id={field.id}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleInputChange(field.id, {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    file: file
                  });
                } else {
                  handleInputChange(field.id, null);
                }
              }}
              accept={field.accept}
              className="w-full px-4 py-3 border border-gray-300 focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 transition-all file:mr-4 file:py-2 file:px-4 file:style={{ borderRadius: '3px' }} file:border-0 file:text-sm file:font-medium file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
            />
            {fieldValue && (
              <div className="mt-2 text-sm text-gray-600">
                Selected: {fieldValue.name} ({Math.round(fieldValue.size / 1024)}KB)
              </div>
            )}
          </div>
        );

      case 'checkbox-single':
        return (
          <div>
            <label className="flex items-start gap-4 rounded-[3px] border border-gray-200 hover:bg-gray-50 cursor-pointer transition-all bg-stone-100 p-5">
              <input
                type="checkbox"
                value="separate"
                checked={fieldValue && fieldValue.includes('separate')}
                onChange={(e) => handleCheckboxChange(field.id, 'separate', e.target.checked)}
                className="text-yellow-600 flex-shrink-0 mt-0.5 w-5 h-5 sm:w-5 sm:h-5"
                style={{ accentColor: '#EAB308' }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-base sm:text-lg font-medium text-gray-900 mb-2 leading-snug">{field.label}</div>
                {field.helpText && (
                  <div className="text-sm sm:text-base text-gray-600 leading-relaxed">{field.helpText}</div>
                )}
              </div>
            </label>
            {error && (
              <p className="mt-4 sm:mt-3 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );


      case 'newsletter-checkbox':
        return (
          <div className="space-y-6">
            {field.options.map(option => (
              <label key={option.value} className={`flex items-start gap-4 rounded-[3px] border cursor-pointer transition-all duration-300 hover:shadow-md p-5 ${
                option.recommended ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-stone-100 hover:bg-gray-50'
              } ${
                fieldValue && fieldValue.includes(option.value)
                  ? option.recommended ? 'border-yellow-500 bg-yellow-100 shadow-md' : 'border-yellow-400 bg-yellow-50 shadow-md'
                  : ''
              }`}>
                <input
                  type="checkbox"
                  value={option.value}
                  checked={fieldValue && fieldValue.includes(option.value)}
                  onChange={(e) => handleCheckboxChange(field.id, option.value, e.target.checked)}
                  className="text-yellow-600 flex-shrink-0 mt-1 w-5 h-5"
                  style={{ accentColor: '#EAB308' }}
                />
                <div className="flex-1 min-w-0">
                  <div className={`text-lg font-semibold mb-2 leading-snug ${
                    option.recommended ? 'text-yellow-800' : 'text-gray-900'
                  }`}>
                    {option.label}
                    {option.recommended && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800">
                        Recommended
                      </span>
                    )}
                  </div>
                  <div className={`text-sm leading-relaxed ${
                    option.recommended ? 'text-yellow-700' : 'text-gray-600'
                  }`}>
                    {option.description}
                  </div>
                </div>
              </label>
            ))}

            {error && (
              <p className="text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        );

      case 'checkbox': {
        const totalAddons = field.options.length;

        const handleAdvance = (index, { scroll = true } = {}) => {
          if (index >= totalAddons - 1) return;
          shouldAutoScrollRef.current = scroll;
          setActiveFeatureIndex(prev => {
            const nextIndex = index + 1;
            return nextIndex > prev ? nextIndex : prev;
          });
        };

        const handleMarkerClick = (index) => {
          if (index > activeFeatureIndex) return;
          setActiveFeatureIndex(index);
        };

        return (
          <div className="space-y-6">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {field.options.map((option, index) => {
                  const statusClass = index === activeFeatureIndex
                    ? 'bg-yellow-500'
                    : index < activeFeatureIndex
                      ? 'bg-gray-500'
                      : 'bg-gray-200';

                  const isClickable = index <= activeFeatureIndex;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleMarkerClick(index)}
                      disabled={!isClickable}
                      aria-current={index === activeFeatureIndex ? 'step' : undefined}
                      className={`flex min-h-[44px] flex-1 items-center rounded-[3px] bg-transparent transition-opacity ${
                        isClickable ? 'hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500' : 'cursor-not-allowed'
                      }`}
                    >
                      <span aria-hidden="true" className={`block h-1.5 w-full rounded-full ${statusClass}`} />
                      <span className="sr-only">Review {option.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-col gap-1 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                <span>Step {Math.min(activeFeatureIndex + 1, totalAddons)} of {totalAddons}</span>
                <span>Tap a completed marker to revisit or adjust your choice.</span>
              </div>
            </div>

            <div className="space-y-5">
              {field.options.map((option, optionIndex) => {
                const iconElement = option.icon
                  ? React.cloneElement(option.icon, { className: 'h-7 w-7 text-yellow-600' })
                  : null;
                const isActive = optionIndex === activeFeatureIndex;
                const isLocked = optionIndex > activeFeatureIndex;

                const renderCard = (content, { scrollToNextOnContinue = true } = {}) => (
                  <div
                    key={option.value}
                    ref={(el) => {
                      featureRefs.current[optionIndex] = el;
                    }}
                    className={`relative rounded-[3px] border p-6 shadow-sm transition ${
                      isActive
                        ? 'border-yellow-400 bg-white'
                        : 'border-gray-200 bg-stone-50'
                    }`}
                  >
                    <div className={isLocked ? 'pointer-events-none opacity-40' : 'opacity-100'}>
                      {content}

                      {isActive && optionIndex < totalAddons - 1 && (
                        <div className="mt-5 border-t border-gray-200 pt-4">
                          <button
                            type="button"
                            onClick={() => handleAdvance(optionIndex, { scroll: scrollToNextOnContinue })}
                            className="inline-flex items-center justify-center gap-2 rounded-[3px] bg-yellow-500 px-4 py-2 text-base font-semibold text-gray-900 shadow-sm transition hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                          >
                            Continue to next add-on
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    {isLocked && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-[3px] bg-white/85 text-sm font-medium text-gray-600">
                        Make your choice above to unlock this add-on.
                      </div>
                    )}
                  </div>
                );

                if (option.value === 'logo-design') {
                  const noneSelected = !fieldValue || (!fieldValue.includes(option.value) && !fieldValue.includes(`${option.value}-bespoke`));
                  const standardSelected = fieldValue && fieldValue.includes(option.value) && !fieldValue.includes(`${option.value}-bespoke`);
                  const bespokeSelected = fieldValue && fieldValue.includes(`${option.value}-bespoke`);

                  const cardContent = (
                    <div className="flex h-full flex-col gap-5">
                      <div className="flex items-start gap-4">
                        {iconElement && (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                            {iconElement}
                          </div>
                        )}
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-900">{option.label}</h3>
                            {option.popular && (
                              <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800">
                                Popular
                              </span>
                            )}
                          </div>
                          <p className="text-base text-gray-600">Choose from a customised template logo or one designed by a graphic designer.</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label
                          className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                            noneSelected
                              ? 'border-yellow-300 bg-yellow-50/70 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                          }`}
                          onClick={() => {
                            if (noneSelected) {
                              handleAdvance(optionIndex);
                            }
                          }}
                        >
                          <input
                            type="radio"
                            name={option.value}
                            checked={noneSelected}
                            onChange={() => {
                              const newValues = (fieldValue || []).filter(v => !v.startsWith(option.value));
                              handleInputChange(field.id, newValues);
                              handleAdvance(optionIndex);
                            }}
                            className="mt-1 text-yellow-600"
                            style={{ accentColor: '#EAB308' }}
                          />
                          <div className="flex-1 text-left">
                            <span className="font-medium text-gray-800">I already have a logo or don’t need logo design</span>
                          </div>
                        </label>

                        <label
                          className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                            standardSelected
                              ? 'border-yellow-300 bg-yellow-50/70 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name={option.value}
                            checked={standardSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newValues = (fieldValue || []).filter(v => !v.startsWith(option.value));
                                newValues.push(option.value);
                                handleInputChange(field.id, newValues);
                                handleAdvance(optionIndex);
                              }
                            }}
                            className="mt-1 text-yellow-600"
                            style={{ accentColor: '#EAB308' }}
                          />
                          <div className="flex w-full items-start justify-between gap-3">
                            <div className="text-left">
                              <span className="font-medium text-gray-900">Business Logo Design</span>
                              <p className="text-sm text-gray-500">Professional logo design for your business.</p>
                            </div>
                            <span className="text-sm font-semibold text-yellow-600 whitespace-nowrap">£65</span>
                          </div>
                        </label>

                        <label
                          className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                            bespokeSelected
                              ? 'border-yellow-400 bg-yellow-50 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name={option.value}
                            checked={bespokeSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newValues = (fieldValue || []).filter(v => !v.startsWith(option.value));
                                newValues.push(`${option.value}-bespoke`);
                                handleInputChange(field.id, newValues);
                                handleAdvance(optionIndex);
                              }
                            }}
                            className="mt-1 text-yellow-600"
                            style={{ accentColor: '#EAB308' }}
                          />
                          <div className="flex w-full items-start justify-between gap-3">
                            <div className="text-left">
                              <span className="font-medium text-gray-900">Bespoke Logo Design</span>
                              <p className="text-sm text-gray-500">Premium custom concept with revision rounds, plus Brand Guidelines.</p>
                            </div>
                            <span className="text-sm font-semibold text-yellow-600 whitespace-nowrap">£499</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  );

                  return renderCard(cardContent);
                }

                

                if (option.value === 'social-setup') {
                  const noneSelected = !fieldValue || (!fieldValue.includes('social-setup-package-1') && !fieldValue.includes('social-setup-package-2'));
                  const package1Selected = fieldValue && fieldValue.includes('social-setup-package-1');
                  const package2Selected = fieldValue && fieldValue.includes('social-setup-package-2');

                  const cardContent = (
                    <div className="flex h-full flex-col gap-5">
                      <div className="flex items-start gap-4">
                        {iconElement && (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                            {iconElement}
                          </div>
                        )}
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold text-gray-900">{option.label}</h3>
                          <p className="text-base text-gray-600">We design social cover photos, optimise profile icon logos, and complete your social profile metadata.</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label
                          className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                            noneSelected
                              ? 'border-yellow-300 bg-yellow-50/70 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name={option.value}
                            checked={noneSelected}
                            onChange={() => {
                              const filteredValues = (fieldValue || []).filter(v => v !== 'social-setup-package-1' && v !== 'social-setup-package-2');
                              handleInputChange(field.id, filteredValues);
                              handleAdvance(optionIndex);
                            }}
                            className="mt-1 text-yellow-600"
                            style={{ accentColor: '#EAB308' }}
                          />
                          <div className="flex-1 text-left">
                            <span className="font-medium text-gray-800">No social package</span>
                          </div>
                        </label>

                        <label
                          className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                            package1Selected
                              ? 'border-yellow-400 bg-yellow-50 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name={option.value}
                            checked={package1Selected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newValues = (fieldValue || []).filter(v => v !== 'social-setup-package-1' && v !== 'social-setup-package-2');
                                newValues.push('social-setup-package-1');
                                handleInputChange(field.id, newValues);
                                handleAdvance(optionIndex);
                              }
                            }}
                            className="mt-1 text-yellow-600"
                            style={{ accentColor: '#EAB308' }}
                          />
                          <div className="flex w-full items-start justify-between gap-3">
                            <div className="text-left">
                              <span className="font-medium text-gray-900">Package 1: Facebook, LinkedIn, X (Twitter)</span>
                            </div>
                            <span className="text-sm font-semibold text-yellow-600 whitespace-nowrap">£165</span>
                          </div>
                        </label>

                        <label
                          className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                            package2Selected
                              ? 'border-yellow-400 bg-yellow-50 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name={option.value}
                            checked={package2Selected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newValues = (fieldValue || []).filter(v => v !== 'social-setup-package-1' && v !== 'social-setup-package-2');
                                newValues.push('social-setup-package-2');
                                handleInputChange(field.id, newValues);
                                handleAdvance(optionIndex);
                              }
                            }}
                            className="mt-1 text-yellow-600"
                            style={{ accentColor: '#EAB308' }}
                          />
                          <div className="flex w-full items-start justify-between gap-3">
                            <div className="text-left">
                              <span className="font-medium text-gray-900">Package 2: Facebook, LinkedIn, X (Twitter), Instagram, YouTube</span>
                            </div>
                            <span className="text-sm font-semibold text-yellow-600 whitespace-nowrap">£195</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  );

                  return renderCard(cardContent);
                }

                if (option.value === 'industry-addons') {
                  const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
                  const currentPlan = formData.websitePlan;
                  const currentPlanName = PLAN_DISPLAY_NAMES[currentPlan] || 'your';

                  const cardContent = (
                    <div className="flex h-full flex-col gap-5">
                      <div className="flex items-start gap-4">
                        {iconElement && (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                            {iconElement}
                          </div>
                        )}
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold text-gray-900">{option.label}</h3>
                          <p className="text-base text-gray-600">Tick any you need, or continue if none apply.</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {INDUSTRY_ADDONS.map((addon) => {
                          const allowed = isAddonAllowedOnPlan(addon, currentPlan);
                          const checked = allowed && selectedValues.includes(addon.value);
                          const upgradeName = PLAN_DISPLAY_NAMES[addon.upgradePlan] || 'Premium';
                          const inputId = `industry-addon-${addon.value}`;

                          return (
                            <div
                              key={addon.value}
                              className={`rounded-[3px] border p-3 transition-all duration-200 ${
                                checked
                                  ? 'border-yellow-400 bg-yellow-50 shadow-sm'
                                  : allowed
                                    ? 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                                    : 'border-gray-200 bg-gray-50'
                              }`}
                            >
                              <label htmlFor={inputId} className={`flex items-start gap-3 text-base text-left ${allowed ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                <input
                                  id={inputId}
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!allowed}
                                  aria-describedby={!allowed ? `${inputId}-plan-note` : undefined}
                                  onChange={(e) => {
                                    const others = selectedValues.filter(v => v !== addon.value);
                                    handleInputChange(field.id, e.target.checked ? [...others, addon.value] : others);
                                  }}
                                  className="mt-1 h-5 w-5 text-yellow-600"
                                  style={{ accentColor: '#EAB308' }}
                                />
                                <div className="flex w-full items-start justify-between gap-3">
                                  <div className="text-left">
                                    <span className={`font-medium ${allowed ? 'text-gray-900' : 'text-gray-500'}`}>{addon.label}</span>
                                    <p className="text-sm text-gray-500">{addon.description}</p>
                                  </div>
                                  <span className={`text-sm font-semibold whitespace-nowrap ${allowed ? 'text-yellow-600' : 'text-gray-400'}`}>{addon.priceText}</span>
                                </div>
                              </label>
                              {!allowed && (
                                <div id={`${inputId}-plan-note`} className="mt-3 flex flex-col gap-2 rounded-[3px] border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-gray-800 sm:flex-row sm:items-center sm:justify-between">
                                  <span>
                                    Not available on the {currentPlanName} plan. Switch to {addon.upgradePlan === 'premium' ? 'the Premium plan' : 'the Business or Premium plan'} to add it.
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleInputChange('websitePlan', addon.upgradePlan);
                                      handleInputChange(field.id, [...selectedValues.filter(v => v !== addon.value), addon.value]);
                                    }}
                                    className="inline-flex shrink-0 items-center justify-center rounded-[3px] bg-yellow-500 px-3 py-1.5 text-sm font-semibold text-gray-900 transition hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                                  >
                                    Switch to {upgradeName}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );

                  return renderCard(cardContent);
                }

                if (option.value === 'local-seo') {
                  const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
                  const withoutLocalSeo = selectedValues.filter(v => !getLocalSeoPlan(v));
                  const selectedSeoPlan = LOCAL_SEO_PLANS.find(seoPlan => selectedValues.includes(seoPlan.value));

                  const cardContent = (
                    <div className="flex h-full flex-col gap-5">
                      <div className="flex items-start gap-4">
                        {iconElement && (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                            {iconElement}
                          </div>
                        )}
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold text-gray-900">{option.label}</h3>
                          <p className="text-base text-gray-600">Help more local customers find you on Google. Billed monthly on top of your website plan.</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label
                          className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                            !selectedSeoPlan
                              ? 'border-yellow-300 bg-yellow-50/70 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name={option.value}
                            value="none"
                            checked={!selectedSeoPlan}
                            onChange={() => {
                              handleInputChange(field.id, withoutLocalSeo);
                              handleAdvance(optionIndex);
                            }}
                            className="mt-1 text-yellow-600"
                            style={{ accentColor: '#EAB308' }}
                          />
                          <div className="flex-1 text-left">
                            <span className="font-medium text-gray-800">No Local SEO for now</span>
                          </div>
                        </label>

                        {LOCAL_SEO_PLANS.map((seoPlan) => {
                          const checked = selectedSeoPlan?.value === seoPlan.value;
                          return (
                            <label
                              key={seoPlan.value}
                              className={`flex items-start gap-3 rounded-[3px] border p-3 text-base text-left transition-all duration-200 ${
                                checked
                                  ? 'border-yellow-400 bg-yellow-50 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
                              }`}
                            >
                              <input
                                type="radio"
                                name={option.value}
                                value={seoPlan.value}
                                checked={checked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    handleInputChange(field.id, [...withoutLocalSeo, seoPlan.value]);
                                    handleAdvance(optionIndex);
                                  }
                                }}
                                className="mt-1 text-yellow-600"
                                style={{ accentColor: '#EAB308' }}
                              />
                              <div className="flex w-full items-start justify-between gap-3">
                                <div className="text-left">
                                  <span className="font-medium text-gray-900">{seoPlan.name}</span>
                                  <p className="text-sm text-gray-500">{seoPlan.description}</p>
                                  <p className="mt-1 text-sm text-gray-500">Minimum {seoPlan.minimumTermMonths} months</p>
                                </div>
                                <span className="text-sm font-semibold text-yellow-600 whitespace-nowrap">{seoPlan.priceText}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>

                      <p className="text-sm text-gray-500">Your first month is paid upfront along with your website plan.</p>
                    </div>
                  );

                  return renderCard(cardContent);
                }

                return null;
              })}
            </div>

            <p className="text-sm text-gray-500">You can fine-tune any completed extra whenever you like.</p>
          </div>
        );
      }
      default:
        return null;
    }
  }

const renderSignatureStep = () => {
  switch (signatureStep) {
    case 'review':
      return (
        <div className="max-w-xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-[3px] p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Getting your agreement ready</h3>
            <p className="text-sm text-gray-600 mb-4">
              We&rsquo;re preparing your agreement and will open the signing window automatically.
            </p>
            <button
              onClick={createSignatureDocument}
              className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-3 rounded-[3px] font-medium transition-all"
            >
              <CheckCircle className="w-4 h-4" />
              Open agreement now
            </button>
          </div>
        </div>
      );

    case 'creating':
      return (
        <div className="max-w-md mx-auto text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-6">
            <svg className="w-8 h-8 text-yellow-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Preparing your agreement
          </h3>
          <p className="text-gray-600">
            Please wait while we get everything ready for you...
          </p>
        </div>
      );

    case 'signing':
      return (
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Signing Action */}
          <div className="bg-green-50 border border-green-200 rounded-[3px] p-6 text-center">
            <div className="flex items-center justify-center gap-2 text-green-700 mb-4">
              <CheckCircle className="w-6 h-6" />
              <h3 className="text-lg font-semibold">Agreement ready</h3>
            </div>

            <p className="text-green-800 mb-4">
              Your agreement is ready. Please open it, scroll to the bottom, and sign.
            </p>
            <button
              type="button"
              onClick={openSignatureModal}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-[3px] font-medium transition-all mb-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open agreement
            </button>

            <div className="grid grid-cols-1 gap-3 text-left sm:grid-cols-3">
              {[
                { step: '1', title: 'Open your agreement', text: 'Use the button above to open the secure signing window.' },
                { step: '2', title: 'Scroll to the bottom', text: 'Read through the agreement and sign at the bottom of the page.' },
                { step: '3', title: 'We will check for you', text: 'Come back here afterwards — we will usually detect it automatically.' }
              ].map((item) => (
                <div key={item.step} className="rounded-[3px] border border-green-200 bg-white/80 p-4">
                  <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-sm font-semibold text-white">
                    {item.step}
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Status Check */}
          <div className="bg-white border border-gray-200 rounded-[3px] p-6 text-center">
            <p className="text-gray-600 mb-2 text-sm">
              We will usually spot your signature automatically.
            </p>
            <p className="text-gray-600 mb-4 text-sm">
              If you have just signed, you can also check now using the button below.
            </p>

            <button
              onClick={() => checkSignatureStatus()}
              disabled={manualCheckCooldown > 0}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-[3px] text-sm font-medium transition-all ${
                manualCheckCooldown > 0
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {manualCheckCooldown > 0 ? `Wait ${manualCheckCooldown}s` : "I've signed — check now"}
            </button>
          </div>
        </div>
      );

    case 'completed':
      return (
        <div className="max-w-md mx-auto text-center py-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Agreement signed
          </h3>
          <p className="text-gray-600 mb-6">
            {isAutoSubmittingAfterSignature
              ? "Your agreement has been signed successfully. We're now submitting your registration automatically."
              : 'Your agreement has been signed successfully.'}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-[3px] p-4">
            <p className="text-green-800 text-sm font-medium">
              Reference: {formData.submissionId}
            </p>
          </div>
        </div>
      );

    case 'error':
      return (
        <div className="max-w-md mx-auto text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
            <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Signature Error
          </h3>
          <p className="text-gray-600 mb-6">
            {signatureData.error || 'There was an issue creating your signature document. Please try again.'}
          </p>
          <button
            onClick={() => { hasAutoStartedSignatureRef.current = false; setSignatureStep('review'); }}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-[3px] font-medium transition-all"
          >
            Try Again
          </button>
        </div>
      );

    default:
      return null;
  }
};

const renderCompletionStep = () => {
  return (
    <div className="max-w-3xl mx-auto py-12 text-left">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <h3 className="text-3xl font-bold text-gray-900 mb-4">
        Website Plan Registration Complete
      </h3>
      <p className="text-lg text-gray-600 mb-4">
        Thank you for choosing Ubie Websites! Your website registration has been submitted successfully.
      </p>
      <p className="text-base text-gray-600 mb-6">
        Our onboarding team is reviewing your details now. If approved, we’ll email your website agreement to sign online, followed by your first payment and Direct Debit set-up.
      </p>

      <div className="bg-green-50 border border-green-200 rounded-[3px] p-6 mb-8">
        <h4 className="text-lg font-semibold text-green-800 mb-3">What happens next?</h4>
        <ul className="text-left text-green-700 space-y-2">
          <li>• Our team will review your application and prepare the next steps within 1–2 business days</li>
              <li>• Once approved, we’ll email your website agreement to sign online, then your first monthly payment request and Direct Debit form, so we can start building</li>
          <li>• Your monthly website billing starts once you receive your first website draft, and is billed upfront each month</li>
        </ul>
      </div>

      {formData.submissionId && (
        <div className="bg-gray-50 border border-gray-200 rounded-[3px] p-4 mb-6">
          <p className="text-sm font-medium text-gray-700">Your reference number:</p>
          <p className="text-lg font-mono text-gray-900">{formData.submissionId}</p>
        </div>
      )}

      <div className="space-y-4">
        <p className="text-gray-600">
          Questions? Contact us at{' '}
          <a href="mailto:support@ubiewebsites.co.uk" className="text-yellow-600 hover:text-yellow-700 font-medium">
            support@ubiewebsites.co.uk
          </a>
          {' '}or call{' '}
          <a href="tel:03338808832" className="text-yellow-600 hover:text-yellow-700 font-medium">
            0333 880 8832
          </a>
        </p>

        <button
          onClick={() => window.location.href = 'https://ubiewebsites.co.uk'}
          className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-[3px] font-medium transition-all"
        >
          Return to Ubie Websites
        </button>
      </div>
    </div>
  );
};

  const promoStatus = getPromoStatus();

  const applyDiscountCode = () => {
    const normalizedCode = String(discountCodeInput || '').trim().toUpperCase();
    const promo = getPromoStatus();

    if (!normalizedCode) {
      setDiscountStatusMessage({ type: 'error', text: 'Please enter a discount code.' });
      return;
    }

    if (normalizedCode !== promo.code) {
      setDiscountStatusMessage({ type: 'error', text: 'That discount code is not recognised.' });
      return;
    }

    if (promo.isExpired) {
      setDiscountStatusMessage({
        type: 'error',
        text: `This code expired on ${promo.displayExpiryDate} (${promo.timezoneLabel}).`
      });
      return;
    }

    handleInputChange('discountCode', promo.code);
    setDiscountCodeInput(promo.code);
    setDiscountStatusMessage({ type: 'success', text: `${promo.code} applied: ${promo.discountPercent}% off your website plan monthly fee.` });
  };

  const removeDiscountCode = () => {
    setDiscountCodeInput('');
    handleInputChange('discountCode', '');
    setDiscountStatusMessage({ type: '', text: '' });
  };

 const renderSummary = () => {
  const websitePlan = formSteps[0].fields[0].options.find(opt => opt.value === formData.websitePlan);
  const newsletterOptions = formSteps.find(s => s.id === 'newsletter-signup')?.fields.find(f => f.id === 'newsletterSubscriptions')?.options || [];
  const selectedNewsletterLabels = (formData.newsletterSubscriptions || []).map((subscription) => newsletterOptions.find(o => o.value === subscription)?.label).filter(Boolean);
  const selectedLocalSeoPlan = LOCAL_SEO_PLANS.find((seoPlan) => (formData.features || []).includes(seoPlan.value));
  const summaryRowClass = 'review-detail-line';
  const summaryChangeButtonClass = 'review-change';
  const toggleSummaryCard = (cardId) => {
    setExpandedSummaryCards((prev) => prev[cardId] ? {} : { [cardId]: true });
  };

  const renderSummaryCard = ({ cardId, title, summary, onChange, details }) => {
    const expanded = Boolean(expandedSummaryCards[cardId]);
    const detailsId = `review-details-${cardId}`;
    return (
      <section className="registration-summary-card review-detail-card">
        <div className="review-detail-header">
          <button
            type="button"
            className="review-detail-toggle"
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={() => toggleSummaryCard(cardId)}
          >
            <span className="review-detail-copy">
              <span className="review-detail-title">{title}</span>
              <span className="review-detail-summary">{summary}</span>
            </span>
            <span className={`review-chevron ${expanded ? 'is-open' : ''}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onChange}
            disabled={isBackNavigationLocked}
            className={summaryChangeButtonClass}
            aria-label={`Change ${title}`}
          >
            Change
          </button>
        </div>
        <div id={detailsId} hidden={!expanded} className="review-detail-content">
          {details}
        </div>
      </section>
    );
  };

  return (
    <div className="registration-review">
      <div className="review-group-heading">
        <h3 className="font-semibold text-gray-900">Your plan</h3>
        <p className="text-sm text-gray-500">Confirm your selected package before signing.</p>
      </div>
      <div className="review-plan">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">{websitePlan?.label}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{websitePlan?.pages} • {websitePlan?.emails}</p>
          </div>
          <button
            onClick={() => navigateToStep('website-plan')}
            disabled={isBackNavigationLocked}
            className={summaryChangeButtonClass}
          >
            Change Plan
          </button>
        </div>
      </div>

      <div className="review-pricing">
        <h3 className="review-section-title">Price summary</h3>
        <div className="review-price-grid">
          <div className="review-monthly">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Monthly cost</h4>
            <div className="text-2xl sm:text-3xl font-semibold text-yellow-700 mb-1">£{calculations.monthlyPrice}</div>
            {(calculations.monthlyAddOnItems ?? []).map((item) => (
              <div key={item.id} className="mt-1 text-xs text-gray-600">
                Includes {item.title} at £{item.amount}/month
              </div>
            ))}
            {calculations.discountApplied && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-semibold">{calculations.discountCode}</span> saved £{calculations.websitePlanDiscountAmount} / month
              </div>
            )}
          </div>
          <div className="review-price-secondary">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Upfront before work starts</h4>
            <div className="text-sm sm:text-base font-semibold text-gray-700 leading-relaxed">1 monthly payment due before work commences</div>
          </div>
          <div className="review-price-secondary">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">One-off add-ons</h4>
            <div className="text-2xl sm:text-3xl font-semibold text-yellow-700 mb-1">£{calculations.oneOffAddonsTotal ?? 0}</div>
          </div>
        </div>
        {domainAnnualCost > 0 && (
          <div className="review-annual">
            <span>Additional web addresses</span>
            <strong>{GBP_CURRENCY_FORMATTER.format(domainAnnualCost)}/year extra</strong>
          </div>
        )}
        <div className="review-discount">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-800">Discount code</p>
              <p className="text-xs text-gray-500">Applies to website plan monthly fee only (not one-off costs).</p>
            </div>
            <button
              type="button"
              onClick={() => setShowDiscountCodeInput((prev) => !prev)}
              className="inline-flex rounded-[3px] border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
            >
              {showDiscountCodeInput ? 'Hide discount code' : 'Have a discount code?'}
            </button>
          </div>
          {showDiscountCodeInput && (
            <div className="mt-3 space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={discountCodeInput}
                  onChange={(event) => setDiscountCodeInput(event.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="w-full rounded-[3px] border border-gray-300 px-3 py-2 text-sm uppercase tracking-wide focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-200"
                  aria-label="Discount code"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={applyDiscountCode}
                    className="rounded-[3px] bg-yellow-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-yellow-600"
                  >
                    Apply
                  </button>
                  {(formData.discountCode || calculations.discountApplied) && (
                    <button
                      type="button"
                      onClick={removeDiscountCode}
                      className="rounded-[3px] border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {discountStatusMessage.text && (
                <div className={`rounded-[3px] border px-3 py-2 text-xs sm:text-sm ${
                  discountStatusMessage.type === 'success'
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}>
                  {discountStatusMessage.text}
                </div>
              )}
              {promoStatus.isExpired && (
                <p className="text-xs text-red-700">
                  Code {promoStatus.code} expired on {promoStatus.displayExpiryDate} ({promoStatus.timezoneLabel}).
                </p>
              )}
            </div>
          )}
        </div>
        <div className="review-billing-toggle">
          <DisclosureToggleButton
            isOpen={showPricingDetails}
            closedLabel="Show billing details"
            openLabel="Hide billing details"
            onClick={() => setShowPricingDetails((prev) => !prev)}
          />
        </div>
        {showPricingDetails && (
          <div className="review-billing-details text-sm text-gray-700 space-y-2">
            <p>• Monthly plans have a minimum 12-month term.</p>
            <p>• After the initial term, your plan continues on a rolling monthly basis unless cancelled in line with your Terms of Service.</p>
            <p>• 1 monthly payment is due upfront before work commences.</p>
            <p>• Ongoing monthly payments are billed upfront each month.</p>
          </div>
        )}
      </div>

      <div className="review-group-heading">
        <h3 className="font-semibold text-gray-900">Your details</h3>
        <p className="text-sm text-gray-500">Review each section and use Change to correct anything.</p>
      </div>
      <div className="review-details-list">
        {renderSummaryCard({
          cardId: 'web-address',
          title: 'Web address',
          summary: formData.domainOption === 'free-domain'
            ? selectedDomains.length > 0
              ? `${selectedDomains.length} selected${domainAnnualCost > 0 ? ` • ${GBP_CURRENCY_FORMATTER.format(domainAnnualCost)}/year extra` : ''}`
              : 'No web address selected yet'
            : formData.domainOption === 'existing-domain'
            ? (formData.existingDomainFull || 'Existing web address')
            : 'Not specified',
          onChange: () => navigateToFirstAvailableStep(['domain-details', 'web-address']),
          details: (
            <div className="space-y-2">
              {selectedDomains.length > 0 ? selectedDomains.map((domain) => {
                const config = DOMAIN_EXTENSION_CONFIG[domain.extension];
                const isFreeDomain = config?.freeEligible && freeDomainSelection === domain.fullDomain;
                return (
                  <div key={domain.fullDomain} className={`${summaryRowClass} flex items-center justify-between gap-2`}>
                    <span className="font-medium break-all">{domain.fullDomain}</span>
                    <span className={isFreeDomain ? 'text-green-600 text-sm' : 'text-gray-600 text-sm'}>
                      {isFreeDomain ? 'Included with plan' : config?.price != null ? `${GBP_CURRENCY_FORMATTER.format(config.price)} per year` : 'Pricing to be confirmed'}
                    </span>
                  </div>
                );
              }) : formData.domainOption === 'existing-domain' ? <div className={summaryRowClass}>{formData.existingDomainFull}</div> : <div className={summaryRowClass}>Add an available web address to continue.</div>}
            </div>
          ),
        })}

        {renderSummaryCard({
          cardId: 'contact-information',
          title: 'Contact Information',
          summary: `${formData.firstName || ''} ${formData.lastName || ''} • ${formData.email || 'No email added'}`.trim(),
          onChange: () => navigateToStep('contact-information'),
          details: (
            <>
              <div className={summaryRowClass}>{formData.phone || 'No phone added'}</div>
              {formData.separateBusinessPhone && formData.separateBusinessPhone.includes('separate') && formData.businessPhone && (
                <div className={summaryRowClass}>Business contact number: {formData.businessPhone}</div>
              )}
            </>
          ),
        })}

        {formData.businessName && renderSummaryCard({
          cardId: 'business-information',
          title: 'Business Information',
          summary: formData.businessName,
          onChange: () => navigateToStep('about-business'),
          details: (
            <>
              {formData.businessCategory && (
                <div className={summaryRowClass}>
                  {formData.businessCategory === 'other' ? formData.businessCategoryOther : formSteps.find(s => s.id === 'about-business')?.fields.find(f => f.id === 'businessCategory')?.options.find(o => o.value === formData.businessCategory)?.label}
                </div>
              )}
              {formData.businessType && (
                <div className={summaryRowClass}>
                  {formSteps.find(s => s.id === 'about-business')?.fields.find(f => f.id === 'businessType')?.options.find(o => o.value === formData.businessType)?.label}
                </div>
              )}
            </>
          ),
        })}

        {renderSummaryCard({
          cardId: 'address-details',
          title: 'Address Details',
          summary: `${formData.invoiceAddressLine1 || ''}${formData.invoicePostcode ? `, ${formData.invoicePostcode}` : ''}` || 'No address provided',
          onChange: () => navigateToStep('address-website-display'),
          details: (
            <>
              <div className={summaryRowClass}>
                {formData.invoiceAddressLine1}<br />
                {formData.invoiceAddressLine2 && <>{formData.invoiceAddressLine2}<br /></>}
                {formData.invoiceCity}, {formData.invoiceCounty}<br />
                {formData.invoicePostcode}
              </div>
              {formData.separateBusinessAddress && formData.separateBusinessAddress.includes('separate') && formData.businessAddressLine1 && (
                <div className={summaryRowClass}>
                  {formData.businessAddressLine1}<br />
                  {formData.businessAddressLine2 && <>{formData.businessAddressLine2}<br /></>}
                  {formData.businessCity}, {formData.businessCounty}<br />
                  {formData.businessPostcode}
                </div>
              )}
            </>
          ),
        })}

        {renderSummaryCard({
          cardId: 'newsletter-subscriptions',
          title: 'Newsletter Subscriptions',
          summary: selectedNewsletterLabels.length > 0 ? `${selectedNewsletterLabels.length} selected` : 'None selected',
          onChange: () => navigateToStep('newsletter-signup'),
          details: (
            selectedNewsletterLabels.length > 0
              ? selectedNewsletterLabels.map((label, index) => <div key={index} className={summaryRowClass}>{label}</div>)
              : <div className={summaryRowClass}>No newsletter subscriptions selected</div>
          ),
        })}
      </div>

      <div className="review-extras">
      {formData.features && formData.features.length > 0 && renderSummaryCard({
        cardId: 'additional-services',
        title: 'Additional Services',
        summary: `${formData.features.length} selected`,
        onChange: () => navigateToStep('enhance-website'),
        details: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {formData.features.map((feature, index) => {
              let serviceName = '';
              let servicePrice = '';
              if (feature === 'logo-design') { serviceName = 'Business Logo Design'; servicePrice = '£65'; }
              else if (feature === 'logo-design-bespoke') { serviceName = 'Bespoke Logo Design'; servicePrice = '£499'; }
              else if (feature === 'business-card') { serviceName = 'Business Card Design (Single Sided)'; servicePrice = '£85'; }
              else if (feature === 'business-card-print') { serviceName = 'Business Card Design + Print 500 (Single Sided)'; servicePrice = '£110'; }
              else if (feature === 'business-card-double') { serviceName = 'Business Card Design (Double Sided)'; servicePrice = '£125'; }
              else if (feature === 'business-card-double-print') { serviceName = 'Business Card Design + Print 500 (Double Sided)'; servicePrice = '£160'; }
              else if (feature === 'social-setup-package-1') { serviceName = 'Social Media Setup + Branding - Package 1'; servicePrice = '£165'; }
              else if (feature === 'social-setup-package-2') { serviceName = 'Social Media Setup + Branding - Package 2'; servicePrice = '£195'; }
              else if (getPricedAddon(feature)) { serviceName = getPricedAddon(feature).label; servicePrice = getPricedAddon(feature).priceText; }
              if (!serviceName) return null;
              return <div key={index} className={`${summaryRowClass} flex items-start justify-between gap-3`}><span>{serviceName}</span><span className="font-medium text-yellow-700">{servicePrice}</span></div>;
            })}
          </div>
        ),
      })}

      </div>

      <div className="review-agreements space-y-3">
        <h3 className="font-semibold text-gray-900">Before submitting</h3>
        <div className="review-key-terms text-sm text-gray-700 space-y-1">
          <p className="font-medium text-gray-900">Key terms at a glance</p>
          <ul className="review-terms-list">
          <li>12-month minimum term, then rolling monthly, and the website is yours to keep.</li>
          {selectedLocalSeoPlan && (
            <li>{selectedLocalSeoPlan.label}: {selectedLocalSeoPlan.minimumTermMonths}-month minimum term, then rolling monthly.</li>
          )}
          <li>1 monthly payment is due upfront before work commences.</li>
          <li>Ongoing monthly payments are billed upfront.</li>
          <li>If approved, we will email your agreement to sign online, then your first payment and Direct Debit set-up.</li>
          </ul>
        </div>
        <div className="review-consents">
        <label className={`flex items-start gap-3 cursor-pointer rounded-[3px] border p-3 ${summaryValidationAttempted && !formData.domainAgreement ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <input
            type="checkbox"
            checked={formData.domainAgreement || false}
            onChange={(e) => handleInputChange('domainAgreement', e.target.checked)}
            className="mt-1 w-5 h-5 text-yellow-600 flex-shrink-0"
            required
          />
          <div className="text-sm text-gray-700 leading-relaxed">
            <strong>Web Address Registration Agreement:</strong> I understand that web addresses selected through Ubie Websites are not guaranteed until registration is approved and payment is confirmed during onboarding.
          </div>
        </label>
        <label className={`flex items-start gap-3 cursor-pointer rounded-[3px] border p-3 ${summaryValidationAttempted && !formData.termsAgreement ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <input
            type="checkbox"
            checked={formData.termsAgreement || false}
            onChange={(e) => handleInputChange('termsAgreement', e.target.checked)}
            className="mt-1 w-5 h-5 text-yellow-600 flex-shrink-0"
            required
          />
          <div className="text-sm text-gray-700 leading-relaxed">
            <strong>Terms of Service:</strong> I agree to Ubie Websites' <button type="button" onClick={(event) => { event.preventDefault(); event.stopPropagation(); setIsTermsModalOpen(true); }} className="font-semibold text-yellow-700 underline hover:text-yellow-800">Terms of Service</button> and understand that if approved, an agreement will be sent for me to sign online.
          </div>
        </label>
        </div>
        {summaryValidationAttempted && (!formData.domainAgreement || !formData.termsAgreement) && (
          <div className="rounded-[3px] border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <strong>{
              !formData.domainAgreement && !formData.termsAgreement
                ? 'Please confirm both boxes to continue.'
                : !formData.domainAgreement
                ? 'Please confirm the Web Address Registration Agreement.'
                : 'Please confirm Terms of Service.'
            }</strong>
          </div>
        )}
      </div>
    </div>
  );
};

  const currentStepData = formSteps[currentStep];
  const summaryAgreementsChecked = Boolean(formData.domainAgreement && formData.termsAgreement);
  const isSummaryStep = Boolean(currentStepData?.isSummary);
  const isSignatureStep = Boolean(currentStepData?.isSignature);
  const isCompletionStep = currentStep >= formSteps.length;
  const displayedStepIndex = Math.min(currentStep, formSteps.length - 1);
  const isDomainDetailsStep = currentStepData?.id === 'domain-details';
  const dynamicDomainDetailsTitle = formData.domainOption === 'existing-domain'
    ? 'Use your existing web address'
    : 'Choose your new web address';
  const dynamicDomainDetailsSubtitle = formData.domainOption === 'existing-domain'
    ? 'Tell us about your current web address and how you want to connect it.'
    : 'Search and choose your new web address with Ubie Websites.';
  const stepTitle = isCompletionStep
    ? 'Website Plan Registration Complete'
    : isDomainDetailsStep
    ? dynamicDomainDetailsTitle
    : currentStepData?.title;
  const stepSubtitle = isCompletionStep
    ? null
    : isDomainDetailsStep
    ? dynamicDomainDetailsSubtitle
    : currentStepData?.subtitle;
  const stepIndicatorValue = isCompletionStep ? '✓' : currentStep + 1;
  const progressBase = formSteps.length > 0 ? displayedStepIndex : 0;
  const rawProgress = formSteps.length > 1 ? (progressBase / (formSteps.length - 1)) * 100 : 100;
  const progressPercentage = Math.min(Math.max(rawProgress, 0), 100);
  const isNextButtonDisabled = (isSummaryStep && !summaryAgreementsChecked) || isTransitioningToSignature;
  const nextStepTitle = formSteps[currentStep + 1]?.title;
  const previousStepTitle = formSteps[currentStep - 1]?.title;
  const STEP_LABEL_SHORTCUTS = {
    'Choose a website plan': 'Plan',
    'What pages do you need?': 'Pages',
    'Your web address': 'Web address',
    'Choose your new web address': 'New web address',
    'Use your existing web address': 'Existing web address',
    'Choose your primary web address': 'Primary web address',
    'Professional email services': 'Email setup',
    'Current email setup': 'Current email',
    'New professional emails': 'New emails',
    'Tell us about your business': 'Business details',
    'About your business': 'Business',
    'How can we reach you?': 'Contact',
    'Quick verification': 'Verification',
    'Your address details': 'Address',
    'Complete your website': 'Add-ons',
    'Review your website plan': 'Review',
    'Sign your agreement': 'Signature'
  };

  const MOBILE_STEP_LABEL_SHORTCUTS = {
    ...STEP_LABEL_SHORTCUTS,
    'Choose your new web address': 'New address',
    'Use your existing web address': 'Existing address',
    'Choose your primary web address': 'Primary address',
    'Professional email services': 'Email',
    'Current email setup': 'Current email',
    'New professional emails': 'New emails',
    'Tell us about your business': 'Business',
  };

  const getStepDisplayTitle = (title = '') => {
    const cleanTitle = String(title || '').trim();
    if (!cleanTitle) return '';
    return STEP_LABEL_SHORTCUTS[cleanTitle] || cleanTitle;
  };

  const getMobileStepDisplayTitle = (title = '') => {
    const cleanTitle = String(title || '').trim();
    if (!cleanTitle) return '';
    return MOBILE_STEP_LABEL_SHORTCUTS[cleanTitle] || STEP_LABEL_SHORTCUTS[cleanTitle] || cleanTitle;
  };

  const buildCompactStepLabel = (label = '', maxLength = 24) => {
    const clean = String(label || '').trim();
    if (!clean) return '';
    if (clean.length <= maxLength) return clean;

    const words = clean.split(/\s+/).filter(Boolean);
    if (!words.length) return clean.slice(0, maxLength).trim();

    let compact = '';
    for (const word of words) {
      const candidate = compact ? `${compact} ${word}` : word;
      if (candidate.length > maxLength) break;
      compact = candidate;
      if (compact.length >= Math.max(10, maxLength - 6)) {
        break;
      }
    }

    return compact || words[0];
  };

  const shortenStepLabel = (title = '', prefix = 'Continue') => {
    const aliasedTitle = isMobileViewport ? getMobileStepDisplayTitle(title) : getStepDisplayTitle(title);
    if (!aliasedTitle) {
      return prefix === 'Back' ? 'Back' : 'Continue';
    }

    const maxLength = isMobileViewport ? 16 : 26;
    const compactTitle = buildCompactStepLabel(aliasedTitle, maxLength);

    if (isMobileViewport) {
      if (prefix === 'Back') {
        return compactTitle ? `Back: ${compactTitle}` : 'Back';
      }

      return compactTitle ? `Next: ${compactTitle}` : 'Next';
    }

    return `${prefix} to ${compactTitle}`;
  };

  const continueButtonLabel = reviewEditing && !isSummaryStep ? getStepDisplayTitle(formSteps.find(step => step.isSummary)?.title) : shortenStepLabel(nextStepTitle, 'Continue') || 'Continue';
  const backButtonLabel = shortenStepLabel(previousStepTitle, 'Back') || 'Back';
  const effectiveContinueButtonLabel = isTransitioningToSignature ? 'Submitting registration…' : (continueButtonLabel || 'Continue');

  const handleContinueAction = async () => {
    if (continueInFlightRef.current || isTransitioningToSignature) return;
    if (reviewEditing && !isSummaryStep) {
      const reviewIndex = formSteps.findIndex(step => step.isSummary);
      for (let index = 0; index < reviewIndex; index += 1) {
        if (!isStepValid(index)) { handleStepChange(index); return; }
      }
      setReviewEditing(false);
      handleStepChange(reviewIndex);
      return;
    }
    if (isSummaryStep) {
      if (!summaryAgreementsChecked) {
        setSummaryValidationAttempted(true);
        return;
      }

      continueInFlightRef.current = true;
      setSummaryValidationAttempted(false);
      setIsTransitioningToSignature(true);

      const submissionId = formData.submissionId || generateSubmissionId();
      if (!formData.submissionId) {
        setFormData((prev) => ({ ...prev, submissionId }));
      }

      try {
        const saveResult = await saveSubmissionSnapshot(submissionId);

        if (!saveResult?.success) {
          return;
        }

        handleStepChange(currentStep + 1);
      } finally {
        continueInFlightRef.current = false;
        setIsTransitioningToSignature(false);
      }
      return;
    }

    if (isStepValid(currentStep, { scrollToError: true })) {
      handleStepChange(currentStep + 1);
    }
  };

  const handleProgressNavigation = (targetIndex) => {
    if (isTransitioningToSignature) return;
    if (targetIndex > currentStep) {
      for (let index = 0; index < targetIndex; index += 1) {
        if (!isStepValid(index)) { handleStepChange(index); return; }
      }
    }
    if (isSummaryStep && targetIndex < currentStep) setReviewEditing(true);
    if (formSteps[targetIndex]?.isSummary) setReviewEditing(false);
    handleStepChange(targetIndex);
  };

  useEffect(() => {
    if (!isSummaryStep) {
      setSummaryValidationAttempted(false);
      return;
    }

    if (summaryAgreementsChecked) {
      setSummaryValidationAttempted(false);
    }
  }, [isSummaryStep, summaryAgreementsChecked]);

  useEffect(() => {
    if (!isSignatureStep) {
      hasAutoStartedSignatureRef.current = false;
      return;
    }

    if (signatureStep !== 'review') {
      return;
    }

    if (hasAutoStartedSignatureRef.current) {
      return;
    }

    hasAutoStartedSignatureRef.current = true;
    createSignatureDocument();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignatureStep, signatureStep]);


  useEffect(() => {
    if (isSignatureStep) {
      setIsTransitioningToSignature(false);
    }
  }, [isSignatureStep]);

  const showMobileStickyNavigation =
    showScrollIndicator &&
    isMobileViewport &&
    !keyboardOpen &&
    !isCompletionStep &&
    !isSignatureStep;

  const renderFormFields = () => {
    return (
      <div className="registration-fields space-y-10 sm:space-y-10">
        {currentStepData?.fields.map((field, index) => {
          if (field.conditionalDisplay && !field.conditionalDisplay(formData)) {
            return null;
          }

          if (field.id === 'separateBusinessAddress') {
            const showBusinessFields = formData.separateBusinessAddress && formData.separateBusinessAddress.includes('separate');
            
            return (
              <div key={field.id} className="space-y-10">
                <div>
                  <label className="flex items-start gap-4 p-5 border border-gray-200 rounded-[3px] hover:bg-gray-50 cursor-pointer transition-all bg-stone-100">
                    <input
                      type="checkbox"
                      value="separate"
                      checked={formData.separateBusinessAddress && formData.separateBusinessAddress.includes('separate')}
                      onChange={(e) => handleCheckboxChange(field.id, 'separate', e.target.checked)}
                      className="text-yellow-600 flex-shrink-0 mt-0.5 w-5 h-5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-base sm:text-lg font-medium text-gray-900 leading-snug mb-1">{field.label}</div>
                      {field.helpText && (
                        <div className="text-sm sm:text-base text-gray-600 leading-relaxed">{field.helpText}</div>
                      )}
                    </div>
                  </label>
                </div>
                
                {showBusinessFields && (
  <div className="ml-8 space-y-10" data-revealed-content="separateBusinessAddress">
                    <div>
                      <label className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                        Business Address - Street Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        ref={businessAddressRef}
                        value={formData.businessAddressLine1 || ''}
                        onChange={(e) => handleInputChange('businessAddressLine1', e.target.value)}
                        placeholder="Start typing your business address..."
                        className={`w-full px-4 py-3 border focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 transition-all ${
                          validationErrors.businessAddressLine1 ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      <div className="mt-1 text-xs text-gray-500">
  This address will be displayed on your website
</div>
                      {validationErrors.businessAddressLine1 && (
                        <p className="mt-1 text-sm text-red-600">
                          {validationErrors.businessAddressLine1}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                        Business Address Line 2
                      </label>
                      <input
                        type="text"
                        value={formData.businessAddressLine2 || ''}
                        onChange={(e) => handleInputChange('businessAddressLine2', e.target.value)}
                        placeholder="Flat, suite, etc. (optional)"
                        className="w-full px-4 py-3 border border-gray-300 style={{ borderRadius: '3px' }} focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 transition-all"
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-6">
                      <div className="col-span-2">
                        <label className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                          Business Town/City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.businessCity || ''}
                          onChange={(e) => handleInputChange('businessCity', e.target.value)}
                          placeholder="Enter business town or city"
                          className={`w-full px-4 py-3 border style={{ borderRadius: '3px' }} focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 transition-all ${
                            validationErrors.businessCity ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {validationErrors.businessCity && (
                          <p className="mt-1 text-sm text-red-600">
                            {validationErrors.businessCity}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                          Business County <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.businessCounty || ''}
                          onChange={(e) => handleInputChange('businessCounty', e.target.value)}
                          placeholder="County (optional)"
                          className="w-full px-4 py-3 border border-gray-300 style={{ borderRadius: '3px' }} focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 transition-all"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                        Business Postcode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.businessPostcode || ''}
                        onChange={(e) => handleInputChange('businessPostcode', e.target.value)}
                        placeholder="e.g. SW1A 1AA"
                        className={`w-full px-4 py-3 border style={{ borderRadius: '3px' }} focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 transition-all ${
                          validationErrors.businessPostcode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      {validationErrors.businessPostcode && (
                        <p className="mt-1 text-sm text-red-600">
                          {validationErrors.businessPostcode}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          if (field.id === 'separateBusinessPhone') {
            const showBusinessPhone = formData.separateBusinessPhone && formData.separateBusinessPhone.includes('separate');
            
            return (
              <div key={field.id} className="space-y-12">
                <div>
                  <label className="flex items-start gap-4 p-5 border border-gray-200 rounded-[3px] hover:bg-gray-50 cursor-pointer transition-all bg-stone-100">
                    <input
                      type="checkbox"
                      value="separate"
                      checked={formData.separateBusinessPhone && formData.separateBusinessPhone.includes('separate')}
                      onChange={(e) => handleCheckboxChange(field.id, 'separate', e.target.checked)}
                      className="text-yellow-600 flex-shrink-0 mt-0.5 w-5 h-5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-base sm:text-lg font-medium text-gray-900 leading-snug mb-1">{field.label}</div>
                      {field.helpText && (
                        <div className="text-sm sm:text-base text-gray-600 leading-relaxed">{field.helpText}</div>
                      )}
                    </div>
                  </label>
                </div>
                
                {showBusinessPhone && (
  <div className="ml-8" data-revealed-content="separateBusinessPhone">
                    <label htmlFor="businessPhone" className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                      Business Contact Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="businessPhone"
                      type="tel"
                      autoComplete="tel-national"
                      aria-invalid={Boolean(validationErrors.businessPhone)}
                      aria-describedby={validationErrors.businessPhone ? "businessPhone-error" : undefined}
                      onBlur={(event) => handleFieldBlur({ id: 'businessPhone', label: 'Business Contact Number' }, event.target.value)}
                      value={formData.businessPhone || ''}
                      onChange={(e) => handleInputChange('businessPhone', e.target.value)}
                      placeholder="Business number to display on your website"
                      style={{ borderRadius: '3px' }}
                      className={`w-full px-4 py-3 border focus:ring-yellow-400 focus:ring-yellow-400 focus:border-yellow-500 transition-all ${
                        validationErrors.businessPhone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    <div className="mt-1 text-xs text-gray-500">
                      This phone number will be displayed on your website
                    </div>
                    {validationErrors.businessPhone && (
                      <p id="businessPhone-error" role="status" className="mt-1 text-sm text-red-600">
                        {validationErrors.businessPhone}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          }

          if (field.width === 'half') {
            const nextField = currentStepData.fields[index + 1];
            const isNextHalf = nextField && nextField.width === 'half';
            const prevField = currentStepData.fields[index - 1];
            const wasPrevHalf = prevField && prevField.width === 'half';
            
            if (wasPrevHalf) return null;
            
            return (
              <div key={field.id} className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-6">
                <div className="space-y-2">
                  <label htmlFor={field.id} className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
                {isNextHalf && (
                  <div className="space-y-2">
                    <label htmlFor={nextField.id} className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                      {nextField.label}
                      {nextField.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderField(nextField)}
                  </div>
                )}
              </div>
            );
          }
          
          if (field.width === 'two-thirds') {
            const nextField = currentStepData.fields[index + 1];
            const isNextOneThird = nextField && nextField.width === 'one-third';
            const prevField = currentStepData.fields[index - 1];
            const wasPrevOneThird = prevField && prevField.width === 'two-thirds';
            
            if (wasPrevOneThird) return null;
            
            return (
              <div key={field.id} className="grid grid-cols-3 gap-6">
                <div className="col-span-2">
                  <label htmlFor={field.id} className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
                {isNextOneThird && (
                  <div className="space-y-2">
                    <label htmlFor={nextField.id} className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                      {nextField.label}
                      {nextField.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderField(nextField)}
                  </div>
                )}
              </div>
            );
          }
          
          const prevField = currentStepData.fields[index - 1];
          if ((prevField && prevField.width === 'half' && field.width === 'half') ||
              (prevField && prevField.width === 'two-thirds' && field.width === 'one-third')) {
            return null;
          }
          
          return (
  <div key={field.id} className="py-1 sm:py-2" {...(field.revealedContentId && { 'data-revealed-content': field.revealedContentId })}>
              {field.label && (
                <label htmlFor={field.id} className="block text-lg sm:text-lg font-medium text-gray-700 mb-4 sm:mb-3">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                  {field.helpText && (
                    <div className="mt-2 text-sm text-gray-500">
                      {field.helpText}
                    </div>
                  )}
                </label>
              )}
              {renderField(field)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-white font-mulish ${showMobileStickyNavigation ? "pb-24" : ""}`}>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Cabin:wght@400;500;600;700&family=Mulish:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      
      <style jsx>{`
        .font-cabin { font-family: 'Cabin', sans-serif; }
        .font-mulish { font-family: 'Mulish', sans-serif; }
        
        /* Yellow color scheme for form controls */
        input[type="radio"]:checked {
          background-color: #EAB308;
          border-color: #EAB308;
        }
        
        input[type="checkbox"]:checked {
          background-color: #EAB308;
          border-color: #EAB308;
        }
        
        input[type="radio"]:focus {
          ring-color: #EAB308;
          ring-opacity: 0.5;
        }
        
        input[type="checkbox"]:focus {
          ring-color: #EAB308;
          ring-opacity: 0.5;
        }
      `}</style>

      <header className="bg-[#0B0B0F] text-white py-[15px] sm:py-5">
        <div className="layout-container">
          <div className="flex items-center justify-between registration-header-row">
            <div className="registration-brand-wrap">
              <img
                src="https://ubiewebsites.co.uk/wp-content/uploads/2026/06/ubie-websites-pay-monthly-website-design-logo.svg"
                alt="Ubie Websites"
                width="325"
                height="52"
                className="registration-brand-logo"
              />
            </div>
            
            <div className="text-right">
              {/* Mobile - Phone icon only */}
              <a 
                href="tel:03338808832" 
                className="sm:hidden flex items-center justify-center w-9 h-9 rounded-full border-2 border-transparent bg-yellow-500 hover:bg-yellow-600 hover:bg-yellow-400 transition-all duration-300 group shadow-md"
                style={{ backgroundColor: '#F0BA1F' }}
                aria-label="Call 0333 880 8832"
              >
                <svg 
                  className="w-4 h-4 text-gray-900 transition-colors duration-300" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </a>
              
              {/* Desktop - Full phone number */}
              <a 
                href="tel:03338808832" 
                className="hidden sm:block text-xl font-medium font-mulish transition-all duration-300"
                style={{ color: '#fff' }}
                onMouseEnter={(e) => e.target.style.color = '#F0BA1F'}
                onMouseLeave={(e) => e.target.style.color = '#fff'}
              >
                0333 880 8832
              </a>
            </div>
          </div>
        </div>
      </header>
      <section className="registration-hero relative overflow-hidden bg-[#0B0B0F] text-white">
        <div className="registration-hero-circle" aria-hidden="true" />
        <div className="layout-container pt-12 pb-16 sm:pt-16 sm:pb-24 relative">
          <div className="max-w-lg sm:max-w-2xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl w-full space-y-4 sm:space-y-6 text-left pr-12 sm:pr-20 md:pr-24 lg:pr-40 xl:pr-56">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium font-cabin tracking-tight leading-[1.3] sm:leading-[1.25] lg:leading-[1.2] text-white">
              Website Plan Registration
            </h1>
            <p className="text-base sm:text-lg text-white leading-[1.7] sm:leading-[1.8] lg:leading-[1.85] font-mulish">
              Online sign up for your new website.
            </p>
          </div>
        </div>
      </section>

      <div className="registration-form layout-container pt-16 pb-10 sm:pt-20 sm:pb-12">

        <nav className="registration-progress" aria-label="Registration steps">
          <details key={currentStepData?.id || 'complete'} className="registration-progress-menu">
            <summary><span>{getStepDisplayTitle(currentStepData?.title || stepTitle)}</span><span>{Math.min(currentStep + 1, formSteps.length)} / {formSteps.length}</span></summary>
            <div className="registration-progress-options">
              {formSteps.map((step, index) => <button key={step.id} type="button"
                disabled={isTransitioningToSignature || isCompletionStep || isBackNavigationLocked || (!visitedStepIds.includes(step.id) && index !== currentStep)}
                aria-current={index === currentStep ? 'step' : undefined}
                onClick={() => handleProgressNavigation(index)}>
                <span aria-hidden="true">{index < currentStep ? '✓' : index + 1}</span>{step.mobileTitle || getStepDisplayTitle(step.title)}
              </button>)}
            </div>
          </details>
          <div className="registration-progress-track" role="progressbar" aria-label="Registration steps" aria-valuemin={0} aria-valuemax={formSteps.length} aria-valuenow={isCompletionStep ? formSteps.length : currentStep}>
            <span style={{ width: `${isCompletionStep ? 100 : progressPercentage}%` }} />
          </div>
        </nav>

        <div className="mx-auto w-full max-w-5xl">
          <div className="registration-step py-10 sm:py-14">
            {/* Step indicator circle and title */}
            <div className="registration-step-heading flex items-center gap-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-lg font-cabin">
                {stepIndicatorValue}
              </div>
              <h2 id="registration-step-title" tabIndex={-1} className="text-2xl sm:text-3xl font-bold font-cabin" style={{ color: '#090909' }}>
                {stepTitle}
              </h2>
            </div>

            {stepSubtitle && (
              <div className="mb-8 sm:mb-10">
                <p className="max-w-3xl whitespace-pre-line text-base leading-relaxed font-mulish sm:text-base" style={{ color: '#090909' }}>
                  {stepSubtitle}
                </p>
              </div>
            )}

            {/* Subtle divider line */}
            {stepSubtitle && (
              <div className="border-b border-gray-200 mb-8 sm:mb-10"></div>
            )}

            {isCompletionStep
  ? renderCompletionStep()
  : isSummaryStep
  ? renderSummary()
  : isSignatureStep
  ? renderSignatureStep()
  : renderFormFields()}

            {!isCompletionStep && (
              <div className="navigation-buttons mt-12 flex flex-col justify-between gap-6 border-t border-gray-200 pt-10 sm:mt-14 sm:flex-row sm:gap-5 sm:pt-10">
                <button
                  onClick={() => handleStepChange(currentStep - 1)}
                  disabled={!canGoBack}
                  className={`order-2 sm:order-1 flex w-full items-center justify-center rounded-[3px] px-6 py-3 text-base font-medium transition-all duration-300 sm:w-auto sm:px-8 sm:py-4 ${
                    canGoBack
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  {backButtonLabel}
                </button>

                {isSignatureStep ? (
                  <button
                    onClick={async () => {
                      if (signatureStep !== 'completed') {
                        return;
                      }

                      const finalized = await finalizeRegistration();
                      if (!finalized) {
                        return;
                      }

                      handleStepChange(currentStep + 1);
                    }}
                    disabled={signatureStep !== 'completed'}
                    className={`order-1 sm:order-2 flex w-full items-center justify-center rounded-[3px] px-8 py-3 text-base font-medium transition-all duration-300 shadow-md sm:w-auto sm:px-10 sm:py-4 ${
                      signatureStep !== 'completed'
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-yellow-600 text-white hover:bg-yellow-700 hover:shadow-lg'
                    }`}
                  >
                    {signatureStep !== 'completed'
                      ? 'Complete Electronic Signature First'
                      : 'Submit registration'}
                  </button>
                ) : (
                  <button
                    onClick={handleContinueAction}
                    aria-busy={isTransitioningToSignature}
                    disabled={isNextButtonDisabled}
                    className={`order-1 sm:order-2 flex w-full items-center justify-center rounded-[3px] px-6 py-3 text-base font-medium transition-all duration-300 shadow-md sm:w-auto sm:px-8 sm:py-4 ${
                      isNextButtonDisabled
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                        : 'bg-yellow-500 text-white hover:bg-yellow-600 hover:shadow-lg'
                    }`}
                  >
                    {effectiveContinueButtonLabel}
                    {isTransitioningToSignature ? (
                      <RefreshCw className="w-4 h-4 ml-2 animate-spin" />
                    ) : (
                      <ChevronRight className="w-4 h-4 ml-2" />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {!isCompletionStep && !isSummaryStep && !isSignatureStep && calculations.monthlyPrice > 0 && (
            <div className="mt-6 rounded-[3px] border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3 sm:items-center">
                  <div className="flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-yellow-100 text-yellow-600">
                    <Calculator className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Current estimate</span>
                    <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">Keep refining your plan—this updates instantly.</p>
                  </div>
                </div>
                <div className="w-full sm:w-auto text-left sm:text-right">
                  <div className="text-2xl font-bold text-yellow-600 leading-none sm:leading-tight">
                    £{calculations.monthlyPrice}<span className="ml-1 text-base font-medium text-gray-500">/month</span>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs font-medium text-gray-600 sm:mt-2 sm:gap-1">
                    <div className="rounded-[3px] border border-gray-200 bg-gray-50 px-2.5 py-1.5 sm:border-0 sm:bg-transparent sm:p-0">
                      1 monthly payment due upfront
                    </div>
                    <div className="rounded-[3px] border border-gray-200 bg-gray-50 px-2.5 py-1.5 sm:border-0 sm:bg-transparent sm:p-0">
                      £{calculations.oneOffAddonsTotal ?? 0} one-off add-ons
                    </div>
                    {calculations.domainAnnualCost > 0 && (
                      <div className="rounded-[3px] border border-gray-200 bg-gray-50 px-2.5 py-1.5 sm:border-0 sm:bg-transparent sm:p-0">
                        + {GBP_CURRENCY_FORMATTER.format(calculations.domainAnnualCost)} /year additional web addresses
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className="mt-10 border-t border-gray-800 bg-[#090909] py-5 text-white sm:py-6">
        <div className="layout-container flex flex-col items-start justify-between gap-3 text-sm sm:flex-row sm:items-center sm:gap-4">
          <p className="text-white/90">Copyright © Ubie Websites {currentYear}</p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setIsPrivacyPolicyModalOpen(true)}
              className="inline-flex items-center rounded-[3px] border border-white/30 px-3 py-1.5 font-medium text-white transition hover:border-yellow-400 hover:text-yellow-300"
            >
              Privacy Policy
            </button>
            <button
              type="button"
              onClick={() => setIsCookiesPolicyModalOpen(true)}
              className="inline-flex items-center rounded-[3px] border border-white/30 px-3 py-1.5 font-medium text-white transition hover:border-yellow-400 hover:text-yellow-300"
            >
              Cookies Policy
            </button>
            <button
              type="button"
              onClick={() => setIsTermsModalOpen(true)}
              className="inline-flex items-center rounded-[3px] border border-white/30 px-3 py-1.5 font-medium text-white transition hover:border-yellow-400 hover:text-yellow-300"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      {isPrivacyPolicyModalOpen && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center bg-black/70 px-4 py-6 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-policy-modal-title"
          onClick={() => setIsPrivacyPolicyModalOpen(false)}
        >
          <div
            className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-[3px] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <h2 id="privacy-policy-modal-title" className="text-xl font-semibold text-gray-900">Privacy Policy</h2>
              <button
                type="button"
                onClick={() => setIsPrivacyPolicyModalOpen(false)}
                className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-[3px] text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close privacy policy"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 text-sm leading-relaxed text-gray-700 sm:px-6 sm:py-5">
              <div className="space-y-4">
                <p>
                  Ubie Websites is the trading name of Ubie Ltd (registered company name). Ubie Ltd is the data controller for the personal information you provide through this registration form. We process your data in line with the UK GDPR and Data Protection Act 2018.
                </p>
                <p>
                  <strong>What we collect:</strong> contact details, business information, billing details, chosen web addresses, requested website services, and signature status where relevant to your order.
                </p>
                <p>
                  <strong>Why we use your data:</strong> to prepare your website plan, provide support, create contracts and invoices, complete service onboarding, and maintain internal records of your submission.
                </p>
                <p>
                  <strong>Legal bases:</strong> contract performance, legitimate interests in operating and improving our services, and legal obligations for accounting and compliance.
                </p>
                <p>
                  <strong>How long we keep it:</strong> we keep personal data only as long as necessary for service delivery, compliance, and record-keeping, then securely delete or anonymise it.
                </p>
                <p>
                  <strong>Sharing:</strong> we only share data with trusted processors and service providers needed to deliver the service (for example, signing, hosting, payment, or automation tools), under appropriate data protection safeguards.
                </p>
                <p>
                  <strong>Your rights:</strong> you can request access, correction, erasure, restriction, objection, and data portability where applicable. You can also complain to the ICO if you are unhappy with how your data is handled.
                </p>
                <p>
                  To make a privacy request, contact us at <a href="mailto:info@ubiewebsites.co.uk" className="font-medium text-yellow-700 underline">info@ubiewebsites.co.uk</a>.
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={() => setIsPrivacyPolicyModalOpen(false)}
                className="inline-flex rounded-[3px] bg-[#090909] px-4 py-2 text-sm font-semibold text-white transition hover:bg-black"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isCookiesPolicyModalOpen && (
        <div
          className="fixed inset-0 z-[66] flex items-center justify-center bg-black/70 px-4 py-6 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookies-policy-modal-title"
          onClick={() => setIsCookiesPolicyModalOpen(false)}
        >
          <div
            className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-[3px] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <h2 id="cookies-policy-modal-title" className="text-xl font-semibold text-gray-900">Cookies Policy</h2>
              <button
                type="button"
                onClick={() => setIsCookiesPolicyModalOpen(false)}
                className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-[3px] text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close cookies policy"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 text-sm leading-relaxed text-gray-700 sm:px-6 sm:py-5">
              <div className="space-y-4">
                <p>
                  This Cookies Policy explains how Ubie Websites (trading name of Ubie Ltd) uses cookies and similar technologies on this registration form.
                </p>
                <p>
                  <strong>First-party cookies on this form:</strong> this registration form does not intentionally set its own non-essential tracking cookies in the application code.
                </p>
                <p>
                  <strong>Third-party services that may set cookies:</strong>
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li><strong>Google Fonts</strong> (for typography) via <code>fonts.googleapis.com</code>/<code>fonts.gstatic.com</code>.</li>
                  <li><strong>Google Maps Places API</strong> (address autocomplete) when address fields are used.</li>
                  <li><strong>eSignatures iframe</strong> during contract signing in the embedded signature window.</li>
                  <li><strong>Brevo CRM/newsletters</strong> when marketing and CRM forms or newsletter flows are used.</li>
                  <li><strong>RapidAPI domain search integrations</strong> used for web address availability checks.</li>
                </ul>
                <p>
                  These third-party providers may use their own cookies to deliver functionality, security, fraud prevention, and performance.
                </p>
                <p>
                  <strong>Local storage:</strong> browser local persistence is intentionally disabled in this app for privacy/GDPR reasons.
                </p>
                <p>
                  <strong>Your choices:</strong> you can manage or block cookies through your browser settings. Blocking functional third-party cookies may affect address autocomplete or embedded signature behavior.
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={() => setIsCookiesPolicyModalOpen(false)}
                className="inline-flex rounded-[3px] bg-[#090909] px-4 py-2 text-sm font-semibold text-white transition hover:bg-black"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {isTermsModalOpen && (
        <div
          className="fixed inset-0 z-[67] flex items-center justify-center bg-black/70 px-4 py-6 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-modal-title"
          onClick={() => setIsTermsModalOpen(false)}
        >
          <div
            className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[3px] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <h2 id="terms-modal-title" className="text-xl font-semibold text-gray-900">Terms of Service</h2>
              <button
                type="button"
                onClick={() => setIsTermsModalOpen(false)}
                className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-[3px] text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close terms of service"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 text-sm leading-relaxed text-gray-700 sm:px-6 sm:py-5">
              <div className="space-y-4">
                <p>
                  These Terms of Service apply to all Ubie Websites plans. Ubie Websites is the trading name of Ubie Ltd (Company No. 12380483), registered office: 27 Old Gloucester Street, London, WC1N 3AX.
                </p>
                <p>
                  By submitting this registration form, paying your first invoice, or otherwise confirming a plan, you agree to these terms and any plan-specific details shown in your registration/plan confirmation.
                </p>
                <p>
                  <strong>Business customers only:</strong> Services are provided to business customers (not consumers) and are governed by the laws of England and Wales.
                </p>
                <p>
                  <strong>Plan scope and limits:</strong> your selected plan includes website/hosting/domain/email/support services according to your plan summary and registration confirmation. Where plan-specific limits differ, your registration confirmation takes priority.
                </p>
                <p>
                  <strong>Start of work:</strong> we start build work once required content/information is provided, onboarding approvals are in place, and required setup/first payments (plus Direct Debit setup where applicable) are completed.
                </p>
                <p>
                  <strong>Payments, term and renewal:</strong> fees are payable as agreed in your selected plan. Minimum terms and renewal/cancellation notice periods are defined in your plan confirmation. Late or failed payments can lead to suspension.
                </p>
                <p>
                  <strong>Support and amendments:</strong> support is provided on UK business days via our support channels. Included monthly amendments are for minor in-scope changes only; larger changes may require a quote, upgrade, or separate agreement.
                </p>
                <p>
                  <strong>Third-party services:</strong> parts of the service may rely on third parties (for example hosting, DNS, domain registrars, email/SMTP, CRM, e-signature, and API providers). Their availability and terms may affect service delivery.
                </p>
                <p>
                  <strong>Suspension and termination:</strong> we may suspend or terminate services for material breach, non-payment, abuse, legal/compliance risk, security incidents, or spam/misuse of email infrastructure.
                </p>
                <p>
                  <strong>Liability:</strong> to the extent permitted by law, we do not guarantee specific commercial outcomes (such as leads, rankings, or sales). Our liability is limited as set out in your signed agreement and applicable plan terms.
                </p>
                <p>
                  <strong>Data protection:</strong> each party must comply with UK GDPR and applicable UK data laws. Our Privacy Policy and Cookies Policy explain how personal data and cookies are handled.
                </p>
                <p>
                  <strong>Priority of documents:</strong> where these generic terms conflict with plan-specific terms in your signed agreement or registration confirmation, the plan-specific terms apply to that conflict.
                </p>
                <p>
                  For full contractual terms or legal queries, contact <a href="mailto:info@ubiewebsites.co.uk" className="font-medium text-yellow-700 underline">info@ubiewebsites.co.uk</a>.
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={() => setIsTermsModalOpen(false)}
                className="inline-flex rounded-[3px] bg-[#090909] px-4 py-2 text-sm font-semibold text-white transition hover:bg-black"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isSignatureModalOpen && signatureData.signingUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6 sm:p-8 bg-black/60"
          role="dialog"
          aria-modal="true"
          aria-labelledby="signature-modal-title"
          onClick={closeSignatureModal}
        >
          <div
            className="relative w-full max-w-4xl bg-white rounded-[3px] shadow-2xl max-h-full flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b border-gray-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 id="signature-modal-title" className="text-lg font-semibold text-gray-900">
                    Sign your agreement
                  </h2>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">
                    Please scroll to the bottom of the agreement to sign.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeSignatureModal}
                  className="inline-flex shrink-0 items-center justify-center rounded-[3px] border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50"
                  aria-label="Close signing window"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              <iframe
                src={signatureData.signingUrl}
                title="Electronic signature document"
                className="w-full h-[70vh] sm:h-[75vh] border-0"
              />
            </div>

            <div className="px-4 sm:px-5 py-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <div className="flex justify-center sm:justify-start">
                <button
                  type="button"
                  onClick={closeSignatureModal}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-3 font-semibold shadow-lg hover:shadow-xl transition-all"
                  style={{ borderRadius: '3px' }}
                  aria-label="I have signed, check now"
                >
                  <RefreshCw className="h-4 w-4" />
                  I&rsquo;ve signed — check now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <MobileStickyNavigation
        show={showMobileStickyNavigation}
        canGoBack={canGoBack}
        backButtonLabel={backButtonLabel}
        continueButtonLabel={effectiveContinueButtonLabel}
        onBack={() => handleStepChange(currentStep - 1)}
        onContinue={handleContinueAction}
        isNextDisabled={isNextButtonDisabled}
        busy={isTransitioningToSignature}
      />
      
      {/* Status Message */}
      {statusMessage.show && (
        <div role={statusMessage.type === 'error' ? 'alert' : 'status'} className={`fixed top-4 right-4 left-4 sm:left-auto sm:w-96 z-50 p-4 style={{ borderRadius: '3px' }} shadow-lg border-l-4 transition-all duration-300 ${
          statusMessage.type === 'success' ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
          statusMessage.type === 'error' ? 'bg-red-50 border-red-500 text-red-800' :
          'bg-yellow-50 border-yellow-500 text-yellow-800'
        }`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="font-medium">{statusMessage.text}</p>
            </div>
            <button
              onClick={() => setStatusMessage(prev => ({ ...prev, show: false }))}
              className="ml-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
</svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default WebsitePackageBuilder;

